


/*!
 * ============================================================================
 *  SCOUT DESIGN STUDIO — ACCESSIBILITY REMEDIATION (WCAG 2.2 AA)
 *  Source: "Scout Design Studio Accessibility Audit Report" (30-June-2026) — 87 findings
 *  Store:  https://www.scoutdesignstudio.com
 *  Theme:  Symmetry 8.3.2 (Clean Canvas)
 *  Apps touched: Klaviyo (popup), Depict (collection grid), Emplifi/Pixlee UGC,
 *                Emplifi/TurnTo Reviews
 * ----------------------------------------------------------------------------
 *  INSTALL
 *    Upload as assets/sds-a11y.js and load it just before </body> in theme.liquid:
 *      <script src="{{ 'sds-a11y.js' | asset_url }}" defer></script>
 *    (or host on your CDN and inject with a Custom Liquid block).
 *    jQuery: the store already loads jQuery 2.1.4 through an app. If jQuery is not
 *    present after 4s, the file loads jQuery 3.7.1 from cdnjs in noConflict mode.
 *
 *  HOW TO ADD / REMOVE A FIX
 *    Every audit finding is its own block, wrapped between
 *        // ===== BUG n — START =====   and   // ===== BUG n — END =====
 *    - Remove a fix:       delete that block. Its CSS goes with it.
 *    - Disable temporarily: set window.SDS_A11Y_DISABLED = [8, 9]; BEFORE this file loads.
 *    - Add a fix:          copy any block, give it a new id, edit css / init / run.
 *        css   → injected only when the block is enabled
 *        init  → runs once after DOM ready (event listeners, observers)
 *        run   → runs on load AND every time the theme/apps inject markup
 *                (Depict grid, cart drawer refresh, quick buy, Klaviyo, TurnTo).
 *                run() MUST be idempotent (safe to call repeatedly).
 *    The only shared part is the CORE (helpers) below — keep it.
 *
 *  IMPORTANT — theme "Add ETA Metafield to Backorder [ADA Working theme]" (#191402279276)
 *    already loads assets/ada-pro.js, which adds its own submenu toggles, Space-key
 *    handlers and dialog traps. This file was written and tested against the LIVE
 *    theme markup. Load only ONE of the two, otherwise controls fire twice
 *    (e.g. cart quantity +/− jumps by 2) and menus get duplicate toggle buttons.
 *
 *  v2 (24-Sep-2026 retest): Bug 20 re-fixed (Pixlee re-render), Bugs 13/27 quick-buy
 *  focus + keyboard reachability re-fixed, Bug 41 dialog naming retried, and Bugs 88-96
 *  added for defects found during the retest.
 *  v3 (24-Sep-2026): Bugs 97-120 added from the "Copy of More issues" sheet — one block
 *  per sheet row, in sheet order. Bug 117 is vendor-side (NetSuite iframe) and Bug 20
 *  stays best-effort (Emplifi widget); everything else is fixed here.
 *  All 24 new blocks were verified in-browser on the ADA Working theme (#191402279276)
 *  on 24-Sep-2026: home, /collections/beds-headboards, a PDP, /pages/design-advice,
 *  /pages/contact, /pages/hospitality and /pages/designer-trade-program, at 1440px and
 *  at 390px for the mobile drawer.
 *  NOTE: the store currently ships this script as assets/ada-pro-max.js. If you keep that
 *  file name, upload this file over it rather than adding a second copy.
 *
 *  Debug in console:  SDS_A11Y.list()    → enabled bugs
 *                     SDS_A11Y.runBug(15) → re-run one fix
 * ============================================================================
 */
(function (window, document) {
  'use strict';
  if (window.SDS_A11Y && window.SDS_A11Y.loaded) return;


  // ==========================================================================
  // CORE — shared helpers (required by every bug block, do not remove)
  // ==========================================================================
  var SDS = window.SDS_A11Y = { loaded: true, bugs: [], state: {}, $: null };
  var DISABLED = (window.SDS_A11Y_DISABLED || []).map(Number);

  /** Register a fix. */
  SDS.bug = function (id, title, def) {
    if (DISABLED.indexOf(id) > -1) return;
    SDS.bugs.push({ id: id, title: title, def: def || {} });
  };
  SDS.enabled = function (id) {
    for (var i = 0; i < SDS.bugs.length; i++) if (SDS.bugs[i].id === id) return true;
    return false;
  };
  SDS.list = function () {
    return SDS.bugs.map(function (b) { return 'Bug ' + b.id + ': ' + b.title; });
  };

  /** Path without locale prefix / trailing slash, e.g. "/pages/about". */
  SDS.path = function () {
    return window.location.pathname
      .replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i, '')
      .replace(/\/+$/, '') || '/';
  };
  SDS.isPage = function (handle) { return SDS.path() === '/pages/' + handle; };
  SDS.isTemplate = function (name) {
    return document.body.classList.contains('template-' + name);
  };

  /** Inject a <style> once. */
  SDS.css = function (id, cssText) {
    if (!cssText || document.getElementById(id)) return;
    var s = document.createElement('style');
    s.id = id;
    s.textContent = cssText;
    document.head.appendChild(s);
  };

  /** Rendered & not visibility:hidden (opacity ignored on purpose: dialogs animate in). */
  SDS.visible = function (el) {
    if (!el || el.nodeType !== 1 || !document.documentElement.contains(el)) return false;
    if (!el.getClientRects().length) return false;
    var cs = window.getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none';
  };

  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
    'select:not([disabled]), textarea:not([disabled]), iframe, summary, [tabindex], [contenteditable="true"]';

  SDS.focusables = function (container) {
    return [].filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      var ti = el.getAttribute('tabindex');
      if (ti !== null && parseInt(ti, 10) < 0) return false;
      var hidden = el.closest('[aria-hidden="true"], [inert]');
      if (hidden && container.contains(hidden)) return false;
      return SDS.visible(el);
    });
  };

  /** Focus the first usable target from a list of elements and/or selectors. */
  SDS.focusFirst = function (list) {
    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      var els = typeof item === 'string' ? document.querySelectorAll(item) : [item];
      for (var j = 0; j < els.length; j++) {
        var el = els[j];
        if (el && SDS.visible(el)) {
          if (!el.matches(FOCUSABLE)) el.setAttribute('tabindex', '-1');
          el.focus();
          if (document.activeElement === el) return el;
        }
      }
    }
    return null;
  };

  /**
   * Modal focus trap. One trap at a time.
   * opts.onEscape(e) → called on Escape (omit when the widget already handles Esc).
   */
  SDS.trap = function (container, opts) {
    opts = opts || {};
    SDS.releaseTrap();
    var onKey = function (e) {
      if (!document.documentElement.contains(container)) { SDS.releaseTrap(); return; }
      if ((e.key === 'Escape' || e.key === 'Esc') && opts.onEscape) { opts.onEscape(e); return; }
      if (e.key !== 'Tab') return;
      document.body.classList.add('tab-used');       // theme uses this class for focus styles
      e.stopPropagation();                             // stop the theme's partial trap from competing
      var f = SDS.focusables(container);
      var active = document.activeElement;
      if (!f.length) { e.preventDefault(); container.focus(); return; }
      var first = f[0], last = f[f.length - 1];
      if (!container.contains(active)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); return; }
      if (e.shiftKey && (active === first || active === container)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    var onFocusIn = function (e) {
      if (!document.documentElement.contains(container)) { SDS.releaseTrap(); return; }
      if (container.contains(e.target)) return;
      if (e.target.closest && e.target.closest('#sds-a11y-live')) return;
      var f = SDS.focusables(container);
      (f[0] || container).focus();
    };
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('focusin', onFocusIn, true);
    SDS.state.trap = {
      container: container,
      release: function () {
        window.removeEventListener('keydown', onKey, true);
        document.removeEventListener('focusin', onFocusIn, true);
        SDS.state.trap = null;
      }
    };
    return SDS.state.trap;
  };
  SDS.releaseTrap = function (onlyFor) {
    var t = SDS.state.trap;
    if (t && (!onlyFor || t.container === onlyFor)) t.release();
  };

  /** Screen reader announcement through one shared live region. */
  SDS.announce = function (msg) {
    var live = document.getElementById('sds-a11y-live');
    if (!live) {
      live = document.createElement('div');
      live.id = 'sds-a11y-live';
      live.className = 'sds-sr-only';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = '';
    setTimeout(function () { live.textContent = msg; }, 120);
  };

  /** Give a non-button control button semantics + Space (and Enter for non-links). */
  SDS.buttonize = function (el) {
    if (!el || el.getAttribute('data-sds-button')) return el;
    el.setAttribute('data-sds-button', '1');
    el.setAttribute('role', 'button');
    var nativeFocus = (el.tagName === 'A' && el.hasAttribute('href')) || el.tagName === 'BUTTON';
    if (!nativeFocus && !el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
    el.addEventListener('keydown', function (e) {
      if (e.key === ' ' || e.key === 'Spacebar' || (e.key === 'Enter' && el.tagName !== 'A')) {
        e.preventDefault();
        el.click();
      }
    });
    return el;
  };

  /** Warn that a link opens a new tab (keeps visible text first → 2.5.3 Label in Name). */
  SDS.newTab = function (a, extra) {
    if (!a || a.getAttribute('data-sds-newtab')) return;
    a.setAttribute('data-sds-newtab', '1');
    a.setAttribute('target', '_blank');
    var rel = (a.getAttribute('rel') || '').split(/\s+/);
    if (rel.indexOf('noopener') < 0) rel.push('noopener');
    a.setAttribute('rel', rel.join(' ').trim());
    var note = extra ? extra : 'opens in a new tab';
    if (a.hasAttribute('aria-label')) {
      a.setAttribute('aria-label', a.getAttribute('aria-label').replace(/[\s,.]+$/, '') + ', ' + note);
    } else {
      var s = document.createElement('span');
      s.className = 'sds-sr-only sds-newtab';
      s.textContent = ' (' + note + ')';
      a.appendChild(s);
    }
  };
  SDS.isExternal = function (a) {
    try { return new URL(a.href, window.location.href).hostname !== window.location.hostname; }
    catch (e) { return false; }
  };

  /** "#color_bone-velvet", "file.jpg__PID:…", "IMG_1234.jpg" → '' (invalid alt). */
  SDS.cleanAlt = function (alt) {
    alt = (alt || '').trim();
    if (!alt) return '';
    if (/^#\w+[_-]/.test(alt) || /__PID:/i.test(alt) || /\.(jpe?g|png|webp|gif|svg|avif)\b/i.test(alt)) return '';
    return alt;
  };

  /** Set alt text by matching a file-name fragment in src / data-src / srcset. */
  SDS.altByFile = function (map, scope) {
    var root = typeof scope === 'string' ? document.querySelector(scope) : (scope || document);
    if (!root) return;
    [].forEach.call(root.querySelectorAll('img'), function (img) {
      var src = (img.getAttribute('src') || '') + ' ' + (img.getAttribute('data-src') || '') + ' ' + (img.getAttribute('srcset') || '');
      for (var key in map) {
        if (Object.prototype.hasOwnProperty.call(map, key) && src.indexOf(key) > -1) {
          if (img.getAttribute('alt') !== map[key]) img.setAttribute('alt', map[key]);
          break;
        }
      }
    });
  };

  /** Change an element's tag, keeping attributes, children and its original look. */
  SDS.retag = function (el, tag) {
    if (!el || el.tagName.toLowerCase() === tag) return el;
    var oldTag = el.tagName.toLowerCase();
    var n = document.createElement(tag);
    for (var i = 0; i < el.attributes.length; i++) n.setAttribute(el.attributes[i].name, el.attributes[i].value);
    if (/^h[1-6]$/.test(oldTag) && !n.classList.contains(oldTag)) n.classList.add(oldTag); // keep theme .h1–.h6 styling
    n.classList.add('sds-was-' + oldTag);
    while (el.firstChild) n.appendChild(el.firstChild);
    el.parentNode.replaceChild(n, el);
    return n;
  };

  /** Visually hidden H1 at the top of <main> when the page has none. */
  SDS.addSrH1 = function (text) {
    var main = document.querySelector('main#content') || document.querySelector('main');
    if (!main || main.querySelector('h1')) return null;
    var h = document.createElement('h1');
    h.className = 'sds-sr-only';
    h.id = 'sds-page-title';
    h.textContent = text;
    main.insertBefore(h, main.firstChild);
    return h;
  };

  /** Promote the first H2 in <main> to H1 when the page has no H1. */
  SDS.promoteFirstH2 = function (textMatch) {
    var main = document.querySelector('main#content') || document.querySelector('main');
    if (!main || main.querySelector('h1')) return null;
    var h2s = [].slice.call(main.querySelectorAll('h2'));
    var target = textMatch
      ? h2s.filter(function (h) { return textMatch.test(h.textContent); })[0]
      : h2s[0];
    return target ? SDS.retag(target, 'h1') : null;
  };

  /** Wrap text matches in links (skips text already inside links/buttons/fields). */
  SDS.linkifyText = function (root, pattern, makeLink) {
    if (!root) return 0;
    var nodes = [];
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    while (walker.nextNode()) {
      var n = walker.currentNode;
      pattern.lastIndex = 0;
      if (n.nodeValue && pattern.test(n.nodeValue) && !n.parentNode.closest('a, button, script, style, textarea, input')) nodes.push(n);
    }
    nodes.forEach(function (node) {
      var text = node.nodeValue, frag = document.createDocumentFragment(), last = 0, m;
      pattern.lastIndex = 0;
      while ((m = pattern.exec(text))) {
        frag.appendChild(document.createTextNode(text.slice(last, m.index)));
        frag.appendChild(makeLink(m[0]));
        last = m.index + m[0].length;
      }
      frag.appendChild(document.createTextNode(text.slice(last)));
      node.parentNode.replaceChild(frag, node);
    });
    return nodes.length;
  };

  /** Accessible play/pause toggle for autoplaying muted videos (2.2.2). */
  SDS.videoControls = function (video, opts) {
    if (!video || video.getAttribute('data-sds-video')) return;
    opts = opts || {};
    video.setAttribute('data-sds-video', '1');
    var host = video.closest('.image-overlay, .video-section') || video.parentNode;
    if (window.getComputedStyle(host).position === 'static') host.style.position = 'relative';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sds-video-toggle';
    var PLAY = '<svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>';
    var PAUSE = '<svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M6 5h4v14H6zm8 0h4v14h-4z"/></svg>';
    var sync = function () {
      btn.setAttribute('aria-label', (video.paused ? 'Play ' : 'Pause ') + (opts.label || 'video'));
      btn.innerHTML = video.paused ? PLAY : PAUSE;
    };
    btn.addEventListener('click', function () {
      if (video.paused) {
        video.setAttribute('data-sds-user', 'playing');
        var p = video.play(); if (p && p.catch) p.catch(function () {});
      } else {
        video.setAttribute('data-sds-user', 'paused');
        video.pause();
      }
      sync();
    });
    video.addEventListener('play', function () {
      // the theme re-plays videos when they scroll into view: respect a user's pause
      if (video.getAttribute('data-sds-user') === 'paused') video.pause();
      sync();
    });
    video.addEventListener('pause', sync);
    host.appendChild(btn);
    if (opts.decorative) video.setAttribute('aria-hidden', 'true');
    if (opts.description) {
      var d = document.createElement('p');
      d.className = 'sds-sr-only';
      d.textContent = opts.description;
      host.appendChild(d);
    }
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      video.setAttribute('data-sds-user', 'paused');
      video.pause();
    }
    if (opts.stopAfterSeconds) {
      setTimeout(function () { if (!video.getAttribute('data-sds-user')) video.pause(); }, opts.stopAfterSeconds * 1000);
    }
    sync();
  };

  SDS.titleCase = function (s) {
    return (s || '').toLowerCase().replace(/(^|[\s\-\/&])([a-z])/g, function (m, pre, ch) { return pre + ch.toUpperCase(); });
  };
  SDS.text = function (el) { return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : ''; };
  var uid = 0;
  SDS.id = function (el, prefix) {
    if (!el.id) el.id = (prefix || 'sds') + '-' + (++uid);
    return el.id;
  };

  var CORE_CSS =
    '.sds-sr-only{position:absolute!important;width:1px!important;height:1px!important;padding:0!important;' +
    'margin:-1px!important;overflow:hidden!important;clip:rect(0,0,0,0)!important;white-space:nowrap!important;border:0!important}' +
    '.sds-inline-heading{font:inherit;font-weight:700;letter-spacing:inherit;text-transform:inherit;line-height:inherit;color:inherit;margin:0}' +
    '.sds-video-toggle{position:absolute;left:16px;bottom:16px;z-index:20;width:44px;height:44px;padding:0;border-radius:50%;' +
    'border:2px solid #fff;background:rgba(0,0,0,.7);color:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer}' +
    '.sds-video-toggle:focus-visible{outline:3px solid #fff;outline-offset:2px;box-shadow:0 0 0 6px #000}' +
    '[tabindex="-1"]:focus:not(:focus-visible){outline:none}';

  // ==========================================================================
  // BOOT (defined here, executed at the very end of the file)
  // ==========================================================================
  function safe(bug, phase) {
    try { if (bug.def[phase]) bug.def[phase].call(bug, SDS.$); }
    catch (err) { if (window.console) console.warn('[SDS a11y] Bug ' + bug.id + ' ' + phase + ' failed:', err); }
  }
  var observer, scheduled = null;
  function runAll() {
    scheduled = null;
    SDS.bugs.forEach(function (b) { safe(b, 'run'); });
    if (observer) observer.takeRecords(); // ignore mutations we just made
  }
  function schedule() {
    if (scheduled) return;
    scheduled = setTimeout(runAll, 250);
  }
  SDS.runBug = function (id) {
    SDS.bugs.forEach(function (b) { if (b.id === id) safe(b, 'run'); });
  };
  SDS.refresh = schedule;

  function start($) {
    SDS.$ = $;
    SDS.css('sds-a11y-core', CORE_CSS);
    SDS.bugs.forEach(function (b) { SDS.css('sds-a11y-bug-' + b.id, b.def.css); });
    SDS.bugs.forEach(function (b) { safe(b, 'init'); });
    runAll();
    // The Emplifi/Pixlee UGC widget rebuilds itself several times a second; its churn must
    // not re-trigger every fix (Bug 20 watches that widget on its own).
    var NOISY = '[id^="pixlee_container"], .pixlee-widget, #sds-a11y-live';
    observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var t = records[i].target;
        if (!(t && t.closest && t.closest(NOISY))) { schedule(); return; }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('load', schedule);
    var resizeT;
    window.addEventListener('resize', function () { clearTimeout(resizeT); resizeT = setTimeout(schedule, 300); });
    ['on:quickbuy:after-open', 'on:quickbuy:after-close', 'on:cart-drawer:after-open', 'on:cart:after-merge',
      'shopify:section:load', 'on:variant:change'].forEach(function (ev) { document.addEventListener(ev, schedule); });
  }

  SDS.boot = function () {
    var go = function () {
      var tries = 0;
      (function waitForJQ() {
        if (window.jQuery) return start(window.jQuery);
        if (++tries < 40) return setTimeout(waitForJQ, 100);
        var s = document.createElement('script');
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jquery/3.7.1/jquery.min.js';
        s.onload = function () { start(window.jQuery.noConflict(true)); };
        document.head.appendChild(s);
      })();
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go);
    else go();
  };

  // ##########################################################################
  //  GLOBAL : HEADER
  // ##########################################################################

  // ===== BUG 1 — START =====
  // Global : Header · Medium · 2.1.1, 2.4.3, 2.4.7
  // "Save 10% Off" (Klaviyo) popup: focus does not move in and is lost on close.
  // Fix: when the Klaviyo modal appears → name it by its heading, move focus to it,
  //      trap Tab inside, Esc closes; when it closes → return focus to the element
  //      that opened it (teaser button or last focused element).
  (function () {
    var st = { dlg: null, opener: null, lastFocus: null, poll: null };

    function findPopup($) {
      return $('div[role="dialog"][aria-modal="true"]').filter(function () {
        return $(this).find('[data-testid="POPUP"], [data-testid="FLYOUT"], form.klaviyo-form').length && SDS.visible(this);
      })[0];
    }
    function closePopup($) {
      var dlg = st.dlg;
      st.dlg = null;
      clearInterval(st.poll);
      SDS.releaseTrap(dlg);
      setTimeout(function () {
        SDS.focusFirst([
          st.opener,
          '[class*="kl-teaser"] [role="button"]',   // "GET 10% OFF" teaser
          'main#content'
        ]);
        st.opener = null;
      }, 350);
    }
    function openPopup($, dlg) {
      st.dlg = dlg;
      st.opener = (st.lastFocus && st.lastFocus !== document.body && document.documentElement.contains(st.lastFocus)) ? st.lastFocus : null;
      var $d = $(dlg);
      var heading = $d.find('h1, h2, h3, [role="heading"]')[0];
      if (heading) { $d.attr('aria-labelledby', SDS.id(heading, 'sds-kl-title')).removeAttr('aria-label'); }
      $d.attr('tabindex', '-1');
      SDS.trap(dlg, {
        onEscape: function (e) {
          var close = $d.find('.klaviyo-close-form')[0];
          if (close) { e.preventDefault(); close.click(); }
        }
      });
      setTimeout(function () { if (!dlg.contains(document.activeElement)) dlg.focus(); }, 150);
      st.poll = setInterval(function () {
        if (!document.documentElement.contains(dlg) || !SDS.visible(dlg)) closePopup($);
      }, 400);
    }

    SDS.bug(1, 'Klaviyo popup focus management', {
      init: function ($) {
        document.addEventListener('focusin', function (e) {
          if (!$(e.target).closest('[role="dialog"][aria-modal="true"]').length) st.lastFocus = e.target;
        }, true);
      },
      run: function ($) {
        var dlg = findPopup($);
        if (dlg && dlg !== st.dlg) openPopup($, dlg);
      }
    });
  })();
  // ===== BUG 1 — END =====


  // ===== BUG 2 — START =====
  // Global : Header · High · 1.1.1
  // Image inside the Klaviyo "Save 10% Off" popup has alt="".
  (function () {
    var ALT = {
      '9205ec40-a106-416b-a225-f15f8f970ace': 'Vintage-style photo of a smiling woman in a floral top and satin pants sitting cross-legged on a tasseled floor cushion'
    };
    SDS.bug(2, 'Klaviyo popup image alt text', {
      run: function ($) {
        $('[data-testid="POPUP"], [data-testid="FLYOUT"]').each(function () { SDS.altByFile(ALT, this); });
      }
    });
  })();
  // ===== BUG 2 — END =====


  // ===== BUG 3 — START =====
  // Global : Header · High · 1.3.1
  // Duplicate "Primary" navigation landmarks, no banner, no contentinfo, and two
  // footer navigations both labelled "Secondary".
  (function () {
    SDS.bug(3, 'Landmarks: banner, single main nav, contentinfo, unique footer nav labels', {
      run: function ($) {
        // banner — header section sits outside <main>
        $('.section-header').first().attr('role', 'banner');

        // One "Main" navigation landmark: whichever tier-1 menu is actually visible
        var proxy = document.getElementById('proxy-nav');
        var mainNav = document.querySelector('#main-nav .navigation--main');
        var proxyVisible = proxy && SDS.visible(proxy.querySelector('.navigation__tier-1') || proxy);
        var on = proxyVisible ? proxy : mainNav;
        var off = proxyVisible ? mainNav : proxy;
        if (on) { on.setAttribute('role', 'navigation'); on.setAttribute('aria-label', 'Main'); }
        if (off) { off.removeAttribute('role'); off.removeAttribute('aria-label'); }
        $('.mobile-navigation-drawer [role="navigation"]').attr('aria-label', 'Mobile menu');

        // contentinfo — theme footer wrapper is a plain <div id="pagefooter">
        var footer = document.getElementById('pagefooter');
        if (footer) {
          footer.setAttribute('role', 'contentinfo');
          $(footer).find('[role="contentinfo"]').removeAttr('role');
          $(footer).find('.section-footer__menu-block[role="navigation"]').each(function () {
            var t = SDS.text(this.querySelector('.section-footer__title'));
            if (t) this.setAttribute('aria-label', 'Footer: ' + SDS.titleCase(t));
          });
        }
      }
    });
  })();
  // ===== BUG 3 — END =====


  // ===== BUG 4 — START =====
  // Global : Header · High · 4.1.2
  // Search control opens an overlay but is announced as a link.
  (function () {
    function sync() {
      var open = document.body.classList.contains('show-search');
      [].forEach.call(document.querySelectorAll('.show-search-link'), function (a) {
        a.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    SDS.bug(4, 'Search trigger exposed as button with popup state', {
      init: function () {
        new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ['class'] });
      },
      run: function ($) {
        var panel = document.querySelector('main-search');
        if (!panel) return;
        SDS.id(panel, 'sds-main-search');
        $('.show-search-link').each(function () {
          SDS.buttonize(this);
          this.setAttribute('aria-haspopup', 'dialog');
          this.setAttribute('aria-controls', panel.id);
          if (!this.hasAttribute('aria-label')) this.setAttribute('aria-label', 'Search');
        });
        sync();
      }
    });
  })();
  // ===== BUG 4 — END =====


  // ===== BUG 5 — START =====
  // Global : Header · Medium · 2.1.1, 2.4.3, 4.1.2
  // Search overlay is not a real modal: Tab escapes it and focus is lost on close.
  (function () {
    var st = { open: false, opener: null };
    function closeSearch() {
      var btn = document.querySelector('main-search .main-search__close');
      if (btn) btn.click(); else document.body.classList.remove('show-search');
    }
    function check() {
      var panel = document.querySelector('main-search');
      if (!panel) return;
      var isOpen = document.body.classList.contains('show-search');
      if (isOpen && !st.open) {
        st.open = true;
        panel.setAttribute('aria-modal', 'true');
        SDS.trap(panel, { onEscape: function (e) { e.preventDefault(); closeSearch(); } });
        setTimeout(function () {   // theme focuses the input after 500ms; make sure it happened
          var input = panel.querySelector('.main-search__input');
          if (input && !panel.contains(document.activeElement)) input.focus();
        }, 600);
      } else if (!isOpen && st.open) {
        st.open = false;
        panel.removeAttribute('aria-modal');
        SDS.releaseTrap(panel);
        setTimeout(function () { SDS.focusFirst([st.opener, '.show-search-link']); }, 120);
      }
    }
    SDS.bug(5, 'Search overlay modal behaviour (trap, Esc, focus return)', {
      init: function () {
        var remember = function (e) {
          var t = e.target.closest && e.target.closest('.show-search-link');
          if (t && (e.type === 'click' || e.key === 'Enter' || e.key === ' ')) st.opener = t;
        };
        document.addEventListener('click', remember, true);
        document.addEventListener('keydown', remember, true);
        new MutationObserver(check).observe(document.body, { attributes: true, attributeFilter: ['class'] });
      },
      run: function () {
        var panel = document.querySelector('main-search');
        if (!panel) return;
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-label', 'Search');
      }
    });
  })();
  // ===== BUG 5 — END =====


  // ===== BUG 6 — START =====
  // Global : Header · High · 4.1.2
  // Cart opens a drawer dialog but is announced as a link; no popup/expanded state.
  (function () {
    function sync() {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      [].forEach.call(document.querySelectorAll('.cart-link'), function (a) {
        a.setAttribute('aria-expanded', drawer.hasAttribute('open') ? 'true' : 'false');
      });
    }
    SDS.bug(6, 'Cart trigger exposed as dialog button with item count', {
      run: function ($) {
        var drawer = document.querySelector('cart-drawer.js-cart-drawer, cart-drawer');
        if (!drawer) return;   // cart type = page → it really is a link
        SDS.id(drawer, 'CartDrawer');
        if (!drawer.getAttribute('data-sds-b6')) {
          drawer.setAttribute('data-sds-b6', '1');
          new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['open'] });
        }
        $('.cart-link').each(function () {
          SDS.buttonize(this);
          var count = parseInt(SDS.text(this.querySelector('.cart-link__count')), 10) || 0;
          this.setAttribute('aria-haspopup', 'dialog');
          this.setAttribute('aria-controls', drawer.id);
          this.setAttribute('aria-label', 'Cart, ' + count + (count === 1 ? ' item' : ' items'));
        });
        sync();
      }
    });
  })();
  // ===== BUG 6 — END =====


  // ===== BUG 7 — START =====
  // Global : Header · Low · 2.1.1, 2.4.3
  // Cart drawer: the theme's trap only reacts on first/last element and it restores
  // focus to evt.target (the inner <span>/<svg>), so focus is lost on close.
  (function () {
    var st = { lastTrigger: null, lastWasCartLink: false };
    function onOpenChange(drawer) {
      if (drawer.hasAttribute('open')) {
        SDS.trap(drawer);                           // theme already closes on Esc
        setTimeout(function () {
          if (drawer.contains(document.activeElement)) return;
          SDS.focusFirst([drawer.querySelector('#CartDrawerModal-Title'), drawer.querySelector('.drawer__close-btn')]);
        }, 120);
      } else {
        SDS.releaseTrap(drawer);
        // Bug 38 handles "opened by Add to cart" on product pages
        if (!st.lastWasCartLink && SDS.enabled(38) && SDS.isTemplate('product')) return;
        setTimeout(function () { SDS.focusFirst([st.lastTrigger, '.cart-link']); }, 450);
      }
    }
    SDS.bug(7, 'Cart drawer focus trap and focus restore', {
      init: function () {
        document.addEventListener('click', function (e) {
          if (!e.target.closest) return;
          var link = e.target.closest('.cart-link');
          var other = e.target.closest('[name="add"], .add-to-cart');
          if (link) { st.lastTrigger = link; st.lastWasCartLink = true; }
          else if (other) { st.lastTrigger = other; st.lastWasCartLink = false; }
        }, true);
      },
      run: function () {
        var drawer = document.querySelector('cart-drawer');
        if (!drawer || drawer.getAttribute('data-sds-b7')) return;
        drawer.setAttribute('data-sds-b7', '1');
        new MutationObserver(function () { onOpenChange(drawer); })
          .observe(drawer, { attributes: true, attributeFilter: ['open'] });
      }
    });
  })();
  // ===== BUG 7 — END =====


  // ===== BUG 8 — START =====
  // Global : Header · High · 3.2.2, 4.1.2
  // Mega menus open by themselves while tabbing/reading: theme CSS
  //   body.tab-used .navigation__item--with-children:focus-within .navigation__tier-2-container
  // reveals the whole submenu as soon as focus reaches the top-level link.
  // Fix: only show a submenu when it was explicitly opened (theme class
  //      .navigation__item--show-children) and keep aria-expanded in sync.
  (function () {
    var HIDE = 'visibility:hidden!important;opacity:0!important;pointer-events:none!important';
    function sync() {
      var mainLis = document.querySelectorAll('#main-nav .navigation__tier-1 > .navigation__item');
      [].forEach.call(mainLis, function (li, i) {
        if (!li.classList.contains('navigation__item--with-children')) return;
        var open = li.classList.contains('navigation__item--show-children') ? 'true' : 'false';
        var proxyLi = document.querySelectorAll('#proxy-nav .navigation__tier-1 > .navigation__item')[i];
        [li, proxyLi].forEach(function (item) {
          var link = item && item.querySelector(':scope > .navigation__link');
          if (link && !link.hasAttribute('data-sds-b9')) link.setAttribute('aria-expanded', open);
        });
      });
    }
    SDS.bug(8, 'Mega menu no longer auto-expands on focus', {
      css:
        // 'body.tab-used .navigation .navigation__tier-1 > .navigation__item--with-children:focus-within:not(.navigation__item--show-children) > .navigation__tier-2-container,' +
        // 'body.tab-used .navigation .navigation__tier-1 > .navigation__item--with-children:focus-within:not(.navigation__item--show-children) > .navigation__child-tier{' + HIDE + '}',
        'body.tab-used #main-nav .navigation .navigation__tier-1 > .navigation__item--with-children:focus-within:not(.navigation__item--show-children) > .navigation__tier-2-container,' +
        'body.tab-used #main-nav .navigation .navigation__tier-1 > .navigation__item--with-children:focus-within:not(.navigation__item--show-children) > .navigation__child-tier' +
        '{visibility:hidden!important;opacity:0!important;pointer-events:none!important}',
        run: function () {
        [].forEach.call(document.querySelectorAll('#main-nav .navigation__tier-1 > .navigation__item--with-children'), function (li) {
          if (li.getAttribute('data-sds-b8')) return;
          li.setAttribute('data-sds-b8', '1');
          new MutationObserver(sync).observe(li, { attributes: true, attributeFilter: ['class'] });
        });
        sync();
      }
    });
  })();
  // ===== BUG 8 — END =====


  // ===== BUG 9 — START =====
  // Global : Header · Medium · 2.1.1, 2.1.2, 4.1.2
  // Dropdown menus: Enter on "FURNITURE" opens the menu instead of following the
  // link, submenus can't be closed with Esc and stay open when focus leaves.
  // Fix (WAI disclosure navigation pattern):
  //   - top-level links become plain links again (Enter navigates)
  //   - a "<Menu> submenu" toggle button (aria-expanded/aria-controls) follows each link;
  //     it is visually hidden until it receives keyboard focus
  //   - Esc closes and returns focus to the toggle; moving focus out closes the menu
  //   - column titles inside the desktop mega menu lose the wrong aria-haspopup
  (function () {
    var CHEVRON = '<svg aria-hidden="true" focusable="false" width="10" height="10" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-width="3" d="M5 9l7 7 7-7"/></svg>';
    function mainLis() { return document.querySelectorAll('#main-nav .navigation__tier-1 > .navigation__item'); }
    function proxyLis() { return document.querySelectorAll('#proxy-nav .navigation__tier-1 > .navigation__item'); }
    function indexOf(li) { return [].indexOf.call(li.parentNode.children, li); }
    function mainFor(li) { return li.closest('#proxy-nav') ? mainLis()[indexOf(li)] : li; }
    function proxyFor(mainLi) { return proxyLis()[indexOf(mainLi)]; }
    function isOpen(li) { return li.classList.contains('navigation__item--show-children'); }
    function openItems() { return [].filter.call(mainLis(), isOpen); }
    function setOpen(li, open) {
      if (open) {
        openItems().forEach(function (o) { if (o !== li) o.dispatchEvent(new Event('mouseleave')); });
        li.dispatchEvent(new Event('mouseenter'));
      } else {
        li.dispatchEvent(new Event('mouseleave'));
      }
    }
    function visibleToggle(mainLi) {
      var p = proxyFor(mainLi);
      var pt = p && p.querySelector('.sds-nav-toggle');
      var mt = mainLi.querySelector(':scope > .sds-nav-toggle');
      return (pt && SDS.visible(pt.parentNode) && SDS.visible(p)) ? pt : mt;
    }
    function syncToggles() {
      [].forEach.call(mainLis(), function (li) {
        var open = isOpen(li) ? 'true' : 'false';
        [li, proxyFor(li)].forEach(function (item) {
          if (!item) return;
          var t = item.querySelector(':scope > .sds-nav-toggle');
          if (t) t.setAttribute('aria-expanded', open);
          var link = item.querySelector(':scope > .navigation__link[data-sds-b9]');
          if (link) { link.removeAttribute('aria-expanded'); link.removeAttribute('aria-haspopup'); }
        });
      });
    }
    function addToggle(li) {
      var link = li.querySelector(':scope > .navigation__link');
      var mainLi = mainFor(li);
      if (!link || !mainLi || li.querySelector(':scope > .sds-nav-toggle')) return;
      var panel = mainLi.querySelector(':scope > .navigation__child-tier, :scope > .navigation__tier-2-container');
      var name = SDS.text(link);
      link.setAttribute('data-sds-b9', '1');
      link.removeAttribute('aria-haspopup');
      link.removeAttribute('aria-expanded');
      link.removeAttribute('aria-controls');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sds-nav-toggle';
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-label', SDS.titleCase(name) + ' submenu');
      if (panel) btn.setAttribute('aria-controls', SDS.id(panel, 'sds-submenu'));
      btn.innerHTML = CHEVRON;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(mainLi, !isOpen(mainLi));
      });
      link.insertAdjacentElement('afterend', btn);
    }

    SDS.bug(9, 'Accessible dropdown menus (disclosure buttons, Esc, focus-out)', {
      css:
        '.sds-nav-toggle{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);' +
        'border:0;background:transparent;color:inherit;white-space:nowrap;cursor:pointer}' +
        // visible only on keyboard focus, drawn inside the link's 18px right padding → no layout shift
        '.sds-nav-toggle:focus-visible{position:relative;width:18px;height:24px;margin:0 0 0 -18px;overflow:visible;clip:auto;' +
        'display:inline-flex;align-items:center;justify-content:center;vertical-align:middle;outline:2px solid currentColor;outline-offset:0}',
      init: function () {
        // Enter on a top-level link = follow the link (stop the theme's "Enter opens menu" handler)
        document.addEventListener('keydown', function (e) {
          if (e.key !== 'Enter' || !e.target.closest) return;
          if (e.target.matches('#main-nav .navigation__tier-1 > .navigation__item > .navigation__link[data-sds-b9],' +
                               '#proxy-nav .navigation__tier-1 > .navigation__item > .navigation__link[data-sds-b9]')) {
            e.stopPropagation();
          }
        }, true);

        // Esc closes the open submenu and returns focus to its toggle
        document.addEventListener('keydown', function (e) {
          if (e.key !== 'Escape' && e.key !== 'Esc') return;
          var open = openItems();
          if (!open.length) return;
          open.forEach(function (li) {
            var p = proxyFor(li);
            var focusInside = li.contains(document.activeElement) || (p && p.contains(document.activeElement));
            setOpen(li, false);
            if (focusInside) { var t = visibleToggle(li); if (t) t.focus(); }
          });
        });

        // Proxy (inline) menu: dropdown content lives in #main-nav, so bridge Tab order
        document.addEventListener('keydown', function (e) {
          if (e.key !== 'Tab' || !e.target.closest) return;
          var proxyToggle = e.target.closest('#proxy-nav .sds-nav-toggle');
          if (proxyToggle && !e.shiftKey) {
            var mli = mainFor(proxyToggle.parentNode);
            if (mli && isOpen(mli)) {
              var first = SDS.focusables(mli.querySelector('.navigation__child-tier, .navigation__tier-2-container') || mli)[0];
              if (first) { e.preventDefault(); first.focus(); }
            }
            return;
          }
          var inMain = e.target.closest('#main-nav .navigation__tier-1 > .navigation__item--show-children');
          var proxyLi = inMain && proxyFor(inMain);
          if (inMain && proxyLi && SDS.visible(proxyLi)) {
            var items = SDS.focusables(inMain.querySelector('.navigation__child-tier, .navigation__tier-2-container') || inMain);
            if (!e.shiftKey && e.target === items[items.length - 1]) {
              var next = proxyLi.nextElementSibling && proxyLi.nextElementSibling.querySelector('.navigation__link');
              e.preventDefault(); setOpen(inMain, false); if (next) next.focus();
            } else if (e.shiftKey && e.target === items[0]) {
              e.preventDefault(); var t = proxyLi.querySelector('.sds-nav-toggle'); if (t) t.focus();
            }
          }
        });

        // Focus leaves an open item → close it
        document.addEventListener('focusin', function (e) {
          openItems().forEach(function (li) {
            var p = proxyFor(li);
            if (!li.contains(e.target) && !(p && p.contains(e.target))) setOpen(li, false);
          });
        });
      },
      run: function ($) {
        [].forEach.call(document.querySelectorAll(
          '#main-nav .navigation__tier-1 > .navigation__item--with-children, #proxy-nav .navigation__tier-1 > .navigation__item--with-children'
        ), addToggle);
        // desktop mega-menu column titles are plain links (tier 3 is always visible)
        $('#main-nav .navigation__tier-2 > .navigation__item > .navigation__column-title').removeAttr('aria-haspopup aria-expanded');
        [].forEach.call(mainLis(), function (li) {
          if (li.getAttribute('data-sds-b9')) return;
          li.setAttribute('data-sds-b9', '1');
          new MutationObserver(syncToggles).observe(li, { attributes: true, attributeFilter: ['class'] });
        });
        syncToggles();
      }
    });
  })();
  // ===== BUG 9 — END =====


  // ##########################################################################
  //  HOMEPAGE  (body.template-index)
  // ##########################################################################

  // ===== BUG 10 — START =====
  // Homepage · High · 1.3.1
  // "ALWAYS HOT. NEVER NOT." — Previous button, heading and Next button share one
  // container in DOM order (button → h2 → button), so they are read as one unit.
  // Fix: heading first in the DOM (flex + CSS order keeps the visual layout), buttons get
  //      descriptive names and point at the product slider.
  (function () {
    SDS.bug(10, 'Separate featured-collection heading from slider controls', {
      css:
        'body.template-index .hometitle.has-paging[data-sds-b10]{display:flex;align-items:center;justify-content:center;flex-wrap:wrap}' +
        'body.template-index .hometitle.has-paging[data-sds-b10]>[name="prev"]{order:1}' +
        'body.template-index .hometitle.has-paging[data-sds-b10]>h2{order:2}' +
        'body.template-index .hometitle.has-paging[data-sds-b10]>[name="next"]{order:3}',
      run: function ($) {
        if (!SDS.isTemplate('index')) return;
        $('.collection-slider-row .hometitle.has-paging').each(function () {
          var box = this;
          var h = box.querySelector(':scope > h2, :scope > .has-paging__title');
          if (!h) return;
          var title = SDS.text(h);
          if (!box.getAttribute('data-sds-b10')) {
            box.setAttribute('data-sds-b10', '1');
            box.insertBefore(h, box.firstChild);
          }
          var grid = $(box).closest('carousel-slider, .collection-slider-row').find('.slider__grid, .product-grid')[0];
          $(box).children('[name="prev"], [name="next"]').each(function () {
            var dir = this.getAttribute('name') === 'prev' ? 'Previous' : 'Next';
            var sr = this.querySelector('.visually-hidden');
            var label = dir + ' products: ' + SDS.titleCase(title);
            if (sr) { if (sr.textContent !== label) sr.textContent = label; }
            else this.setAttribute('aria-label', label);
            if (grid) this.setAttribute('aria-controls', SDS.id(grid, 'sds-slider'));
          });
        });
      }
    });
  })();
  // ===== BUG 10 — END =====


  // ===== BUG 11 — START =====
  // Homepage · High · 1.1.1
  // Product card image link has aria-hidden="true" and all images alt="".
  // Fix: expose the (non-tabbable) image link, give the visible image the product
  //      name, hide the inactive gallery images of the card.
  (function () {
    function fixBlock(block) {
      var title = SDS.text(block.querySelector('.product-block__title'));
      if (!title) return;
      [].forEach.call(block.querySelectorAll('.image-cont a.product-link[aria-hidden="true"]'), function (a) {
        a.removeAttribute('aria-hidden');   // tabindex="-1" stays → no duplicate tab stop
      });
      [].forEach.call(block.querySelectorAll('.product-block__image'), function (wrap) {
        var img = wrap.querySelector('img');
        if (wrap.classList.contains('product-block__image--active')) {
          wrap.removeAttribute('aria-hidden');
          if (img) { var alt = SDS.cleanAlt(img.getAttribute('alt')) || title; if (img.getAttribute('alt') !== alt) img.setAttribute('alt', alt); }
        } else if (wrap.getAttribute('aria-hidden') !== 'true') {
          wrap.setAttribute('aria-hidden', 'true');
        }
      });
    }
    SDS.bug(11, 'Homepage product card images exposed with alt text', {
      init: function () {
        document.addEventListener('click', function (e) {
          var b = e.target.closest && e.target.closest('body.template-index product-block');
          if (b) setTimeout(function () { fixBlock(b); }, 80);
        }, true);
      },
      run: function () {
        if (!SDS.isTemplate('index')) return;
        [].forEach.call(document.querySelectorAll('product-block'), fixBlock);
      }
    });
  })();
  // ===== BUG 11 — END =====


  // ===== BUG 12 — START =====
  // Homepage · High · 2.4.3, 4.1.2
  // Card image slider arrows are <a href="#" tabindex="-1"> shown on hover only,
  // so keyboard users can't reach them and focus disappears.
  (function () {
    SDS.bug(12, 'Homepage card image arrows as focusable buttons', {
      css:
        'body.template-index product-block .image-cont:focus-within .image-page-button,' +
        'body.template-index product-block .image-page-button:focus-visible' +
        '{opacity:1!important;visibility:visible!important;pointer-events:auto!important;transition:none!important}',
      init: function () {
        document.addEventListener('click', function (e) {
          var btn = e.target.closest && e.target.closest('body.template-index product-block .image-page-button');
          if (!btn) return;
          var block = btn.closest('product-block');
          setTimeout(function () {
            var imgs = block.querySelectorAll('.product-block__image');
            var active = [].indexOf.call(imgs, block.querySelector('.product-block__image--active')) + 1;
            if (active > 0) SDS.announce('Image ' + active + ' of ' + imgs.length);
            if (document.activeElement !== btn && SDS.visible(btn)) btn.focus();
          }, 60);
        });
      },
      run: function () {
        if (!SDS.isTemplate('index')) return;
        [].forEach.call(document.querySelectorAll('product-block .image-page-button'), function (btn) {
          var title = SDS.text(btn.closest('product-block').querySelector('.product-block__title'));
          SDS.buttonize(btn);
          if (btn.getAttribute('tabindex') !== '0') btn.setAttribute('tabindex', '0');
          var label = (btn.classList.contains('image-page-button--previous') ? 'Previous image' : 'Next image') + (title ? ', ' + title : '');
          if (btn.getAttribute('aria-label') !== label) btn.setAttribute('aria-label', label);
        });
      }
    });
  })();
  // ===== BUG 12 — END =====


  // ===== BUG 13 — START =====
  // Homepage · Low · 3.2.2, 4.1.2 (+ 2.1.1 / 2.4.3 found at retest)
  // "Quick buy" expands an inline panel (>=768px) with no state, no name and no focus move.
  // RETEST 24-Sep-2026: naming/state/focus-in all worked, but focus was NOT restored on
  // close — the theme hides .quickbuy-toggle with visibility:hidden until the card is
  // hovered, so it cannot be focused at all (keyboard users can never reach Quick buy).
  // Fix now: reveal the control while anything in the card has focus, restore focus
  // directly on close, and detect close from the panel itself (the theme does not always
  // dispatch on:quickbuy:after-close).
  (function () {
    var st = { opener: null };
    function panelFor(toggle) {
      var block = toggle.closest('product-block');
      return (block && block.querySelector('.quickbuy-container')) ||
             (toggle.closest('.collection-slider-row') && toggle.closest('.collection-slider-row').querySelector('.quickbuy-container'));
    }
    function restore() {
      setTimeout(function () {
        var o = st.opener;
        if (o && document.documentElement.contains(o)) { o.focus(); if (document.activeElement === o) return; }
        SDS.focusFirst([o, o && o.closest('product-block') && o.closest('product-block').querySelector('.product-block__title a, a.product-link')]);
      }, 350);
    }
    SDS.bug(13, 'Homepage Quick buy: state, name, focus management', {
      css:
        // Keyboard users must be able to see and reach the control (the theme reveals it on
        // hover only). transition:none is required — the theme's visibility/opacity transition
        // otherwise keeps the computed value pinned at hidden.
        'body.template-index product-block:focus-within .btn.quickbuy-toggle,' +
        'body.template-index product-block .btn.quickbuy-toggle:focus' +
        '{visibility:visible!important;opacity:1!important;transition:none!important}',
      init: function ($) {
        document.addEventListener('click', function (e) {
          var t = e.target.closest && e.target.closest('body.template-index .quickbuy-toggle');
          if (t) st.opener = t;
        }, true);
        // Enter/Space must reach the theme's click handler. Delegated (capture) so it keeps
        // working after the theme re-renders the card. RETEST NOTE: with the deployed build,
        // key presses on this control did not always reach the theme handler.
        document.addEventListener('keydown', function (e) {
          if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
          if (!e.target.closest) return;
          var t = e.target.closest('body.template-index .quickbuy-toggle');
          if (!t || window.innerWidth < 768) return;
          e.preventDefault();
          t.click();
        }, true);
        document.addEventListener('on:quickbuy:after-open', function (e) {
          if (!SDS.isTemplate('index')) return;
          var cont = e.target;
          setTimeout(function () {
            var h = cont.querySelector('.product-title');
            var title = SDS.text(h);
            cont.setAttribute('role', 'region');
            cont.setAttribute('aria-label', 'Quick buy: ' + title);
            if (h && h.tagName === 'H1') h = SDS.retag(h, 'h2');   // keep a single H1 on the page
            if (h) { h.setAttribute('tabindex', '-1'); h.focus(); }
            SDS.refresh();
          }, 150);
        });
        // close: theme event, close button, or the panel collapsing
        document.addEventListener('on:quickbuy:after-close', function () { if (SDS.isTemplate('index')) restore(); });
        document.addEventListener('click', function (e) {
          if (!SDS.isTemplate('index') || !e.target.closest) return;
          if (e.target.closest('.quickbuy-container .close-detail')) restore();
        }, true);
        document.addEventListener('keydown', function (e) {
          if ((e.key !== 'Escape' && e.key !== 'Esc') || !e.target.closest) return;
          var cont = e.target.closest('body.template-index .quickbuy-container');
          var close = cont && cont.querySelector('.close-detail');
          if (close) { e.preventDefault(); close.click(); restore(); }
        });
      },
      run: function () {
        if (!SDS.isTemplate('index')) return;
        var desktop = window.innerWidth >= 768;   // theme only expands inline on >=768px
        [].forEach.call(document.querySelectorAll('.quickbuy-toggle'), function (t) {
          var block = t.closest('product-block');
          var title = SDS.text(block && block.querySelector('.product-block__title'));
          t.setAttribute('aria-label', 'Quick buy, ' + title);
          var panel = panelFor(t);
          if (desktop && panel) {
            SDS.buttonize(t);
            t.setAttribute('role', 'button');
            t.setAttribute('aria-controls', SDS.id(panel, 'sds-quickbuy'));
            t.setAttribute('aria-expanded', block.classList.contains('expanded') ? 'true' : 'false');
          } else {
            t.removeAttribute('role'); t.removeAttribute('aria-controls'); t.removeAttribute('aria-expanded');
          }
        });
      }
    });
  })();
  // ===== BUG 13 — END =====


  // ===== BUG 14 — START =====
  // Homepage · Medium · 1.3.1
  // Product title AND price (and swatches) sit inside one <a class="product-link">.
  // Fix: keep only the title inside the link; move price/other content after it.
  (function () {
    SDS.bug(14, 'Homepage: price moved out of product title link', {
      run: function ($) {
        if (!SDS.isTemplate('index')) return;
        $('product-block .product-block__detail a.product-link').each(function () {
          var extra = $(this).children().not('.product-block__title');
          if (extra.length) $(this).after(extra);
        });
      }
    });
  })();
  // ===== BUG 14 — END =====


  // ===== BUG 15 — START =====
  // Homepage · High · 1.3.1
  // Price pieces ("Sale price", "$950", "Regular price", "$1,450", "Sale", "From")
  // are separate fragments; users can't tell which price is which.
  // Fix: one clear screen-reader sentence per price block; visual fragments hidden from AT.
  (function () {
    function priceText(el) {
      if (!el) return '';
      var c = el.cloneNode(true);
      [].forEach.call(c.querySelectorAll('.js-label, .visually-hidden, .price__from'), function (n) { n.remove(); });
      return SDS.text(c);
    }
    function labelPrice(price) {
      var cur = priceText(price.querySelector('.price__current'));
      if (!cur) return;
      var was = priceText(price.querySelector('.price__was'));
      var from = !!price.querySelector('.price__from');
      var unit = priceText(price.querySelector('.unit-price, .price__unit'));
      var parts = [];
      if (price.classList.contains('price--on-sale') && was) {
        parts.push('Sale price ' + (from ? 'from ' : '') + cur, 'Regular price ' + was);
      } else {
        parts.push((from ? 'Price from ' : 'Regular price ') + cur);
      }
      if (unit) parts.push('Unit price ' + unit);
      var label = parts.join(', ');
      if (price.getAttribute('data-sds-price') === label) return;
      price.setAttribute('data-sds-price', label);
      [].forEach.call(price.querySelectorAll(':scope > .sds-price-sr'), function (n) { n.remove(); });
      [].forEach.call(price.children, function (child) { child.setAttribute('aria-hidden', 'true'); });
      var sr = document.createElement('span');
      sr.className = 'sds-sr-only sds-price-sr';
      sr.textContent = label;
      price.insertBefore(sr, price.firstChild);
    }
    SDS.bug(15, 'Homepage: labelled product prices', {
      run: function () {
        if (!SDS.isTemplate('index')) return;
        [].forEach.call(document.querySelectorAll('product-block .product-price .price'), labelPrice);
      }
    });
  })();
  // ===== BUG 15 — END =====


  // ===== BUG 16 — START =====
  // Homepage · High · 1.1.1
  // "EVERY COLOR, EVERY YEAR." image alt = "#color_berry-velvet-with-sunset-velvet-piping".
  (function () {
    var ALT = {
      'Marlene-Photoshoot.29': 'Living room with a lavender sofa, rattan coffee table and pink alligator rug, where two people in dog masks lounge'
    };
    SDS.bug(16, 'Alt text: EVERY COLOR, EVERY YEAR image', {
      run: function () { if (SDS.isTemplate('index')) SDS.altByFile(ALT, 'main'); }
    });
  })();
  // ===== BUG 16 — END =====


  // ===== BUG 17 — START =====
  // Homepage · High · 1.1.1
  // "SCOUT BEDDING" image alt = "#color_marine-blue-velvet-with-azure-blue-velvet-circle".
  (function () {
    var ALT = {
      'Marlene-Photoshoot.23': 'Bedroom seen through open French doors: navy upholstered headboard with a round cushion, white bedding with green embroidered accents and a blue rug'
    };
    SDS.bug(17, 'Alt text: SCOUT BEDDING image', {
      run: function () { if (SDS.isTemplate('index')) SDS.altByFile(ALT, 'main'); }
    });
  })();
  // ===== BUG 17 — END =====


  // ===== BUG 18 — START =====
  // Homepage · High · 1.1.1
  // "COME SEE US IRL" image has alt="".
  (function () {
    var ALT = {
      'Add_a_heading_5c23b8a5': 'Watercolor illustration of the Scout Design Studio showroom with a pink striped awning, elephant sculpture, rainbow-striped wall and a cat lounging in the window'
    };
    SDS.bug(18, 'Alt text: COME SEE US IRL image', {
      run: function () { if (SDS.isTemplate('index')) SDS.altByFile(ALT, 'main'); }
    });
  })();
  // ===== BUG 18 — END =====


  // ===== BUG 19 — START =====
  // Homepage · Medium · 1.3.1
  // Paragraphs use runs of <br><br> for spacing (e.g. "COME SEE US IRL" address block),
  // producing blank/duplicated lines in screen readers.
  // Fix: split paragraphs at every run of 2+ <br> into real <p> elements and drop
  //      leading/trailing <br>. Single line breaks (address lines) are kept.
  (function () {
    function isBr(n) { return n.nodeType === 1 && n.tagName === 'BR'; }
    function isBlank(n) { return n.nodeType === 3 && !n.nodeValue.trim(); }
    function splitParagraph(p) {
      var groups = [[]], nodes = [].slice.call(p.childNodes), i = 0;
      while (i < nodes.length) {
        if (isBr(nodes[i])) {
          var j = i, brs = 0;
          while (j < nodes.length && (isBr(nodes[j]) || isBlank(nodes[j]))) { if (isBr(nodes[j])) brs++; j++; }
          if (brs >= 2) { groups.push([]); i = j; continue; }
        }
        groups[groups.length - 1].push(nodes[i]);
        i++;
      }
      groups = groups.map(function (g) {
        while (g.length && (isBr(g[0]) || isBlank(g[0]))) g.shift();
        while (g.length && (isBr(g[g.length - 1]) || isBlank(g[g.length - 1]))) g.pop();
        return g;
      }).filter(function (g) { return g.length; });
      if (groups.length < 2 && p.lastChild && !isBr(p.lastChild)) { p.setAttribute('data-sds-b19', '1'); return; }
      var frag = document.createDocumentFragment();
      groups.forEach(function (g) {
        var np = p.cloneNode(false);
        np.setAttribute('data-sds-b19', '1');
        g.forEach(function (n) { np.appendChild(n); });
        frag.appendChild(np);
      });
      p.parentNode.replaceChild(frag, p);
    }
    SDS.bug(19, 'Homepage: <br><br> spacing replaced by paragraphs', {
      run: function () {
        if (!SDS.isTemplate('index')) return;
        [].forEach.call(document.querySelectorAll('main .rte p:not([data-sds-b19])'), function (p) {
          if (p.querySelector('br')) splitParagraph(p); else p.setAttribute('data-sds-b19', '1');
        });
      }
    });
  })();
  // ===== BUG 19 — END =====


  // ===== BUG 20 — START =====
  // Homepage (+ product) · Medium · 3.2.2 — NOT FULLY FIXABLE FROM THE THEME
  // "Powered by Emplifi" (Pixlee UGC widget) has target="blank" and no new-tab warning.
  // RETEST 24-Sep-2026: measured with no script touching it, the widget REBUILDS this
  // link continuously (12 new nodes in 3 seconds), so any attribute or child we add is
  // wiped within ~250ms. Inserting a child element also makes the widget re-render in a
  // loop and can lock the page up, so this block never inserts anything.
  // What this does: patch attributes at the moment the link is focused, hovered or
  // pressed (the point where the name is actually announced/used) plus on each normal pass.
  // What the client must do for a durable fix (vendor-side):
  //   • turn off "Powered by Emplifi" in the Pixlee/Emplifi widget settings, or
  //   • ask Emplifi support to give the branding link a correct target/rel and an
  //     accessible name that includes the new-tab warning.
  (function () {
    var SEL = 'a[id^="powered_by_pixlee"], [id^="pixlee_container"] a[target]';
    var LABEL = 'Powered by Emplifi, opens in a new tab';
    function patch(a) {
      if (!a) return;
      if (a.getAttribute('target') !== '_blank') a.setAttribute('target', '_blank');
      var rel = a.getAttribute('rel') || '';
      if (rel.indexOf('noopener') < 0) a.setAttribute('rel', (rel + ' noopener').trim());
      if (a.getAttribute('aria-label') !== LABEL) a.setAttribute('aria-label', LABEL);
    }
    SDS.bug(20, '"Powered by Emplifi" new-tab warning (best effort — vendor fix needed)', {
      init: function () {
        ['focusin', 'mouseover', 'pointerdown', 'touchstart'].forEach(function (ev) {
          document.addEventListener(ev, function (e) {
            var a = e.target.closest && e.target.closest(SEL);
            if (a) patch(a);
          }, true);
        });
      },
      run: function () { [].forEach.call(document.querySelectorAll(SEL), patch); }
    });
  })();
  // ===== BUG 20 — END =====


  // ===== BUG 21 — START =====
  // Homepage · Medium · 1.1.1
  // Decorative swatch thumbnails in product cards are announced with alt values like
  // "#color_bone-velvet-with-black-velvet-piping" next to the real colour name.
  (function () {
    SDS.bug(21, 'Homepage: decorative swatch images hidden (alt="")', {
      run: function () {
        if (!SDS.isTemplate('index')) return;
        [].forEach.call(document.querySelectorAll('.product-block-options__item img, img#pixlee-logo'), function (img) {
          if (img.getAttribute('alt') !== '') img.setAttribute('alt', '');
        });
      }
    });
  })();
  // ===== BUG 21 — END =====


  // ===== BUG 22 — START =====
  // Global footer (logged on Homepage) · Medium · 3.2.2
  // Social links open in a new tab without warning.
  (function () {
    SDS.bug(22, 'Social links: descriptive name + new-tab warning', {
      run: function () {
        [].forEach.call(document.querySelectorAll('a.social__link'), function (a) {
          if (a.getAttribute('data-sds-b22')) return;
          a.setAttribute('data-sds-b22', '1');
          var network = SDS.text(a.querySelector('.visually-hidden')) || (a.getAttribute('title') || '').replace(/^.* on /, '');
          a.setAttribute('aria-label', 'Follow Scout Design Studio on ' + network);
          SDS.newTab(a);
        });
      }
    });
  })();
  // ===== BUG 22 — END =====


  // ##########################################################################
  //  COLLECTION PAGE  (body.template-collection) — /collections/bestsellers
  //  Product grid is rendered by Depict; run() re-applies after every re-render.
  // ##########################################################################

  // ===== BUG 23 — START =====
  // Collection · High · 1.3.1
  // Gallery section below the grid repeats the same H2 "Trending:" on every tile.
  // Fix: each heading gets its tile name ("Trending: Wimbledon Series"); the visual
  //      name is hidden from AT to avoid a double read; the image-only link is named.
  (function () {
    SDS.bug(23, 'Collection: unique "Trending:" gallery headings', {
      run: function () {
        if (!SDS.isTemplate('collection')) return;
        [].forEach.call(document.querySelectorAll('.section-gallery .gallery__item:not([data-sds-b23])'), function (item) {
          var h = item.querySelector('.text-overlay__title');
          var nameEl = item.querySelector('.text-overlay__button');
          var name = SDS.text(nameEl);
          if (!h || !name) return;
          item.setAttribute('data-sds-b23', '1');
          var base = SDS.text(h).replace(/:\s*$/, '');
          var sr = document.createElement('span');
          sr.className = 'sds-sr-only';
          sr.textContent = ' ' + name;
          h.appendChild(sr);
          nameEl.setAttribute('aria-hidden', 'true');
          var link = item.querySelector('a.image-overlay__image-link');
          if (link && !SDS.text(link)) link.setAttribute('aria-label', base + ': ' + name);
        });
      }
    });
  })();
  // ===== BUG 23 — END =====


  // ===== BUG 24 — START =====
  // Collection · Low · 4.1.2, 2.4.3
  // "Filter" toggle is <a href="#">, announced as a link, no state, focus lost.
  // Fix: button semantics + aria-expanded/aria-controls; on mobile (drawer) focus
  //      moves into the filters and back to the toggle when they close.
  (function () {
    var st = { toggle: null };
    function filtersOpen(fc) {
      return window.innerWidth >= 768
        ? fc.classList.contains('filter-container--show-filters-desktop')
        : fc.classList.contains('filter-container--show-filters-mobile');
    }
    function sync(fc) {
      var filters = fc.querySelector('.filters');
      var open = filtersOpen(fc);
      [].forEach.call(document.querySelectorAll('.toggle-btn[data-toggle-filters]'), function (t) {
        t.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (filters) t.setAttribute('aria-controls', SDS.id(filters, 'sds-filters'));
      });
      var mobile = window.innerWidth < 768;
      if (mobile && open && filters) {
        SDS.trap(filters, { onEscape: function (e) { var c = fc.querySelector('.filters__close'); if (c) { e.preventDefault(); c.click(); } } });
        setTimeout(function () { SDS.focusFirst([fc.querySelector('.filters__heading-text'), fc.querySelector('.filters__close')]); }, 300);
      } else if (mobile && !open && filters) {
        SDS.releaseTrap(filters);
        setTimeout(function () { SDS.focusFirst([st.toggle, '.toggle-btn[data-toggle-filters]']); }, 300);
      } else if (st.toggle && (document.activeElement === document.body || !document.activeElement)) {
        st.toggle.focus();   // desktop: keep focus on the toggle
      }
    }
    SDS.bug(24, 'Collection: Filter toggle as stateful button', {
      init: function () {
        document.addEventListener('click', function (e) {
          var t = e.target.closest && e.target.closest('.toggle-btn[data-toggle-filters]');
          if (t) st.toggle = t;
        }, true);
      },
      run: function () {
        if (!SDS.isTemplate('collection')) return;
        [].forEach.call(document.querySelectorAll('.toggle-btn[data-toggle-filters], .filters__close'), SDS.buttonize);
        var fc = document.querySelector('filter-container');
        if (!fc) return;
        if (!fc.getAttribute('data-sds-b24')) {
          fc.setAttribute('data-sds-b24', '1');
          new MutationObserver(function () { sync(fc); }).observe(fc, { attributes: true, attributeFilter: ['class'] });
        }
        var open = filtersOpen(fc) ? 'true' : 'false';
        [].forEach.call(document.querySelectorAll('.toggle-btn[data-toggle-filters]'), function (t) {
          if (t.getAttribute('aria-expanded') !== open) t.setAttribute('aria-expanded', open);
        });
      }
    });
  })();
  // ===== BUG 24 — END =====


  // ===== BUG 25 — START =====
  // Collection · High · 4.1.2
  // Price filter heading (<toggle-target role="button">) has no aria-expanded.
  // Applied to every filter group heading (Price, Stock Status, …) — same component.
  (function () {
    function sync(heading, panel) {
      var v = panel.classList.contains('toggle-target--hidden') ? 'false' : 'true';
      if (heading.getAttribute('aria-expanded') !== v) heading.setAttribute('aria-expanded', v);
    }
    SDS.bug(25, 'Collection: filter group headings expose expanded state', {
      run: function () {
        if (!SDS.isTemplate('collection')) return;
        [].forEach.call(document.querySelectorAll('.filter-group__heading'), function (heading) {
          var panel = heading.nextElementSibling;
          if (!panel || !panel.classList.contains('toggle-target')) return;
          heading.setAttribute('role', 'button');
          if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '0');
          heading.setAttribute('aria-controls', SDS.id(panel, 'sds-filter-group'));
          if (!heading.getAttribute('data-sds-b25')) {
            heading.setAttribute('data-sds-b25', '1');
            heading.addEventListener('keydown', function (e) {   // theme only supports Enter
              if (e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); heading.click(); }
            });
            new MutationObserver(function () { sync(heading, panel); }).observe(panel, { attributes: true, attributeFilter: ['class'] });
          }
          sync(heading, panel);
        });
      }
    });
  })();
  // ===== BUG 25 — END =====


  // ===== BUG 26 — START =====
  // Collection · Medium · 2.1.1, 4.1.2
  // Product card image slider arrows (same component as Bug 12, collection scope).
  (function () {
    SDS.bug(26, 'Collection card image arrows as focusable buttons', {
      css:
        'body.template-collection product-block .image-cont:focus-within .image-page-button,' +
        'body.template-collection product-block .image-page-button:focus-visible' +
        '{opacity:1!important;visibility:visible!important;pointer-events:auto!important;transition:none!important}',
      init: function () {
        document.addEventListener('click', function (e) {
          var btn = e.target.closest && e.target.closest('body.template-collection product-block .image-page-button');
          if (!btn) return;
          var block = btn.closest('product-block');
          setTimeout(function () {
            var imgs = block.querySelectorAll('.product-block__image');
            var active = [].indexOf.call(imgs, block.querySelector('.product-block__image--active')) + 1;
            if (active > 0) SDS.announce('Image ' + active + ' of ' + imgs.length);
            if (document.activeElement !== btn && SDS.visible(btn)) btn.focus();
          }, 60);
        });
      },
      run: function () {
        if (!SDS.isTemplate('collection')) return;
        [].forEach.call(document.querySelectorAll('product-block .image-page-button'), function (btn) {
          var title = SDS.text(btn.closest('product-block').querySelector('.product-block__title'));
          SDS.buttonize(btn);
          if (btn.getAttribute('tabindex') !== '0') btn.setAttribute('tabindex', '0');
          var label = (btn.classList.contains('image-page-button--previous') ? 'Previous image' : 'Next image') + (title ? ', ' + title : '');
          if (btn.getAttribute('aria-label') !== label) btn.setAttribute('aria-label', label);
        });
      }
    });
  })();
  // ===== BUG 26 — END =====


  // ===== BUG 27 — START =====
  // Collection · Low · 2.4.3, 4.1.2 (+ 2.1.1 found at retest)
  // Quick buy (same component as Bug 13, collection scope).
  // RETEST 24-Sep-2026: same defect as Bug 13 — .quickbuy-toggle is visibility:hidden
  // until hover, so it is unreachable by keyboard and focus could not be restored.
  (function () {
    var st = { opener: null };
    function restore() {
      setTimeout(function () {
        var o = st.opener;
        if (o && document.documentElement.contains(o)) { o.focus(); if (document.activeElement === o) return; }
        SDS.focusFirst([o, o && o.closest('product-block') && o.closest('product-block').querySelector('a.product-link')]);
      }, 350);
    }
    SDS.bug(27, 'Collection Quick buy: state, name, focus management', {
      css:
        // 'body.template-collection product-block:focus-within .btn.quickbuy-toggle,' +
        // 'body.template-collection product-block .btn.quickbuy-toggle:focus' +
        // '{visibility:visible!important;opacity:1!important;transition:none!important}',
        'body.template-collection product-block .image-cont .btn.quickbuy-toggle' +
        '{visibility:visible!important;pointer-events:none}' +
        'body.template-collection product-block .image-cont:hover .btn.quickbuy-toggle,' +
        'body.template-collection product-block:focus-within .btn.quickbuy-toggle,' +
        'body.template-collection product-block .btn.quickbuy-toggle:focus-visible' +
        '{opacity:1!important;pointer-events:auto;transition:none!important}',
      init: function () {
        document.addEventListener('click', function (e) {
          var t = e.target.closest && e.target.closest('body.template-collection .quickbuy-toggle');
          if (t) st.opener = t;
        }, true);
        // Enter/Space must reach the theme's click handler. Delegated (capture) so it keeps
        // working after the theme re-renders the card. RETEST NOTE: with the deployed build,
        // key presses on this control did not always reach the theme handler.
        document.addEventListener('keydown', function (e) {
          if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
          if (!e.target.closest) return;
          var t = e.target.closest('body.template-collection .quickbuy-toggle');
          if (!t || window.innerWidth < 768) return;
          e.preventDefault();
          t.click();
        }, true);
        document.addEventListener('on:quickbuy:after-open', function (e) {
          if (!SDS.isTemplate('collection')) return;
          var cont = e.target;
          setTimeout(function () {
            var h = cont.querySelector('.product-title');
            cont.setAttribute('role', 'region');
            cont.setAttribute('aria-label', 'Quick buy: ' + SDS.text(h));
            if (h && h.tagName === 'H1') h = SDS.retag(h, 'h2');
            if (h) { h.setAttribute('tabindex', '-1'); h.focus(); }
            SDS.refresh();
          }, 150);
        });
        document.addEventListener('on:quickbuy:after-close', function () { if (SDS.isTemplate('collection')) restore(); });
        document.addEventListener('click', function (e) {
          if (!SDS.isTemplate('collection') || !e.target.closest) return;
          if (e.target.closest('.quickbuy-container .close-detail')) restore();
        }, true);
        document.addEventListener('keydown', function (e) {
          if ((e.key !== 'Escape' && e.key !== 'Esc') || !e.target.closest) return;
          var cont = e.target.closest('body.template-collection .quickbuy-container');
          var close = cont && cont.querySelector('.close-detail');
          if (close) { e.preventDefault(); close.click(); restore(); }
        });
      },
      run: function () {
        if (!SDS.isTemplate('collection')) return;
        var desktop = window.innerWidth >= 768;
        [].forEach.call(document.querySelectorAll('.quickbuy-toggle'), function (t) {
          var block = t.closest('product-block');
          var panel = block && block.querySelector('.quickbuy-container');
          t.setAttribute('aria-label', 'Quick buy, ' + SDS.text(block && block.querySelector('.product-block__title')));
          if (desktop && panel) {
            SDS.buttonize(t);
            t.setAttribute('role', 'button');
            t.setAttribute('aria-controls', SDS.id(panel, 'sds-quickbuy'));
            t.setAttribute('aria-expanded', block.classList.contains('expanded') ? 'true' : 'false');
          } else {
            t.removeAttribute('role'); t.removeAttribute('aria-controls'); t.removeAttribute('aria-expanded');
          }
        });
      }
    });
  })();
  // ===== BUG 27 — END =====


  // ===== BUG 28 — START =====
  // Collection · Medium · 1.3.1
  // Title + price inside one link (same component as Bug 14, collection scope).
  (function () {
    SDS.bug(28, 'Collection: price moved out of product title link', {
      run: function ($) {
        if (!SDS.isTemplate('collection')) return;
        $('product-block .product-block__detail a.product-link').each(function () {
          var extra = $(this).children().not('.product-block__title');
          if (extra.length) $(this).after(extra);
        });
      }
    });
  })();
  // ===== BUG 28 — END =====


  // ===== BUG 29 — START =====
  // Collection · High · 1.3.1
  // Price labels (same component as Bug 15, collection scope).
  (function () {
    function priceText(el) {
      if (!el) return '';
      var c = el.cloneNode(true);
      [].forEach.call(c.querySelectorAll('.js-label, .visually-hidden, .price__from'), function (n) { n.remove(); });
      return SDS.text(c);
    }
    function labelPrice(price) {
      var cur = priceText(price.querySelector('.price__current'));
      if (!cur) return;
      var was = priceText(price.querySelector('.price__was'));
      var from = !!price.querySelector('.price__from');
      var unit = priceText(price.querySelector('.unit-price, .price__unit'));
      var parts = [];
      if (price.classList.contains('price--on-sale') && was) parts.push('Sale price ' + (from ? 'from ' : '') + cur, 'Regular price ' + was);
      else parts.push((from ? 'Price from ' : 'Regular price ') + cur);
      if (unit) parts.push('Unit price ' + unit);
      var label = parts.join(', ');
      if (price.getAttribute('data-sds-price') === label) return;
      price.setAttribute('data-sds-price', label);
      [].forEach.call(price.querySelectorAll(':scope > .sds-price-sr'), function (n) { n.remove(); });
      [].forEach.call(price.children, function (child) { child.setAttribute('aria-hidden', 'true'); });
      var sr = document.createElement('span');
      sr.className = 'sds-sr-only sds-price-sr';
      sr.textContent = label;
      price.insertBefore(sr, price.firstChild);
    }
    SDS.bug(29, 'Collection: labelled product prices', {
      run: function () {
        if (!SDS.isTemplate('collection')) return;
        [].forEach.call(document.querySelectorAll('product-block .product-price .price'), labelPrice);
      }
    });
  })();
  // ===== BUG 29 — END =====


  // ===== BUG 30 — START =====
  // Collection · Medium · 2.4.6
  // Pagination has no navigation landmark/label, no current-page info.
  (function () {
    SDS.bug(30, 'Collection: labelled pagination', {
      run: function () {
        if (!SDS.isTemplate('collection')) return;
        [].forEach.call(document.querySelectorAll('.pagination'), function (p) {
          p.setAttribute('role', 'navigation');
          p.setAttribute('aria-label', 'Pagination');
          [].forEach.call(p.querySelectorAll('.pagination__number'), function (n) {
            var a = n.querySelector('a');
            if (a) { a.setAttribute('aria-label', 'Page ' + SDS.text(a)); return; }
            if (!SDS.text(n) || n.getAttribute('data-sds-b30')) return;
            n.setAttribute('data-sds-b30', '1');
            n.setAttribute('aria-current', 'page');
            n.insertAdjacentHTML('afterbegin', '<span class="sds-sr-only">Page </span>');
            n.insertAdjacentHTML('beforeend', '<span class="sds-sr-only">, current page</span>');
          });
          [].forEach.call(p.querySelectorAll('.pagination__next'), function (a) { a.setAttribute('aria-label', 'Next page'); });
          [].forEach.call(p.querySelectorAll('.pagination__prev'), function (a) { a.setAttribute('aria-label', 'Previous page'); });
          [].forEach.call(p.querySelectorAll('.pagination__sep'), function (s) { s.setAttribute('aria-hidden', 'true'); });
        });
      }
    });
  })();
  // ===== BUG 30 — END =====


  // ===== BUG 31 — START =====
  // Collection · High · 4.1.2
  // Colour swatches on product cards are mouse-only <span>s; screen readers only hear
  // images, no selected state, no hint that the card image changes.
  // Fix: labelled group of toggle buttons (aria-pressed), keyboard operable,
  //      announcement of the image change, hidden "+N more" gets a readable label.
  (function () {
    SDS.bug(31, 'Collection: accessible colour swatches', {
      init: function () {
        document.addEventListener('click', function (e) {
          var item = e.target.closest && e.target.closest('body.template-collection .product-block-options__item[data-sds-b31]');
          if (!item) return;
          var group = item.closest('.product-block-options__inner');
          [].forEach.call(group.querySelectorAll('[data-sds-b31]'), function (s) { s.setAttribute('aria-pressed', s === item ? 'true' : 'false'); });
          var block = item.closest('product-block');
          var title = SDS.text(block.querySelector('.product-block__title'));
          var name = item.getAttribute('aria-label');
          setTimeout(function () {   // theme only swaps the image when that colour's photo is in the card
            var active = block.querySelector('.product-block__image--active');
            var changed = active && active.getAttribute('data-media-id') === item.getAttribute('data-media');
            SDS.announce(name + ' selected' + (changed ? '. Image updated to ' + name + ', ' + title : '') + '.');
          }, 150);
        });
      },
      run: function () {
        if (!SDS.isTemplate('collection')) return;
        [].forEach.call(document.querySelectorAll('product-block .product-block-options--swatch'), function (opts) {
          var block = opts.closest('product-block');
          var title = SDS.text(block.querySelector('.product-block__title'));
          var optName = opts.getAttribute('data-option-name') || 'Color';
          var inner = opts.querySelector('.product-block-options__inner') || opts;
          inner.setAttribute('role', 'group');
          inner.setAttribute('aria-label', optName + ' options for ' + title);
          [].forEach.call(opts.querySelectorAll('.product-block-options__item'), function (item) {
            [].forEach.call(item.querySelectorAll('img'), function (img) { if (img.getAttribute('alt') !== '') img.setAttribute('alt', ''); });
            if (!item.hasAttribute('data-media')) return;   // theme only binds swatches that change the image
            // Bug 110 makes the hover-only ("+N more") swatches keyboard reachable — leave those exposed
            if (!SDS.visible(item) && !item.getAttribute('data-sds-b110')) {
              item.setAttribute('aria-hidden', 'true'); item.setAttribute('tabindex', '-1'); return;
            }
            item.removeAttribute('aria-hidden');
            var name = SDS.text(item.querySelector('.product-block-options__item__text'));
            if (!item.getAttribute('data-sds-b31')) {
              item.setAttribute('data-sds-b31', '1');
              item.setAttribute('aria-pressed', 'false');
              SDS.buttonize(item);
            }
            item.setAttribute('tabindex', '0');
            item.setAttribute('aria-label', name);
          });
          [].forEach.call(opts.querySelectorAll('.product-block-options__more-label:not([data-sds-b31])'), function (more) {
            var n = (SDS.text(more).match(/\d+/) || [])[0];
            if (!n) return;
            more.setAttribute('data-sds-b31', '1');
            more.setAttribute('aria-hidden', 'true');
            more.insertAdjacentHTML('afterend', '<span class="sds-sr-only">' + n + ' more ' + optName.toLowerCase() + ' options on the product page</span>');
          });
        });
      }
    });
  })();
  // ===== BUG 31 — END =====


  // ##########################################################################
  //  PRODUCT PAGE  (body.template-product)
  //  /collections/bestsellers/products/framed-butterfly-print-in-pink-marshmallow
  // ##########################################################################

  // ===== BUG 32 — START =====
  // Product · Medium · 2.4.4
  // Breadcrumb "Next" product link is announced only as "Next".
  (function () {
    SDS.bug(32, 'Product: descriptive Next/Previous product links', {
      run: function () {
        if (!SDS.isTemplate('product')) return;
        [].forEach.call(document.querySelectorAll('.breadcrumbs-prod-nav__link'), function (a) {
          var dir = a.classList.contains('breadcrumbs-prod-nav__link--prev') ? 'Previous product' : 'Next product';
          var name = a.getAttribute('title') || a.getAttribute('data-sds-title') || '';
          if (name) a.setAttribute('data-sds-title', name);
          a.setAttribute('aria-label', dir + (name ? ': ' + name : ''));
          a.removeAttribute('title');   // avoid a duplicate tooltip announcement
        });
      }
    });
  })();
  // ===== BUG 32 — END =====


  // ===== BUG 33 — START =====
  // Product · High · 1.3.1
  // Product media carousel has role="region" aria-roledescription="Carousel" but an
  // EMPTY aria-label, so users aren't told it is a media gallery.
  (function () {
    SDS.bug(33, 'Product: named media gallery region', {
      run: function () {
        if (!SDS.isTemplate('product')) return;
        [].forEach.call(document.querySelectorAll('media-gallery .main-image carousel-slider'), function (c) {
          var count = c.querySelectorAll('.slider__item').length;
          var title = SDS.text(document.querySelector('.product-title'));
          c.setAttribute('role', 'region');
          c.setAttribute('aria-roledescription', 'carousel');
          c.setAttribute('aria-label', 'Product media gallery' + (title ? ', ' + title : '') + (count ? ', ' + count + ' items' : ''));
        });
      }
    });
  })();
  // ===== BUG 33 — END =====


  // ===== BUG 34 — START =====
  // Product · High · 1.3.1
  // Off-screen gallery slides are still exposed/tabbable; link labels
  // ("Load image 1 in gallery view") and empty image alts are inaccurate.
  (function () {
    function update(gallery) {
      var title = SDS.text(document.querySelector('.product-title'));
      var slides = gallery.querySelectorAll('.main-image .slider__item');
      [].forEach.call(slides, function (slide, i) {
        var active = slide.classList.contains('is-active');
        if (active) slide.removeAttribute('aria-hidden'); else slide.setAttribute('aria-hidden', 'true');
        [].forEach.call(slide.querySelectorAll('a, button, [tabindex]'), function (el) {
          if (el === slide) return;
          if (!active) {
            if (!el.hasAttribute('data-sds-tabindex')) el.setAttribute('data-sds-tabindex', el.getAttribute('tabindex') || '');
            el.setAttribute('tabindex', '-1');
          } else if (el.hasAttribute('data-sds-tabindex')) {
            var orig = el.getAttribute('data-sds-tabindex');
            if (orig) el.setAttribute('tabindex', orig); else el.removeAttribute('tabindex');
            el.removeAttribute('data-sds-tabindex');
          }
        });
        var n = i + 1, total = slides.length;
        var link = slide.querySelector('a.show-gallery');
        if (link) link.setAttribute('aria-label', 'Open image ' + n + ' of ' + total + ' in full-screen gallery');
        var img = slide.querySelector('img');
        if (img) {
          var alt = SDS.cleanAlt(img.getAttribute('alt')) || (title + ', image ' + n + ' of ' + total);
          if (img.getAttribute('alt') !== alt) img.setAttribute('alt', alt);
        }
      });
    }
    SDS.bug(34, 'Product: only the visible slide is exposed; accurate labels', {
      run: function () {
        if (!SDS.isTemplate('product')) return;
        [].forEach.call(document.querySelectorAll('media-gallery'), function (g) {
          if (!g.getAttribute('data-sds-b34')) {
            g.setAttribute('data-sds-b34', '1');
            var main = g.querySelector('.main-image');
            if (main) new MutationObserver(function () { update(g); }).observe(main, { attributes: true, subtree: true, attributeFilter: ['class'] });
          }
          update(g);
        });
      }
    });
  })();
  // ===== BUG 34 — END =====


  // ===== BUG 35 — START =====
  // Product · Medium · 4.1.2
  // Gallery arrows are read as "Previous" / "Next".
  (function () {
    SDS.bug(35, 'Product: gallery arrows named Previous/Next slide', {
      run: function () {
        if (!SDS.isTemplate('product')) return;
        [].forEach.call(document.querySelectorAll('media-gallery .slider-nav__btn'), function (b) {
          var label = b.getAttribute('name') === 'prev' ? 'Previous slide' : 'Next slide';
          var sr = b.querySelector('.visually-hidden');
          if (sr) { if (sr.textContent !== label) sr.textContent = label; }
          else if (b.getAttribute('aria-label') !== label) b.setAttribute('aria-label', label);
        });
      }
    });
  })();
  // ===== BUG 35 — END =====


  // ===== BUG 36 — START =====
  // Product · Medium · 1.1.1
  // Thumbnail images have empty / "#color_…" style alt text.
  // Fix: thumbnail = button "Show image n of N: <description>", active one marked
  //      aria-current; the image inside is decorative (the button carries the name).
  (function () {
    SDS.bug(36, 'Product: meaningful thumbnail names', {
      run: function () {
        if (!SDS.isTemplate('product')) return;
        var title = SDS.text(document.querySelector('.product-title'));
        [].forEach.call(document.querySelectorAll('media-gallery .thumbnails'), function (thumbs) {
          var items = thumbs.querySelectorAll('.slider__item');
          [].forEach.call(items, function (li, i) {
            var a = li.querySelector('a.thumbnail, a, button');
            if (!a) return;
            var img = a.querySelector('img');
            var desc = img ? (SDS.cleanAlt(img.getAttribute('data-sds-alt') || img.getAttribute('alt')) || title) : title;
            if (img) {
              if (!img.hasAttribute('data-sds-alt')) img.setAttribute('data-sds-alt', SDS.cleanAlt(img.getAttribute('alt')));
              if (img.getAttribute('alt') !== '') img.setAttribute('alt', '');
            }
            SDS.buttonize(a);
            a.setAttribute('aria-label', 'Show image ' + (i + 1) + ' of ' + items.length + (desc ? ': ' + desc : ''));
            if (li.classList.contains('is-active')) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
          });
        });
      }
    });
  })();
  // ===== BUG 36 — END =====


  // ===== BUG 37 — START =====
  // Product · Low · 2.4.3, 4.1.2
  // "Add to cart" opens the cart drawer without telling the user and focus stays behind.
  (function () {
    var st = { clickedAt: 0 };
    function onDrawer(drawer) {
      if (!drawer.hasAttribute('open') || Date.now() - st.clickedAt > 10000) return;
      st.clickedAt = 0;
      setTimeout(function () {
        var title = drawer.querySelector('#CartDrawerModal-Title');
        SDS.focusFirst([title, drawer.querySelector('.drawer__close-btn')]);
        SDS.announce('Item added to your cart.');
      }, 350);
    }
    SDS.bug(37, 'Product: Add to cart announces the cart dialog and moves focus', {
      init: function () {
        document.addEventListener('click', function (e) {
          if (e.target.closest && e.target.closest('body.template-product .js-product-form [name="add"], body.template-product .add-to-cart')) st.clickedAt = Date.now();
        }, true);
      },
      run: function () {
        if (!SDS.isTemplate('product')) return;
        var drawer = document.querySelector('cart-drawer');
        if (!drawer) return;
        SDS.id(drawer, 'CartDrawer');
        [].forEach.call(document.querySelectorAll('.js-product-form [name="add"]'), function (b) {
          b.setAttribute('aria-haspopup', 'dialog');
          b.setAttribute('aria-controls', drawer.id);
        });
        if (!drawer.getAttribute('data-sds-b37')) {
          drawer.setAttribute('data-sds-b37', '1');
          new MutationObserver(function () { onDrawer(drawer); }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
        }
      }
    });
  })();
  // ===== BUG 37 — END =====


  // ===== BUG 38 — START =====
  // Product · Medium · 2.4.3
  // Closing the cart drawer (Close button or Esc) after Add to cart loses focus.
  (function () {
    var st = { opener: null, fromAdd: false };
    SDS.bug(38, 'Product: focus returns to Add to cart when drawer closes', {
      init: function () {
        document.addEventListener('click', function (e) {
          if (!e.target.closest || !SDS.isTemplate('product')) return;
          var add = e.target.closest('.js-product-form [name="add"], .add-to-cart');
          if (add) { st.opener = add; st.fromAdd = true; }
          else if (e.target.closest('.cart-link')) { st.fromAdd = false; }
        }, true);
      },
      run: function () {
        if (!SDS.isTemplate('product')) return;
        var drawer = document.querySelector('cart-drawer');
        if (!drawer || drawer.getAttribute('data-sds-b38')) return;
        drawer.setAttribute('data-sds-b38', '1');
        new MutationObserver(function () {
          if (drawer.hasAttribute('open') || !st.fromAdd) return;
          setTimeout(function () {
            SDS.focusFirst([st.opener, '.js-product-form [name="add"]']);
            st.fromAdd = false;
          }, 450);
        }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
      }
    });
  })();
  // ===== BUG 38 — END =====


  // ===== BUG 39 — START =====
  // Product (cart drawer) · High · 4.1.2
  // Quantity − / + and Remove are <a href="/cart/change">; the drawer re-renders after
  // each change and focus is dropped to <body>.
  // Fix: button semantics + Space key; after the re-render, focus returns to the same
  //      control (or the drawer title if the line was removed) and the result is announced.
  (function () {
    var st = { id: null, kind: null, line: null, at: 0, timer: null };
    function restore(drawer) {
      if (!st.at || Date.now() - st.at > 6000) return;
      clearTimeout(st.timer);
      st.timer = setTimeout(function () {
        if (drawer.contains(document.activeElement) && document.activeElement.id === st.id) { st.at = 0; return; }
        var el = st.id && document.getElementById(st.id);
        var qty = st.line && document.getElementById('updates_' + st.line);
        if (el && SDS.visible(el) && st.kind !== 'remove') {
          el.focus();
          if (qty) SDS.announce('Quantity updated to ' + qty.value + '.');
        } else {
          SDS.focusFirst([drawer.querySelector('#CartDrawerModal-Title'), drawer.querySelector('.drawer__close-btn')]);
          SDS.announce(st.kind === 'remove' ? 'Item removed from your cart.' : 'Cart updated.');
        }
        st.at = 0;
      }, 250);
    }
    SDS.bug(39, 'Cart drawer quantity/remove controls: buttons + focus restore', {
      init: function () {
        var remember = function (e) {
          if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
          var c = e.target.closest && e.target.closest('cart-drawer .quantity-down, cart-drawer .quantity-up, cart-drawer .cart-item__remove');
          if (!c) return;
          st.kind = c.classList.contains('cart-item__remove') ? 'remove' : 'qty';
          var item = c.closest('.cart-item');
          var qtyInput = item && item.querySelector('input[id^="updates_"]');
          var m = (c.id || (qtyInput && qtyInput.id) || '').match(/_(\d+)$/);
          st.line = m ? m[1] : null;
          st.id = c.id || null;
          st.at = Date.now();
        };
        document.addEventListener('click', remember, true);
        document.addEventListener('keydown', remember, true);
      },
      run: function () {
        var drawer = document.querySelector('cart-drawer');
        if (!drawer) return;
        [].forEach.call(drawer.querySelectorAll('.quantity-down, .quantity-up, .cart-item__remove'), SDS.buttonize);
        if (!drawer.getAttribute('data-sds-b39')) {
          drawer.setAttribute('data-sds-b39', '1');
          new MutationObserver(function () { restore(drawer); }).observe(drawer, { childList: true, subtree: true });
        }
      }
    });
  })();
  // ===== BUG 39 — END =====


  // ===== BUG 40 — START =====
  // Product · High · 4.1.2
  // Emplifi (TurnTo) "Write a Review" opens a dialog without saying so.
  (function () {
    SDS.bug(40, 'Reviews: Write a Review declares its dialog', {
      run: function () {
        [].forEach.call(document.querySelectorAll('.tt-c-reviews-summary__write-review, .tt-container button'), function (b) {
          if (/write a review|ask a question/i.test(SDS.text(b)) && b.getAttribute('aria-haspopup') !== 'dialog') {
            b.setAttribute('aria-haspopup', 'dialog');
          }
        });
      }
    });
  })();
  // ===== BUG 40 — END =====


  // ===== BUG 41 — START =====
  // Product · Medium · 2.4.3, 2.1.2
  // Emplifi (TurnTo) review dialog: initial focus lands on a tabindex="-1" container,
  // Tab can leave the dialog and focus isn't returned when it closes.
  // Fix: name the dialog by its heading, focus the heading, normalise positive
  //      tabindex values, trap Tab, Esc closes, focus returns to "Write a Review".
  (function () {
    var st = { modal: null, opener: null, poll: null };
    function close() {
      var m = st.modal;
      st.modal = null;
      clearInterval(st.poll);
      SDS.releaseTrap(m);
      setTimeout(function () { SDS.focusFirst([st.opener, '.tt-c-reviews-summary__write-review']); }, 200);
    }
    SDS.bug(41, 'Reviews: dialog tab order, trap and focus return', {
      init: function () {
        document.addEventListener('click', function (e) {
          var b = e.target.closest && e.target.closest('.tt-container button, .tt-container a');
          if (b && !b.closest('.tt-o-modal')) st.opener = b;
        }, true);
      },
      run: function () {
        var m = [].filter.call(document.querySelectorAll('.tt-o-modal[role="dialog"]'), SDS.visible)[0];
        if (!m || m === st.modal) return;
        st.modal = m;
        [].forEach.call(m.querySelectorAll('[tabindex]'), function (el) {
          if (parseInt(el.getAttribute('tabindex'), 10) > 0) el.setAttribute('tabindex', '0');
        });
        var name = function () {   // TurnTo renders the heading after the dialog appears
          var h = m.querySelector('[role="heading"], h1, h2, h3');
          if (!h || m.getAttribute('aria-labelledby')) return !!m.getAttribute('aria-labelledby');
          m.setAttribute('aria-labelledby', SDS.id(h, 'sds-tt-title'));
          m.removeAttribute('aria-label');
          h.setAttribute('tabindex', '-1');
          return true;
        };
        var h = m.querySelector('[role="heading"], h1, h2, h3');
        name();
        var tries = 0;
        var namer = setInterval(function () {
          if (name() || ++tries > 20 || !document.documentElement.contains(m)) clearInterval(namer);
        }, 250);
        SDS.trap(m, {
          onEscape: function (e) {
            var c = m.querySelector('.tt-o-modal__close-btn');
            if (c) { e.preventDefault(); c.click(); }
          }
        });
        setTimeout(function () {
          var head = m.querySelector('[role="heading"], h1, h2, h3');
          SDS.focusFirst([head, h, m.querySelector('.tt-o-modal__close-btn')]);
        }, 300);
        st.poll = setInterval(function () {
          if (!document.documentElement.contains(m) || !SDS.visible(m)) close();
        }, 400);
      }
    });
  })();
  // ===== BUG 41 — END =====


  // ##########################################################################
  //  CONTENT PAGES (A): Artist Application → Career
  // ##########################################################################

  // ===== BUG 42 — START =====
  // /pages/artist-application · High · 1.3.1
  // No H1 (first heading is H2 "Let's Create magic together").
  // Fix: visually hidden H1 with the page name. (Prefer a visible H1 in the theme editor.)
  (function () {
    SDS.bug(42, 'Artist Application: add H1', {
      run: function () { if (SDS.isPage('artist-application')) SDS.addSrH1('Artist Application'); }
    });
  })();
  // ===== BUG 42 — END =====


  // ===== BUG 43 — START =====
  // /pages/artist-application · Medium · 3.2.2, 2.4.4
  // "Start Application" link opens a new tab without warning.
  // NOTE: on 15-Sep-2026 the page no longer contains this link (content now asks to
  //       email digital@…). The fix is kept so it applies if the button returns.
  (function () {
    SDS.bug(43, 'Artist Application: Start Application new-tab warning', {
      run: function () {
        if (!SDS.isPage('artist-application')) return;
        [].forEach.call(document.querySelectorAll('main a'), function (a) {
          if (/start application/i.test(SDS.text(a)) && (a.target === '_blank' || SDS.isExternal(a))) SDS.newTab(a);
        });
      }
    });
  })();
  // ===== BUG 43 — END =====


  // ===== BUG 44 — START =====
  // /pages/design-advice · High · 1.3.1
  // No H1; the visual page title "Design advice" is an H2.
  (function () {
    SDS.bug(44, 'Design Advice: "Design advice" becomes the H1', {
      run: function () { if (SDS.isPage('design-advice')) SDS.promoteFirstH2(/design advice/i) || SDS.addSrH1('Design Advice'); }
    });
  })();
  // ===== BUG 44 — END =====


  // ===== BUG 45 — START =====
  // /pages/designer-trade-program · High · 1.3.1
  // No H1; hero title "JOIN THE VIPEEP SHOW" is an H2.
  (function () {
    SDS.bug(45, 'Designer Trade Program: hero title becomes the H1', {
      run: function () { if (SDS.isPage('designer-trade-program')) SDS.promoteFirstH2(/vipeep/i) || SDS.addSrH1('Designer Trade Program'); }
    });
  })();
  // ===== BUG 45 — END =====


  // ===== BUG 46 — START =====
  // /pages/designer-trade-program · High · 4.1.2
  // Embedded application form <iframe> has no title.
  (function () {
    SDS.bug(46, 'Designer Trade Program: iframe title', {
      run: function () {
        if (!SDS.isPage('designer-trade-program')) return;
        [].forEach.call(document.querySelectorAll('main iframe'), function (f) {
          var t = (f.getAttribute('title') || '').trim();
          if (!t || /^(embedded content|iframe|frame)$/i.test(t)) f.setAttribute('title', 'Designer Trade Program application form');
        });
      }
    });
  })();
  // ===== BUG 46 — END =====


  // ===== BUG 47 — START =====
  // /pages/designer-trade-program · High · 1.1.1
  // Hero and gallery images have alt="".
  (function () {
    var ALT = {
      'SarahBrannonInteriors': 'Dining room by Sarah Brannon Interiors with a pale blue lacquered sideboard, white slipcovered chairs and a crystal chandelier',
      'Sarah-Vaile-Design-Homepage': 'Dining room by Sarah Vaile Design with a black-and-white botanical mural, burl pedestal table and white upholstered chairs',
      'Dan-Piassick-DLP_0979': 'Entry vignette with a light blue lacquered cabinet, a pair of white urn lamps and a gold mirror',
      'awelldressedhomeconsole': 'Living space with a framed gallery wall, green console table, leopard-print bench and blue sofa',
      'Homepage-Juliet-Headboard': 'Twin beds with scalloped green upholstered headboards against green toile wallpaper and a white nightstand'
    };
    SDS.bug(47, 'Designer Trade Program: image alt text', {
      run: function () { if (SDS.isPage('designer-trade-program')) SDS.altByFile(ALT, 'main'); }
    });
  })();
  // ===== BUG 47 — END =====


  // ===== BUG 48 — START =====
  // /pages/hospitality · High · 1.3.1
  // Two H1s: "Hospitality Services" and "Featured projects".
  (function () {
    SDS.bug(48, 'Hospitality: single H1', {
      run: function () {
        if (!SDS.isPage('hospitality')) return;
        var h1s = document.querySelectorAll('main h1');
        for (var i = 1; i < h1s.length; i++) SDS.retag(h1s[i], 'h2');
      }
    });
  })();
  // ===== BUG 48 — END =====


  // ===== BUG 49 — START =====
  // /pages/hospitality · Medium · 1.1.1
  // "US MANUFACTURING" image alt = "Hospitality-USA-Production.jpg__PID:…".
  (function () {
    var ALT = { 'Hospitality-USA-Production': 'Scout craftsman clamping the frame of a custom wood cabinet in the Dallas workshop' };
    SDS.bug(49, 'Hospitality: US MANUFACTURING image alt', {
      run: function () { if (SDS.isPage('hospitality')) SDS.altByFile(ALT, 'main'); }
    });
  })();
  // ===== BUG 49 — END =====


  // ===== BUG 50 — START =====
  // /pages/hospitality · High · 1.1.1
  // "See it to believe it" image has alt="".
  (function () {
    var ALT = { 'Hospitality-Mod-Hotels': 'Hotel guest room with a coral ceiling, white four-poster bed, black-and-white graphic wallpaper and a striped rug' };
    SDS.bug(50, 'Hospitality: See it to believe it image alt', {
      run: function () { if (SDS.isPage('hospitality')) SDS.altByFile(ALT, 'main'); }
    });
  })();
  // ===== BUG 50 — END =====


  // ===== BUG 51 — START =====
  // /pages/hospitality · Medium · 1.2.1/1.2.2/1.2.3, 2.2.2
  // "Local custom workshop" background video autoplays and loops with no controls.
  // Fix: Play/Pause button, paused for prefers-reduced-motion, text description.
  // The video is muted b-roll with no speech → captions are not required; the text
  // description below is the media alternative. If an audio track is ever added,
  // captions (.vtt) must be supplied in Shopify Files.
  (function () {
    SDS.bug(51, 'Hospitality: workshop video pause control + description', {
      run: function () {
        if (!SDS.isPage('hospitality')) return;
        [].forEach.call(document.querySelectorAll('main .section-background-video video'), function (v) {
          SDS.videoControls(v, {
            label: 'Local custom workshop video',
            decorative: true,
            description: 'Video description: Scout craftspeople cutting, assembling and finishing custom furniture pieces in the Dallas workshop.'
          });
        });
      }
    });
  })();
  // ===== BUG 51 — END =====


  // ===== BUG 52 — START =====
  // /pages/hospitality · High · 1.1.1
  // "Featured projects" gallery images: empty or file-name alt text.
  (function () {
    var ALT = {
      'Birdies_KathyTran': 'Restaurant dining room with a turquoise shiplap wall, framed cowboy sketches, copper pendant lights and orange leather booths',
      'Virgin_Hotel_x_Scout': 'Hotel lounge nook with blue upholstered chairs, leopard-print cushions and a swimming-pool mural above wood paneling',
      'Hospitality-Virgin-Hotels': 'Outdoor hotel terrace with a long wood communal table and black wire bar stools',
      'Limelight-Catering': 'Event dining table set with tall candles and white ghost chairs on a patterned rug',
      'Hospitality-Athenian': 'Hotel guest room with a green upholstered stepped headboard, white bedding and a leather bench',
      'Hospitality-Dallas-Mavs': 'Custom wood tabletop engraved with the Dallas Mavericks logo'
    };
    SDS.bug(52, 'Hospitality: Featured projects image alt', {
      run: function () { if (SDS.isPage('hospitality')) SDS.altByFile(ALT, 'main'); }
    });
  })();
  // ===== BUG 52 — END =====


  // ===== BUG 53 — START =====
  // /pages/hospitality · High · 1.1.1
  // "Delivered with Care" section + workshop gallery below it: missing/file-name alts.
  (function () {
    var ALT = {
      'Hospitality-WhiteGlove-TextWit': 'Scout delivery truck with an illustrated furniture wrap parked at a home while a team member carries in framed art',
      'Hospitality-paint': 'Scout craftsman finishing a burl wood cabinet in the workshop',
      'Hospitality-cnc': 'CNC router cutting furniture panels in the Scout workshop',
      'CUSTOM_STORY2': 'Smiling Scout upholsterer at a work table covered in pink and green printed fabric'
    };
    SDS.bug(53, 'Hospitality: Delivered with Care image alt', {
      run: function () { if (SDS.isPage('hospitality')) SDS.altByFile(ALT, 'main'); }
    });
  })();
  // ===== BUG 53 — END =====


  // ===== BUG 54 — START =====
  // /pages/lacquer-stain-care-guide · High · 1.3.1
  // Page starts at H3 and jumps to H5 (H5s are sign-off lines, not headings).
  (function () {
    SDS.bug(54, 'Lacquer & Stain Care Guide: heading hierarchy', {
      run: function () {
        if (!SDS.isPage('lacquer-stain-care-guide')) return;
        SDS.addSrH1('Lacquer & Stain Care Guide');
        [].forEach.call(document.querySelectorAll('main .rte h3, main .rte h4'), function (h) { SDS.retag(h, 'h2'); });
        [].forEach.call(document.querySelectorAll('main .rte h5, main .rte h6'), function (h) { SDS.retag(h, 'p'); });
      }
    });
  })();
  // ===== BUG 54 — END =====


  // ===== BUG 55 — START =====
  // /pages/promotion-policy · High · 1.3.1
  // No headings at all; section labels are bold <div><strong>…</strong></div> lines.
  (function () {
    SDS.bug(55, 'Promotion Policy: H1 + section headings', {
      run: function () {
        if (!SDS.isPage('promotion-policy')) return;
        SDS.addSrH1('Promotion Policy');
        [].forEach.call(document.querySelectorAll('main .rte div, main .rte p'), function (el) {
          var kids = [].filter.call(el.childNodes, function (n) { return n.nodeType === 1 || n.nodeValue.trim(); });
          if (kids.length === 1 && kids[0].nodeType === 1 && kids[0].tagName === 'STRONG' && SDS.text(el).length < 80) {
            var h = SDS.retag(el, 'h2');
            h.classList.add('sds-inline-heading');
          }
        });
      }
    });
  })();
  // ===== BUG 55 — END =====


  // ===== BUG 56 — START =====
  // /pages/promotion-policy · Medium · 3.2.2
  // "Subscribe to our VIP list" (attn.tv) opens a new tab without warning.
  (function () {
    SDS.bug(56, 'Promotion Policy: VIP link new-tab warning', {
      run: function () {
        if (!SDS.isPage('promotion-policy')) return;
        [].forEach.call(document.querySelectorAll('main a'), function (a) {
          if (/vip list/i.test(SDS.text(a)) && !a.getAttribute('data-sds-b57')) SDS.newTab(a);
        });
      }
    });
  })();
  // ===== BUG 56 — END =====


  // ===== BUG 57 — START =====
  // /pages/promotion-policy · High · 3.2.4
  // "Subscribe to our VIP list" → https://scoutdesignstudio.attn.tv/p/TvK/landing-page
  // redirects to the homepage (Attentive landing page no longer live).
  // Content fix (preferred): edit the link in Online Store › Pages › Promotion Policy.
  // Code fix: if VIP_URL is set, the link is pointed there; otherwise the link opens
  //           the Klaviyo "10% off" sign-up form in place (button semantics, no new tab).
  (function () {
    var VIP_URL = '';                 // ← paste the correct sign-up URL here if one exists
    var KLAVIYO_FORM_ID = 'WS9dTL';   // Klaviyo "GET 10% OFF" popup form on this store
    SDS.bug(57, 'Promotion Policy: VIP link goes to a working destination', {
      run: function () {
        if (!SDS.isPage('promotion-policy')) return;
        [].forEach.call(document.querySelectorAll('main a'), function (a) {
          if (!/vip list/i.test(SDS.text(a)) || a.getAttribute('data-sds-b57')) return;
          a.setAttribute('data-sds-b57', '1');
          if (VIP_URL) { a.setAttribute('href', VIP_URL); return; }
          [].forEach.call(a.querySelectorAll('.sds-newtab'), function (n) { n.remove(); });
          a.removeAttribute('target');
          a.setAttribute('href', '#');
          a.setAttribute('aria-haspopup', 'dialog');
          SDS.buttonize(a);
          a.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();     // prevent theme's external-link handler
            window._klOnsite = window._klOnsite || [];
            window._klOnsite.push(['openForm', KLAVIYO_FORM_ID]);
          }, true);
        });
      }
    });
  })();
  // ===== BUG 57 — END =====


  // ===== BUG 58 — START =====
  // /pages/sample-swatches · High · 1.3.1
  // No H1; "Shop finish & fabric samples" is an H2.
  (function () {
    SDS.bug(58, 'Sample Swatches: page title becomes the H1', {
      run: function () { if (SDS.isPage('sample-swatches')) SDS.promoteFirstH2(/finish|samples/i) || SDS.addSrH1('Sample Swatches'); }
    });
  })();
  // ===== BUG 58 — END =====


  // ===== BUG 59 — START =====
  // /pages/sample-swatches · High · 1.1.1, 2.4.4
  // Tiles (Grasscloth, Fabric, Wood, Lacquer): image alt="" and the link is only named
  // by the overlay word, so the purpose ("shop samples") is unclear.
  // Fix: link named "Shop <name> samples" (visible word kept → 2.5.3); if a tile link
  //      ever contains only the image, the image carries that name as alt text.
  (function () {
    SDS.bug(59, 'Sample Swatches: linked image names', {
      run: function () {
        if (!SDS.isPage('sample-swatches')) return;
        [].forEach.call(document.querySelectorAll('main .gallery__item'), function (item) {
          var name = SDS.text(item.querySelector('.text-overlay__title'));
          var link = item.querySelector('a');
          var img = link && link.querySelector('img');
          if (!name || !link) return;
          var label = 'Shop ' + name + ' samples';
          if (SDS.text(link)) {
            if (link.getAttribute('aria-label') !== label) link.setAttribute('aria-label', label);
            if (img && img.getAttribute('alt') !== '') img.setAttribute('alt', '');
          } else if (img && img.getAttribute('alt') !== label) {
            img.setAttribute('alt', label);
          }
        });
      }
    });
  })();
  // ===== BUG 59 — END =====


  // ===== BUG 60 — START =====
  // /pages/about · High · 1.3.1
  // No H1 (page starts with H2 "Bienvenidos, y'all.").
  (function () {
    SDS.bug(60, 'About: add H1', {
      run: function () { if (SDS.isPage('about')) SDS.addSrH1('About Scout Design Studio'); }
    });
  })();
  // ===== BUG 60 — END =====


  // ===== BUG 61 — START =====
  // /pages/about · Low · 2.2.2
  // "Bienvenidos, y'all." video autoplays and loops forever with no controls.
  (function () {
    SDS.bug(61, 'About: video pause control', {
      run: function () {
        if (!SDS.isPage('about')) return;
        [].forEach.call(document.querySelectorAll('main .section-video-with-text video, main .section-background-video video'), function (v) {
          SDS.videoControls(v, {
            label: 'Scout Design Studio video',
            decorative: true,
            // CONFIRM wording with the client after watching the clip:
            description: 'Video description: a Scout Design Studio sign on a busy New York City street.',
            stopAfterSeconds: 0   // set to 5 to stop automatically after 5 seconds
          });
        });
      }
    });
  })();
  // ===== BUG 61 — END =====


  // ===== BUG 62 — START =====
  // /pages/about · High · 1.1.1
  // Images in several sections have alt="".
  (function () {
    var ALT = {
      'who_we_are': 'Corkboard collage of Scout team photos, a Scout delivery truck and playful cut-out illustrations',
      'COLORS_2500_x_3500_px_1': 'Watercolor illustration of a mint green striped tiger',
      'About-Us-What-We-Do2': 'Scout Design Studio box truck wrapped with a styled room photo, parked on a city street',
      'About-Us-Our-Story-3': 'Scout team in dog and horse masks lounging around a red leather Chesterfield sofa in the studio',
      'About-Us-What-We-Do-2': 'Magenta velvet lounge chair beside a black mannequin floor lamp and framed denim artwork',
      'About-Us-DogMask': 'Person wearing a dog mask reading a book in bed against a pale blue headboard',
      'About-Us-DogPicture': 'Person in a black dog mask seated next to a brown dog in front of a burl credenza',
      'About-Us.jpg': 'Banana-print throw pillow tossed into a bright blue sky',
      'About-Us-WNFENAY-Mobile': 'Three people in bird, giraffe and lion masks playing cards at a burl wood table',
      'About-Us-Delivered-With-Care-M': 'Scout delivery truck parked at a home while a team member carries in framed art'
    };
    SDS.bug(62, 'About: image alt text', {
      run: function () { if (SDS.isPage('about')) SDS.altByFile(ALT, 'main'); }
    });
  })();
  // ===== BUG 62 — END =====


  // ===== BUG 63 — START =====
  // /pages/career-page · High · 1.3.1
  // Page starts with H2 "Wanna join the pack?"; "Benefits & Perks" is the only H1 and
  // the benefit titles are H2 siblings of it.
  // Result: H1 Wanna join the pack? › H2 Benefits & Perks › H3 Medical… / Paid Time Off /
  //         Retirement / Employee Discount › H2 Equal opportunity › H2 E-Verify
  (function () {
    var BENEFITS = /medical|paid time off|retirement|employee discount/i;
    SDS.bug(63, 'Career: heading hierarchy', {
      run: function () {
        if (!SDS.isPage('career-page')) return;
        var main = document.querySelector('main');
        [].forEach.call(main.querySelectorAll('h1'), function (h) {
          if (!/wanna join/i.test(h.textContent)) SDS.retag(h, 'h2');
        });
        [].forEach.call(main.querySelectorAll('h2'), function (h) {
          if (/wanna join/i.test(h.textContent)) SDS.retag(h, 'h1');
          else if (BENEFITS.test(h.textContent)) SDS.retag(h, 'h3');
        });
      }
    });
  })();
  // ===== BUG 63 — END =====


  // ===== BUG 64 — START =====
  // /pages/career-page · Medium · 3.2.2
  // "Search open positions" (external ATS) opens a new tab without warning.
  (function () {
    SDS.bug(64, 'Career: Search open positions new-tab warning', {
      run: function () {
        if (!SDS.isPage('career-page')) return;
        [].forEach.call(document.querySelectorAll('main a'), function (a) {
          if (/search open positions/i.test(SDS.text(a))) SDS.newTab(a);
        });
      }
    });
  })();
  // ===== BUG 64 — END =====


  // ===== BUG 65 — START =====
  // /pages/career-page · High · 1.1.1
  // Hero, gallery and benefit images have alt="".
  (function () {
    var ALT = {
      'lobster-careers-page': 'Scout team in animal masks seated at a formal dining table while one pours champagne',
      'JointheScoutpack4': 'Three Scout team members in bird, giraffe and lion masks playing cards',
      'JointheScoutpack1': 'Team member in a tiger mask lounging on a cream sofa above a leopard-print rug',
      'JointheScoutpack5': 'Person in a tiger mask draped over a green topiary bear sculpture in a bedroom',
      'JointheScoutpack2': 'Person in a horse mask beside a blue chair while another lies on the rug in a pink tracksuit',
      'Careers-Medical-Dental-Vision': 'Two team members in bird and horse masks relaxing in a bright living room',
      'Careers-Paid-Time-Off': 'Child in a blue bird mask running across a pastel striped rug',
      'Careers-Employee-Discount': 'Team member in a pigeon mask sitting on a wood credenza beside colorful glass bottles',
      'Careers-Retirement': 'Two people in dog and giraffe masks at a dining table, one sipping tea and one taking a selfie',
      'pillows.01': 'Three illustrated throw pillows on a green velvet sofa in front of colorful artwork'
    };
    SDS.bug(65, 'Career: image alt text', {
      run: function () { if (SDS.isPage('career-page')) SDS.altByFile(ALT, 'main'); }
    });
  })();
  // ===== BUG 65 — END =====


  // ===== BUG 66 — START =====
  // /pages/career-page · Low · 2.4.4, 1.3.1
  // "Know Your Rights" opens a PDF in a new tab without warning.
  // PDF note: the file is hosted by the EEOC (eeoc.gov) and cannot be remediated from
  // Shopify; the linked file is already the EEOC "screen reader" version. If the client
  // prefers, link to the HTML version: https://www.eeoc.gov/poster
  (function () {
    SDS.bug(66, 'Career: Know Your Rights PDF warning', {
      run: function () {
        if (!SDS.isPage('career-page')) return;
        [].forEach.call(document.querySelectorAll('main a[href*="knowyourrights" i]'), function (a) {
          SDS.newTab(a, 'PDF, opens in a new tab');
        });
      }
    });
  })();
  // ===== BUG 66 — END =====


  // ===== BUG 67 — START =====
  // /pages/career-page · Low · 2.4.4, 1.3.1
  // "E-Verify Participation" and "Right to Work" PDFs open without warning.
  // PDF note: federal posters hosted on e-verify.gov — not editable from Shopify.
  // Accessible alternatives are listed at https://www.e-verify.gov/posters
  (function () {
    SDS.bug(67, 'Career: E-Verify / Right to Work PDF warnings', {
      run: function () {
        if (!SDS.isPage('career-page')) return;
        [].forEach.call(document.querySelectorAll('main a'), function (a) {
          var href = a.getAttribute('href') || '';
          var flat = href.replace(/%20|[%\-_ ]/g, '');
          if (/everify|righttowork/i.test(flat) && /\.pdf|poster/i.test(href)) SDS.newTab(a, 'PDF, opens in a new tab');
        });
      }
    });
  })();
  // ===== BUG 67 — END =====


  // ##########################################################################
  //  CONTENT PAGES (B): Contact → California Prop 65
  // ##########################################################################

  // ===== BUG 68 — START =====
  // /pages/contact · High · 1.3.1
  // Order today: H2 Contact us › H3 cards › H1 "WE'RE A SMALL BUSINESS…" › H2 Showroom/Warehouse.
  // Result:      H1 Contact us › H2 cards › H2 We're a small business… › H2 Our Showroom › H2 Our Warehouse
  (function () {
    SDS.bug(68, 'Contact: heading hierarchy', {
      run: function () {
        if (!SDS.isPage('contact')) return;
        var main = document.querySelector('main');
        [].forEach.call(main.querySelectorAll('h1'), function (h) {
          if (!/^\s*contact us\s*$/i.test(h.textContent)) SDS.retag(h, 'h2');
        });
        [].forEach.call(main.querySelectorAll('h2'), function (h) {
          if (/^\s*contact us\s*$/i.test(h.textContent) && !main.querySelector('h1')) SDS.retag(h, 'h1');
        });
        [].forEach.call(main.querySelectorAll('.text-column__title'), function (h) {
          if (h.tagName === 'H3') SDS.retag(h, 'h2');
        });
      }
    });
  })();
  // ===== BUG 68 — END =====


  // ===== BUG 69 — START =====
  // /pages/contact · High · 1.1.1, 2.4.4
  // Illustration links (mailto:) contain only an image with alt="" → empty links.
  (function () {
    var PURPOSE = {
      'sales@': 'Email our sales team',
      'delivery@': 'Email our delivery team',
      'customerservice@': 'Email customer service',
      'hello@': 'Email Scout Design Studio to collaborate'
    };
    SDS.bug(69, 'Contact: names for image-only email links', {
      run: function () {
        if (!SDS.isPage('contact')) return;
        [].forEach.call(document.querySelectorAll('main a[href^="mailto:"]'), function (a) {
          var img = a.querySelector('img');
          if (!img || SDS.text(a)) return;
          var href = a.getAttribute('href').toLowerCase();
          for (var key in PURPOSE) {
            if (href.indexOf(key) > -1) { if (img.getAttribute('alt') !== PURPOSE[key]) img.setAttribute('alt', PURPOSE[key]); break; }
          }
        });
      }
    });
  })();
  // ===== BUG 69 — END =====


  // ===== BUG 70 — START =====
  // /pages/contact · Medium · 2.4.4
  // Email text links are just the addresses; the purpose is only in the card heading.
  (function () {
    var PURPOSE = {
      'sales@': 'buying something',
      'delivery@': 'delivery updates',
      'customerservice@': 'order issues',
      'hello@': 'collaborations and general questions'
    };
    SDS.bug(70, 'Contact: descriptive email link names', {
      run: function () {
        if (!SDS.isPage('contact')) return;
        [].forEach.call(document.querySelectorAll('main a[href^="mailto:"]'), function (a) {
          var address = SDS.text(a);
          if (!address || a.querySelector('img')) return;
          var href = a.getAttribute('href').toLowerCase();
          for (var key in PURPOSE) {
            if (href.indexOf(key) > -1) {
              // visible address stays at the end of the name (2.5.3 Label in Name)
              var label = 'Email for ' + PURPOSE[key] + ': ' + address;
              if (a.getAttribute('aria-label') !== label) a.setAttribute('aria-label', label);
              break;
            }
          }
        });
      }
    });
  })();
  // ===== BUG 70 — END =====


  // ===== BUG 71 — START =====
  // /pages/contact · Medium · 2.4.4
  // Showroom phone "214-741-2414" is plain text (can't tap to call).
  (function () {
    SDS.bug(71, 'Contact: showroom phone as tel: link', {
      run: function () {
        if (!SDS.isPage('contact')) return;
        SDS.linkifyText(document.querySelector('main'), /214[-.\s]?741[-.\s]?2414/g, function (num) {
          var a = document.createElement('a');
          a.href = 'tel:+12147412414';
          a.textContent = num;
          a.setAttribute('aria-label', 'Call our showroom at ' + num);
          return a;
        });
      }
    });
  })();
  // ===== BUG 71 — END =====


  // ===== BUG 72 — START =====
  // /pages/contact · High · 2.4.4, 3.2.5
  // Showroom "View Map" (Google Maps) opens a new tab; both map links are named "View Map".
  (function () {
    SDS.bug(72, 'Contact: showroom map link name + new-tab warning', {
      run: function () {
        if (!SDS.isPage('contact')) return;
        [].forEach.call(document.querySelectorAll('main .feature-text-paired'), function (box) {
          if (!/showroom/i.test(SDS.text(box.querySelector('h1, h2, h3')))) return;
          [].forEach.call(box.querySelectorAll('a'), function (a) {
            if (!/view map/i.test(SDS.text(a)) || a.getAttribute('data-sds-newtab')) return;
            a.setAttribute('aria-label', 'View map for our showroom');
            SDS.newTab(a);
          });
        });
      }
    });
  })();
  // ===== BUG 72 — END =====


  // ===== BUG 73 — START =====
  // /pages/contact · Medium · 2.4.4
  // Warehouse phone "214-217-4060" is plain text.
  (function () {
    SDS.bug(73, 'Contact: warehouse phone as tel: link', {
      run: function () {
        if (!SDS.isPage('contact')) return;
        SDS.linkifyText(document.querySelector('main'), /214[-.\s]?217[-.\s]?4060/g, function (num) {
          var a = document.createElement('a');
          a.href = 'tel:+12142174060';
          a.textContent = num;
          a.setAttribute('aria-label', 'Call our warehouse at ' + num);
          return a;
        });
      }
    });
  })();
  // ===== BUG 73 — END =====


  // ===== BUG 74 — START =====
  // /pages/contact · High · 2.4.4, 3.2.5
  // Warehouse "View Map" opens a new tab and duplicates the showroom link name.
  (function () {
    SDS.bug(74, 'Contact: warehouse map link name + new-tab warning', {
      run: function () {
        if (!SDS.isPage('contact')) return;
        [].forEach.call(document.querySelectorAll('main .feature-text-paired'), function (box) {
          if (!/warehouse/i.test(SDS.text(box.querySelector('h1, h2, h3')))) return;
          [].forEach.call(box.querySelectorAll('a'), function (a) {
            if (!/view map/i.test(SDS.text(a)) || a.getAttribute('data-sds-newtab')) return;
            a.setAttribute('aria-label', 'View map for our warehouse');
            SDS.newTab(a);
          });
        });
      }
    });
  })();
  // ===== BUG 74 — END =====


  // ===== BUG 75 — START =====
  // /pages/shipping · High · 1.3.1
  // Starts at H3 (SHIPPING, RETURNS, LOCAL PICK-UP, UPHOLSTERY); no H1/H2.
  (function () {
    SDS.bug(75, 'Shipping: heading hierarchy', {
      run: function () {
        if (!SDS.isPage('shipping')) return;
        SDS.addSrH1('Shipping & Returns');
        [].forEach.call(document.querySelectorAll('main .rte h3'), function (h) { SDS.retag(h, 'h2'); });
      }
    });
  })();
  // ===== BUG 75 — END =====


  // ===== BUG 76 — START =====
  // /policies/terms-of-service · High · 1.3.1
  // H1 "Terms of service" jumps straight to H3 "TERMS OF SALE".
  (function () {
    SDS.bug(76, 'Terms of Service: no skipped heading level', {
      run: function () {
        if (SDS.path() !== '/policies/terms-of-service') return;
        var main = document.querySelector('main') || document.body;
        if (main.querySelector('h2')) return;
        [].forEach.call(main.querySelectorAll('h3'), function (h) { SDS.retag(h, 'h2'); });
        [].forEach.call(main.querySelectorAll('h4'), function (h) { SDS.retag(h, 'h3'); });
      }
    });
  })();
  // ===== BUG 76 — END =====


  // ===== BUG 77 — START =====
  // /pages/privacy-policy · High · 1.3.1
  // Starts at H3; no H1/H2.
  (function () {
    SDS.bug(77, 'Privacy Policy: heading hierarchy', {
      run: function () {
        if (!SDS.isPage('privacy-policy')) return;
        SDS.addSrH1('Privacy Policy');
        [].forEach.call(document.querySelectorAll('main .rte h3'), function (h) { SDS.retag(h, 'h2'); });
        [].forEach.call(document.querySelectorAll('main .rte h4'), function (h) { SDS.retag(h, 'h3'); });
      }
    });
  })();
  // ===== BUG 77 — END =====


  // ===== BUG 78 — START =====
  // /pages/privacy-policy · Medium · 2.4.4
  // hello@scoutdesignstudio.com appears as plain text.
  (function () {
    SDS.bug(78, 'Privacy Policy: email as mailto link', {
      run: function () {
        if (!SDS.isPage('privacy-policy')) return;
        SDS.linkifyText(document.querySelector('main .rte') || document.querySelector('main'), /[A-Za-z0-9._%+-]+@scoutdesignstudio\.com/g, function (email) {
          var a = document.createElement('a');
          a.href = 'mailto:' + email;
          a.textContent = email;
          a.setAttribute('aria-label', 'Email privacy questions to ' + email);
          return a;
        });
      }
    });
  })();
  // ===== BUG 78 — END =====


  // ===== BUG 79 — START =====
  // /pages/hospitality-warranty · High · 1.3.1
  // Seven H1s (every section title). Keep the first, demote the rest to H2.
  (function () {
    SDS.bug(79, 'Hospitality Warranty: single H1', {
      run: function () {
        if (!SDS.isPage('hospitality-warranty')) return;
        var h1s = document.querySelectorAll('main h1');
        for (var i = 1; i < h1s.length; i++) SDS.retag(h1s[i], 'h2');
      }
    });
  })();
  // ===== BUG 79 — END =====


  // ===== BUG 80 — START =====
  // /pages/hospitality-warranty · Medium · 2.4.4
  // customerservice@scoutdesignstudio.com is plain text.
  (function () {
    SDS.bug(80, 'Hospitality Warranty: email as mailto link', {
      run: function () {
        if (!SDS.isPage('hospitality-warranty')) return;
        SDS.linkifyText(document.querySelector('main .rte') || document.querySelector('main'), /[A-Za-z0-9._%+-]+@scoutdesignstudio\.com/g, function (email) {
          var a = document.createElement('a');
          a.href = 'mailto:' + email;
          a.textContent = email;
          a.setAttribute('aria-label', 'Email warranty support at ' + email);
          return a;
        });
      }
    });
  })();
  // ===== BUG 80 — END =====


  // ===== BUG 81 — START =====
  // /pages/data-sharing-opt-out · High · 1.3.1
  // No headings on the page.
  (function () {
    SDS.bug(81, 'Data Sharing Opt-Out: add H1', {
      run: function () { if (SDS.isPage('data-sharing-opt-out')) SDS.addSrH1('Data Sharing Opt-Out'); }
    });
  })();
  // ===== BUG 81 — END =====


  // ===== BUG 82 — START =====
  // /pages/influencer-program · High · 1.3.1
  // First heading is H2 "join our creator collective"; no H1.
  (function () {
    SDS.bug(82, 'Influencer Program: hero title becomes the H1', {
      run: function () { if (SDS.isPage('influencer-program')) SDS.promoteFirstH2(/creator collective/i) || SDS.addSrH1('Influencer Program'); }
    });
  })();
  // ===== BUG 82 — END =====


  // ===== BUG 83 — START =====
  // /pages/influencer-program · Medium · 3.2.5
  // "Apply Now" (Typeform) opens a new tab without warning.
  (function () {
    SDS.bug(83, 'Influencer Program: Apply Now new-tab warning', {
      run: function () {
        if (!SDS.isPage('influencer-program')) return;
        [].forEach.call(document.querySelectorAll('main a'), function (a) {
          if (!/apply now/i.test(SDS.text(a)) || a.getAttribute('data-sds-newtab')) return;
          a.setAttribute('aria-label', 'Apply now to join our creator collective');
          SDS.newTab(a);
        });
      }
    });
  })();
  // ===== BUG 83 — END =====


  // ===== BUG 84 — START =====
  // /pages/influencer-program · High · 1.1.1
  // "From Our Partners" images have alt="".
  (function () {
    var ALT = {
      'Herring-Chair': 'Partner photo: pair of gray upholstered Herring chairs with palm-print pillows in a sunlit sitting room',
      'GracieHunt': 'Partner photo: cream coffee table styled with a Tom Ford book, candle and white orchids in front of a blue lacquered cabinet',
      'katiepeoples': 'Partner photo: light blue dresser beneath a scalloped wood mirror against polka-dot wallpaper'
    };
    SDS.bug(84, 'Influencer Program: From Our Partners image alt', {
      run: function () { if (SDS.isPage('influencer-program')) SDS.altByFile(ALT, 'main'); }
    });
  })();
  // ===== BUG 84 — END =====


  // ===== BUG 85 — START =====
  // /pages/white-glove-delivery · High · 1.3.1
  // First heading is H2 "White Glove Delivery"; no H1.
  (function () {
    SDS.bug(85, 'White Glove Delivery: page title becomes the H1', {
      run: function () { if (SDS.isPage('white-glove-delivery')) SDS.promoteFirstH2(/white glove/i) || SDS.addSrH1('White Glove Delivery'); }
    });
  })();
  // ===== BUG 85 — END =====


  // ===== BUG 86 — START =====
  // /pages/california-proposition-65-warning · High · 1.3.1
  // Starts at H3; no H1/H2.
  (function () {
    SDS.bug(86, 'California Prop 65: heading hierarchy', {
      run: function () {
        if (!SDS.isPage('california-proposition-65-warning')) return;
        SDS.addSrH1('California Proposition 65 Warning');
        [].forEach.call(document.querySelectorAll('main .rte h3'), function (h) { SDS.retag(h, 'h2'); });
      }
    });
  })();
  // ===== BUG 86 — END =====


  // ===== BUG 87 — START =====
  // /pages/california-proposition-65-warning · Medium · 3.2.5
  // www.P65Warnings.ca.gov links (and "Learn more…", target="_blank") open a new tab without warning.
  (function () {
    SDS.bug(87, 'California Prop 65: new-tab warnings', {
      run: function () {
        if (!SDS.isPage('california-proposition-65-warning')) return;
        [].forEach.call(document.querySelectorAll('main a'), function (a) {
          if (/p65warnings\.ca\.gov/i.test(a.getAttribute('href') || '') || a.getAttribute('target') === '_blank') SDS.newTab(a);
        });
      }
    });
  })();
  // ===== BUG 87 — END =====



  // ##########################################################################
  //  NEW FINDINGS — logged during the 24-Sep-2026 retest (Bugs 88–96)
  // ##########################################################################

  // ===== BUG 88 — START =====
  // Global : Footer · High · 1.4.3 Contrast (Minimum)
  // Footer menu links and the copyright line are #6d7175 on #f1efe2 = 4.25:1 (needs 4.5:1).
  // 17 elements on every page. Preferred fix: change the footer text colour in the theme
  // colour scheme; this CSS is the interim override (#5f6367 on #f1efe2 = 5.24:1).
  (function () {
    SDS.bug(88, 'Footer link + copyright contrast raised to 4.5:1', {
      // NOTE: the theme animates footer link colour, and that transition keeps the computed
      // colour pinned to the old value, so the override also has to stop the transition and
      // set -webkit-text-fill-color (which is what actually paints the text).
      css:
        '#pagefooter .section-footer__menu-block__menu a,#pagefooter .section-footer__text,' +
        '#pagefooter .section-footer__text a,#pagefooter .copyright,#pagefooter .copyright a,' +
        '#pagefooter .copy,#pagefooter .copy a,#pagefooter .cart-policies,#pagefooter .cart-policies a,' +
        '#pagefooter .section-footer__row-lower a' +
        '{color:#5f6367!important;-webkit-text-fill-color:#5f6367!important;transition:none!important}'
    });
  })();
  // ===== BUG 88 — END =====


  // ===== BUG 89 — START =====
  // Homepage · High · 1.1.1, 2.4.4, 4.1.2
  // The image-only links in the multi-column sections ("ARTFULLY YOURS", "In-House
  // Delivery", "Come see us irl", …) have no accessible name: <a><img alt=""></a>.
  // Fix: name each link from the button or heading text of its own column.
  (function () {
    SDS.bug(89, 'Homepage: accessible names for image-only column links', {
      run: function () {
        if (!SDS.isTemplate('index')) return;
        [].forEach.call(document.querySelectorAll('.text-column, .column'), function (col) {
          var link = col.querySelector('.text-column__image a, .image-overlap a');
          if (!link || SDS.text(link)) return;
          var name = SDS.text(col.querySelector('.text-column__button, .btn, .text-column__title, h2, h3'));
          if (!name) return;
          var img = link.querySelector('img');
          if (img && img.getAttribute('alt')) return;             // a real alt already names it
          if (link.getAttribute('aria-label') !== name) link.setAttribute('aria-label', name);
          if (img && img.getAttribute('alt') !== '') img.setAttribute('alt', '');
        });
      }
    });
  })();
  // ===== BUG 89 — END =====


  // ===== BUG 90 — START =====
  // Homepage + Collection · High · 2.1.1, 2.4.7
  // "Quick buy" is visibility:hidden until the card is hovered, so keyboard users can
  // neither see nor reach it. Fixed by the CSS in Bugs 13 and 27 (kept there so the fix
  // travels with the component). This block only re-states it for other templates
  // (search results, featured product rows) where the same card is used.
  (function () {
    SDS.bug(90, 'Quick buy reachable by keyboard on every template', {
      css:
        'product-block:focus-within .btn.quickbuy-toggle,product-block .btn.quickbuy-toggle:focus' +
        '{visibility:visible!important;opacity:1!important;transition:none!important}' +
        'product-block .image-cont:focus-within .image-page-button,product-block .image-page-button:focus-visible' +
        '{opacity:1!important;visibility:visible!important;pointer-events:auto!important;transition:none!important}'
    });
  })();
  // ===== BUG 90 — END =====


  // ===== BUG 91 — START =====
  // /pages/contact · Medium · 1.1.1
  // The warehouse photo carries a file name as alt text
  // ("About-Us-Woodall-Desktop.jpg__PID:66d96e3a-…").
  (function () {
    var ALT = {
      'About-Us-Woodall-Desktop': 'Scout warehouse exterior in Dallas with a large black sign painted with the studio manifesto',
      'About-Us-Storefront': 'Scout Design Studio showroom exterior: teal building with pink striped awnings and an elephant sculpture'
    };
    SDS.bug(91, 'Contact: replace file-name alt text', {
      run: function () { if (SDS.isPage('contact')) SDS.altByFile(ALT, 'main'); }
    });
  })();
  // ===== BUG 91 — END =====


  // ===== BUG 92 — START =====
  // /pages/california-proposition-65-warning · Medium · 1.1.1
  // Nine warning icons have no alt attribute at all, so screen readers announce the file
  // name. The word "WARNING:" follows each icon in text, so the icons are decorative.
  (function () {
    SDS.bug(92, 'Prop 65: warning icons marked decorative', {
      run: function () {
        if (!SDS.isPage('california-proposition-65-warning')) return;
        [].forEach.call(document.querySelectorAll('main img'), function (img) {
          if (/warning_\d+x\d+|warning\.svg/i.test(img.getAttribute('src') || '') && img.getAttribute('alt') === null) {
            img.setAttribute('alt', '');
          }
        });
      }
    });
  })();
  // ===== BUG 92 — END =====


  // ===== BUG 93 — START =====
  // /pages/artist-application, /pages/design-advice · Medium · 2.4.4
  // Email addresses (digital@ and sales@scoutdesignstudio.com) are plain text, so they
  // can't be activated and aren't announced as links.
  (function () {
    var LABEL = {
      'artist-application': 'Email your artist portfolio to ',
      'design-advice': 'Email our design team at '
    };
    SDS.bug(93, 'Artist Application / Design Advice: emails as mailto links', {
      run: function () {
        var page = SDS.path().replace('/pages/', '');
        if (!LABEL[page]) return;
        SDS.linkifyText(document.querySelector('main'), /[A-Za-z0-9._%+-]+@scoutdesignstudio\.com/g, function (email) {
          var a = document.createElement('a');
          a.href = 'mailto:' + email;
          a.textContent = email;
          a.setAttribute('aria-label', LABEL[page] + email);
          return a;
        });
      }
    });
  })();
  // ===== BUG 93 — END =====


  // ===== BUG 94 — START =====
  // Global (Klaviyo) · Low · 2.5.8 Target Size (Minimum)
  // The "Close teaser" button on the GET 10% OFF tab is 20x20 CSS px (needs 24x24).
  (function () {
    SDS.bug(94, 'Klaviyo teaser close button meets 24x24 target size', {
      css: 'button.klaviyo-close-form{min-width:24px!important;min-height:24px!important}'
    });
  })();
  // ===== BUG 94 — END =====


  // ===== BUG 95 — START =====
  // /pages/data-sharing-opt-out · Low · 4.1.2, 2.4.1
  // The hCaptcha iframe inside Shopify's privacy opt-out form has no title.
  (function () {
    SDS.bug(95, 'Opt-out form: title on the hCaptcha iframe', {
      run: function () {
        [].forEach.call(document.querySelectorAll('iframe[src*="hcaptcha"], iframe[src*="recaptcha"]'), function (f) {
          if (!(f.getAttribute('title') || '').trim()) f.setAttribute('title', 'Security check');
        });
      }
    });
  })();
  // ===== BUG 95 — END =====


  // ===== BUG 96 — START =====
  // Product (Emplifi/TurnTo reviews) · Low · 4.1.2
  // The review dialog is announced as "Submission Modal" because TurnTo renders its
  // heading after the dialog opens. Handled inside Bug 41 (the naming now retries until
  // the heading exists); this block is a no-op placeholder so the finding has an id.
  (function () {
    SDS.bug(96, 'Review dialog named from its heading (see Bug 41)', { run: function () {} });
  })();
  // ===== BUG 96 — END =====



  // ##########################################################################
  //  "MORE ISSUES" SHEET — reported 24-Sep-2026 (Bugs 97–120)
  //  Source: "Copy of More issues" tab of the retest workbook.
  //  Each block below is one row of that sheet, in sheet order.
  // ##########################################################################

  // ===== BUG 97 — START =====
  // Global · Medium · 2.2.2 Pause, Stop, Hide (+1.2.1)
  // Sheet row 1: "Add play/pause button for all the background videos."
  // Bugs 51 and 61 only covered Hospitality and About. Every autoplaying video on any
  // template now gets the same Play/Pause control (SDS.videoControls skips ones already done).
  (function () {
    function label(v) {
      var sec = v.closest('.shopify-section, section');
      var h = sec && sec.querySelector('h1, h2, h3, .text-overlay__title, .text-overlay__subheading');
      var t = SDS.text(h);
      return t ? t + ' video' : 'background video';
    }
    SDS.bug(97, 'Play/Pause control on every autoplaying video', {
      run: function () {
        [].forEach.call(document.querySelectorAll('video'), function (v) {
          if (v.closest('.tt-o-modal, [role="dialog"]')) return;         // third-party dialogs
          if (!(v.autoplay || v.hasAttribute('autoplay') || v.loop)) return;
          SDS.videoControls(v, { label: label(v), decorative: true });
        });
      }
    });
  })();
  // ===== BUG 97 — END =====


  // ===== BUG 98 — START =====
  // Global (banners/sliders) · Medium · 1.4.3 Contrast (Minimum)
  // Sheet row 2: "This text over image is hard to read." (homepage hero, e.g. "THE
  // TREASURE HUNT NEVER ENDS." / "MADE IN AMERICA." — white text, no shadow, over photos).
  // Theme-side fix: switch the section's overlay style to "Shadow" in the theme editor.
  // Interim fix: a text shadow behind overlay text so it stays legible on any photo.
  (function () {
    SDS.bug(98, 'Legible text over banner images', {
      css:
        '.image-overlay .text-overlay__title,.image-overlay .text-overlay__subheading,' +
        '.image-overlay .text-overlay__rte,.image-overlay .text-overlay__heading,' +
        '.slideshow .text-overlay__title,.slideshow .text-overlay__subheading,.slideshow .text-overlay__rte' +
        '{text-shadow:0 1px 4px rgba(0,0,0,.75),0 0 18px rgba(0,0,0,.45)}'
    });
  })();
  // ===== BUG 98 — END =====


  // ===== BUG 99 — START =====
  // Homepage / product · Medium · 2.4.1 Bypass Blocks
  // Sheet row 3: "Add skip machasim for keyboard user to skip this section."
  // (the "FOLLOW THE FUN" Emplifi/Pixlee customer-photo gallery — a long run of links).
  (function () {
    SDS.bug(99, 'Skip link for the customer photo gallery', {
      css:
        '.sds-skip-widget{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;' +
        'clip:rect(0,0,0,0);white-space:nowrap}' +
        '.sds-skip-widget:focus{position:static;width:auto;height:auto;margin:0;clip:auto;overflow:visible;' +
        'display:inline-block;padding:8px 14px;background:#fff;color:#000;border:2px solid currentColor;border-radius:4px}',
      run: function () {
        [].forEach.call(document.querySelectorAll('[id^="pixlee_container"]'), function (c) {
          var section = c.closest('.shopify-section') || c.parentNode;
          if (!section || section.getAttribute('data-sds-b99')) return;
          section.setAttribute('data-sds-b99', '1');
          var end = document.createElement('span');
          end.id = SDS.id(end, 'sds-after-gallery');
          end.setAttribute('tabindex', '-1');
          var skip = document.createElement('a');
          skip.className = 'sds-skip-widget';
          skip.href = '#' + end.id;
          skip.textContent = 'Skip customer photo gallery';
          skip.addEventListener('click', function (e) { e.preventDefault(); end.focus(); });
          section.parentNode.insertBefore(skip, section);
          section.parentNode.insertBefore(end, section.nextSibling);
        });
      }
    });
  })();
  // ===== BUG 99 — END =====


  // ===== BUG 100 — START =====
  // /pages/design-advice (and every theme contact form) · High · 3.3.1, 3.3.2, 3.3.3, 4.1.3
  // Sheet row 4: "Error msg should be descriptive for all input fields with require label
  // visually present." Today the form relies on the browser's own validation bubble
  // ("Please fill in this field"), which is not announced reliably and disappears.
  // Fix: mark required fields visibly and programmatically, replace native validation with
  // inline messages tied to each field, move focus to the first error and announce the
  // result — success message included.
  (function () {
    function fieldLabel(form, input) {
      var l = input.id && form.querySelector('label[for="' + input.id + '"]');
      return SDS.text(l) || input.getAttribute('aria-label') || input.name || 'This field';
    }
    function markRequired(form) {
      [].forEach.call(form.querySelectorAll('input[required], textarea[required], select[required]'), function (input) {
        input.setAttribute('aria-required', 'true');
        var l = input.id && form.querySelector('label[for="' + input.id + '"]');
        if (l && !l.querySelector('.sds-req')) {
          var s = document.createElement('span');
          s.className = 'sds-req';
          s.innerHTML = ' <span aria-hidden="true">*</span><span class="sds-sr-only"> (required)</span>';
          l.appendChild(s);
        }
      });
    }
    function clearError(input) {
      input.removeAttribute('aria-invalid');
      var id = input.getAttribute('data-sds-err');
      var box = id && document.getElementById(id);
      if (box) box.textContent = '';
    }
    function showError(input, msg) {
      var id = input.getAttribute('data-sds-err');
      var box = id && document.getElementById(id);
      if (!box) {
        box = document.createElement('p');
        box.className = 'sds-field-error';
        box.id = SDS.id(box, 'sds-error');
        input.setAttribute('data-sds-err', box.id);
        (input.parentNode || input).insertBefore(box, input.nextSibling);
        var desc = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
        desc.push(box.id);
        input.setAttribute('aria-describedby', desc.join(' '));
      }
      box.textContent = msg;
      input.setAttribute('aria-invalid', 'true');
    }
    function validate(form) {
      var errors = [];
      [].forEach.call(form.querySelectorAll('input, textarea, select'), function (input) {
        if (input.type === 'hidden' || input.disabled) return;
        clearError(input);
        var name = fieldLabel(form, input).replace(/\s*\*?\s*\(required\)\s*$/i, '').replace(/\s*\*$/, '');
        if (input.required && !String(input.value).trim()) {
          showError(input, 'Enter your ' + name.toLowerCase() + '.');
          errors.push(input);
        } else if (input.type === 'email' && input.value && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(input.value)) {
          showError(input, 'Enter an email address in the format name@example.com.');
          errors.push(input);
        }
      });
      return errors;
    }
    SDS.bug(100, 'Contact forms: required marking, inline errors, announcements', {
      css:
        '.sds-field-error{color:#a6231b;font-size:.9em;margin:4px 0 0}' +
        '.sds-field-error:empty{display:none}' +
        '[aria-invalid="true"]{outline:2px solid #a6231b;outline-offset:1px}',
      init: function () {
        document.addEventListener('submit', function (e) {
          var form = e.target;
          if (!form.matches || !form.matches('form.contact-form, form[action*="/contact"]')) return;
          var errors = validate(form);
          if (errors.length) {
            e.preventDefault();
            SDS.announce(errors.length === 1 ? 'There is 1 problem with the form. ' + SDS.text(document.getElementById(errors[0].getAttribute('data-sds-err')))
                                             : 'There are ' + errors.length + ' problems with the form. Review the highlighted fields.');
            errors[0].focus();
          }
        }, true);
        document.addEventListener('input', function (e) {
          if (e.target.matches && e.target.matches('[aria-invalid="true"]')) clearError(e.target);
        }, true);
      },
      run: function () {
        [].forEach.call(document.querySelectorAll('form.contact-form, form[action*="/contact"]'), function (form) {
          if (!form.hasAttribute('novalidate')) form.setAttribute('novalidate', '');   // use our messages, not the browser bubble
          markRequired(form);
        });
        // success message returned by Shopify after a redirect
        [].forEach.call(document.querySelectorAll('.form-status, .form-success, .note--success'), function (s) {
          if (s.getAttribute('data-sds-b100') || !SDS.text(s)) return;
          s.setAttribute('data-sds-b100', '1');
          s.setAttribute('role', 'status');
          s.setAttribute('tabindex', '-1');
          SDS.announce(SDS.text(s));
          setTimeout(function () { s.focus(); }, 200);
        });
      }
    });
  })();
  // ===== BUG 100 — END =====


  // ===== BUG 101 — START =====
  // Global : Mobile header · High · 2.1.1, 2.4.3, 4.1.2
  // Sheet row 5: "Mobile navigation submenu is not accessible to keyboard, also focus
  // management is not correct... Not closing using ESC Key in 200% and 400%."
  // The drawer's submenu openers are <a href="#" aria-label="Show links"> (same name on
  // every row), the Back control is a link, focus stays on the hamburger when the drawer
  // opens, and Esc does nothing.
  (function () {
    var st = { opener: null, lastToggle: null };
    function drawer() { return document.querySelector('.mobile-navigation-drawer'); }
    function isOpen() { return document.body.classList.contains('reveal-mobile-nav'); }
    function close() {
      var shade = document.querySelector('.page-shade');
      if (shade) shade.click(); else document.body.classList.remove('reveal-mobile-nav');
    }
    SDS.bug(101, 'Mobile navigation: keyboard submenus, focus management, Esc', {
      init: function () {
        document.addEventListener('click', function (e) {
          // var t = e.target.closest && e.target.closest('.mobile-nav-toggle');
          var t = e.target.closest && e.target.closest('button.mobile-nav-toggle');
          if (t) st.opener = t;
        }, true);

        // Esc closes the drawer (or steps back out of a submenu) at any zoom level
        // document.addEventListener('keydown', function (e) {
        //   if ((e.key !== 'Escape' && e.key !== 'Esc') || !isOpen()) return;
        //   var d = drawer();
        //   var back = d && d.classList.contains('mobile-navigation-drawer--child-open') && d.querySelector('.mobile-nav-back');
        //   e.preventDefault();
        //   if (back) { back.click(); return; }
        //   close();
        //   setTimeout(function () { SDS.focusFirst([st.opener, '.mobile-nav-toggle']); }, 350);
        // });

        document.addEventListener('keydown', function (e) {
          if ((e.key !== 'Escape' && e.key !== 'Esc') || !isOpen()) return;
          var d = drawer();
          if (!d) return;
          e.preventDefault();

          // 1. Focus is on/inside an expanded tier-2 disclosure: collapse just that one,
          //    focus returns to its chevron. (click → theme collapses it; our click
          //    handler syncs aria-expanded and announces "… submenu collapsed")
          var openItem = e.target.closest && e.target.closest('.navigation__tier-2 > .navigation__item--open');
          if (openItem) {
            var chevron = openItem.querySelector(':scope > .navigation__children-toggle');
            if (chevron) { chevron.click(); chevron.focus(); }
            return;
          }

          // 2. Inside a child panel with nothing expanded: go Back. The Back handler
          //    returns focus to the tier-1 chevron that opened the panel (st.lastToggle).
          if (d.classList.contains('mobile-navigation-drawer--child-open')) {
            var back = d.querySelector('.mobile-nav-back');
            if (back) { back.click(); return; }
          }

          // 3. Main menu: close the drawer, focus returns to the hamburger.
          close();
          setTimeout(function () { SDS.focusFirst([st.opener, '.mobile-nav-toggle']); }, 350);
        });

        // Space activates the link-based toggles; Enter already works on links
        document.addEventListener('keydown', function (e) {
          if (e.key !== ' ' && e.key !== 'Spacebar') return;
          var t = e.target.closest && e.target.closest('.mobile-navigation-drawer .navigation__children-toggle, .mobile-nav-back');
          if (!t) return;
          e.preventDefault();
          t.click();
        }, true);

        // focus follows the panel that opened
        // document.addEventListener('click', function (e) {
        //   if (!e.target.closest) return;
        //   var d = drawer();
        //   if (!d) return;
        //   var toggle = e.target.closest('.mobile-navigation-drawer .navigation__children-toggle');
        //   if (toggle) {
        //     st.lastToggle = toggle;
        //     setTimeout(function () {
        //       var panel = toggle.nextElementSibling;
        //       SDS.focusFirst([panel && panel.querySelector('.navigation__link'), d.querySelector('.mobile-nav-back')]);
        //     }, 320);
        //     return;
        //   }
        //   if (e.target.closest('.mobile-nav-back')) {
        //     setTimeout(function () { SDS.focusFirst([st.lastToggle, d.querySelector('.navigation__link')]); }, 420);
        //   }
        // }, true);
        document.addEventListener('click', function (e) {
            if (!e.target.closest) return;
            var d = drawer();
            if (!d) return;

            var toggle = e.target.closest('.mobile-navigation-drawer .navigation__children-toggle');
            if (toggle) {
              var item = toggle.closest('.navigation__item');

              // Tier-2 chevron: in-place disclosure. Focus stays on the control,
              // aria-expanded is synced as soon as the theme has toggled the class
              // (its handler is bubble-phase on the drawer, hence the 0ms tick).
              if (toggle.closest('.navigation__tier-2')) {
                var name = SDS.text(item && item.querySelector(':scope > .navigation__link')) || 'menu';
                setTimeout(function () {
                  var open = item && item.classList.contains('navigation__item--open');
                  toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
                  SDS.announce(SDS.titleCase(name) + ' submenu ' + (open ? 'expanded' : 'collapsed'));
                }, 0);
                return;
              }

              // Tier-1 chevron: opens a new panel (title + Back) → move focus into it.
              st.lastToggle = toggle;
              setTimeout(function () {
                toggle.setAttribute('aria-expanded', item && item.classList.contains('navigation__item--open') ? 'true' : 'false');
                var panel = toggle.nextElementSibling;
                SDS.focusFirst([panel && panel.querySelector('.navigation__link'), d.querySelector('.mobile-nav-back')]);
              }, 320);
              return;
            }

            if (e.target.closest('.mobile-nav-back')) {
              setTimeout(function () {
                SDS.focusFirst([st.lastToggle, d.querySelector('.navigation__link')]);
              }, 420);
            }
      }, true);

        // when the drawer opens, move focus into it and trap Tab
        // new MutationObserver(function () {
        //   var d = drawer();
        //   if (!d) return;
        //   if (isOpen()) {
        //     d.setAttribute('role', 'dialog');
        //     d.setAttribute('aria-modal', 'true');
        //     d.setAttribute('aria-label', 'Main menu');
        //     setTimeout(function () {
        //       if (!d.contains(document.activeElement)) SDS.focusFirst([d.querySelector('.navigation__link'), d]);
        //       SDS.trap(d);
        //     }, 300);
        //   } else {
        //     SDS.releaseTrap(d);
        //     d.removeAttribute('aria-modal');
        //   }
        // }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

        new MutationObserver(function () {
            var d = drawer();
            if (!d) return;
            if (isOpen()) {
              d.removeAttribute('inert');
              d.setAttribute('role', 'dialog');
              d.setAttribute('aria-modal', 'true');
              d.setAttribute('aria-label', 'Main menu');
              setTimeout(function () {
                if (!d.contains(document.activeElement)) SDS.focusFirst([d.querySelector('.navigation__link'), d]);
                SDS.trap(d);
              }, 300);
            } else {
              SDS.releaseTrap(d);
              d.removeAttribute('aria-modal');
              // Theme hides the drawer with visibility:hidden on the container, but an open
              // child panel sets visibility:visible on itself, which wins over inheritance —
              // so expanded items stay focusable and readable while the drawer is closed.
              // inert takes the whole drawer out of the tab order and the a11y tree.
              // Delay = let the slide-out finish so the content doesn't vanish mid-animation.
              setTimeout(function () {
                if (isOpen()) return;
                d.setAttribute('inert', '');
                if (d.contains(document.activeElement) || document.activeElement === document.body) {
                  SDS.focusFirst([st.opener && !d.contains(st.opener) ? st.opener : null, 'button.mobile-nav-toggle']);
                }
              }, 350);
            }
          }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
      },
      run: function () {
        var d = drawer();
        if (!d) return;

        if (!d.hasAttribute('data-sds-b101-inert')) {
            d.setAttribute('data-sds-b101-inert', '1');
            if (!isOpen()) d.setAttribute('inert', '');
        }
        [].forEach.call(d.querySelectorAll('.navigation__children-toggle'), function (a) {
          var item = a.closest('.navigation__item');
          var name = SDS.text(item && item.querySelector(':scope > .navigation__link')) || 'menu';
          SDS.buttonize(a);
          a.setAttribute('aria-label', 'Show ' + SDS.titleCase(name) + ' submenu');
          var panel = a.nextElementSibling;
          if (panel) a.setAttribute('aria-controls', SDS.id(panel, 'sds-mobile-submenu'));
          a.setAttribute('aria-expanded', item && item.classList.contains('navigation__item--open') ? 'true' : 'false');
        });
        [].forEach.call(d.querySelectorAll('.mobile-nav-back'), function (a) {
          SDS.buttonize(a);
          a.setAttribute('aria-label', 'Back to the main menu');
        });
        [].forEach.call(document.querySelectorAll('.mobile-nav-toggle'), function (t) {
          t.setAttribute('aria-expanded', isOpen() ? 'true' : 'false');
          t.setAttribute('aria-haspopup', 'dialog');
        });
      }
    });
  })();
  // ===== BUG 101 — END =====


  // ===== BUG 102 — START =====
  // Global : Header · Medium · 2.4.7 Focus Visible
  // Sheet row 6: "Focus indicator is missing for the search button."
  // The header icon controls have outline-style:none, so keyboard focus is invisible.
  (function () {
    SDS.bug(102, 'Visible focus indicator on header controls', {
      css:
        '.show-search-link:focus-visible,.cart-link:focus-visible,.mobile-nav-toggle:focus-visible,' +
        '.header-account-link:focus-visible,.header-account-link a:focus-visible,.logo__link:focus-visible,' +
        '.main-search__close:focus-visible,.main-search__button:focus-visible,.drawer__close-btn:focus-visible' +
        '{outline:2px solid currentColor!important;outline-offset:3px!important;border-radius:2px}' +
        '.logo-area__left__inner:focus-within{overflow:visible}'
    });
  })();
  // ===== BUG 102 — END =====


  // ===== BUG 103 — START =====
  // Global : Header · Medium · 1.3.2 Meaningful Sequence, 2.4.3 Focus Order
  // Sheet row 7: reading order is "Skip to content › logo › search › account and cart";
  // it should follow the visual order "Skip to content › search › logo › account and cart".
  // Cause: .logo-area__left (search + menu) sits AFTER .logo-area__middle (logo) in the DOM
  // and is pulled left visually with CSS order:-1.
  (function () {
    SDS.bug(103, 'Header DOM order matches the visual order', {
      css: '.logo-area[data-sds-b103] .logo-area__left{order:0!important}',
      run: function () {
        [].forEach.call(document.querySelectorAll('.logo-area'), function (area) {
          if (area.getAttribute('data-sds-b103')) return;
          var left = area.querySelector(':scope > .logo-area__left');
          var middle = area.querySelector(':scope > .logo-area__middle');
          if (!left || !middle) return;
          area.setAttribute('data-sds-b103', '1');
          area.insertBefore(left, middle);   // search/menu now precede the logo for AT and Tab
        });
      }
    });
  })();
  // ===== BUG 103 — END =====


  // ===== BUG 104 — START =====
  // Global : Header · Low · 1.4.1 Use of Colour / consistency
  // Sheet row 8: "Add the dropdown icon in menus always. currently it is present only for
  // keyboard user." The submenu button added by Bug 9 was visually hidden until focus;
  // this makes the chevron permanently visible for everyone.
  (function () {
    SDS.bug(104, 'Menu dropdown icon always visible', {
      css:
        '.sds-nav-toggle{position:relative!important;width:18px!important;height:22px!important;margin:0 0 0 -13px!important;' +
        'padding:0!important;overflow:visible!important;clip:auto!important;display:inline-flex!important;' +
        'align-items:center;justify-content:center;vertical-align:middle;opacity:.9}' +
        '.sds-nav-toggle:focus-visible{outline:2px solid currentColor!important;outline-offset:1px!important}' +
        '.sds-nav-toggle[aria-expanded="true"] svg{transform:rotate(180deg)}'
    });
  })();
  // ===== BUG 104 — END =====


  // ===== BUG 105 — START =====
  // Collection : Filters · Medium · 2.1.1 Keyboard, 4.1.3 Status Messages
  // Sheet row 9: "checkboxes are not working with enter key. Also when result changes the
  // announcement is not informed by the screen reader user."
  (function () {
    SDS.bug(105, 'Filter checkboxes: Enter key + change announcement', {
      init: function () {
        document.addEventListener('keydown', function (e) {
          if (e.key !== 'Enter') return;
          var cb = e.target.closest && e.target.closest('.filter-group__checkbox');
          if (!cb) return;
          e.preventDefault();
          cb.click();
        }, true);
        document.addEventListener('change', function (e) {
          var cb = e.target.closest && e.target.closest('.filter-group__checkbox');
          if (!cb) return;
          var name = SDS.text(cb.closest('label')) || cb.value;
          SDS.announce(name + (cb.checked ? ' filter applied.' : ' filter removed.'));
        }, true);
      }
    });
  })();
  // ===== BUG 105 — END =====


  // ===== BUG 106 — START =====
  // Collection : Filters · Medium · 2.4.4 Link Purpose, 4.1.3 Status Messages
  // Sheet row 10: "Delete applied filter does not [have a] proper accessible name and it
  // does not inform the screen reader user what item is deleted / updated result."
  (function () {
    SDS.bug(106, 'Applied-filter chips: names and removal announcements', {
      init: function () {
        document.addEventListener('click', function (e) {
          if (!e.target.closest) return;
          var chip = e.target.closest('.filter-group__applied-item');
          if (chip) { SDS.announce('Removed filter ' + SDS.text(chip) + '. Updating results.'); return; }
          if (e.target.closest('.filter-group__clear-row a, .filter-group__clear')) SDS.announce('All filters cleared. Updating results.');
        }, true);
      },
      run: function () {
        [].forEach.call(document.querySelectorAll('.filter-group__applied-item'), function (chip) {
          var text = SDS.text(chip);
          if (text) chip.setAttribute('aria-label', 'Remove filter: ' + text);
        });
        [].forEach.call(document.querySelectorAll('.filter-group__clear-row a, .filter-group__clear'), function (a) {
          if (!a.getAttribute('aria-label')) a.setAttribute('aria-label', 'Clear all filters');
        });
      }
    });
  })();
  // ===== BUG 106 — END =====


  // ===== BUG 107 — START =====
  // Collection : Filters · Medium · 2.4.3 Focus Order
  // Sheet row 11: "When filter is expanded the focus is not going inside the filter,
  // instead it is going to 'Sort by : Featured' dropdown."
  (function () {
    SDS.bug(107, 'Focus moves into the filter panel when it opens', {
      init: function () {
        document.addEventListener('click', function (e) {
          var t = e.target.closest && e.target.closest('.toggle-btn[data-toggle-filters]');
          if (!t) return;
          setTimeout(function () {
            if (t.getAttribute('aria-expanded') !== 'true') return;      // only when it opened
            var fc = document.querySelector('filter-container');
            var panel = fc && fc.querySelector('.filters');
            if (!panel || !panel.offsetParent) return;
            // The drawer heading only exists on mobile; on desktop it collapses to 0x0,
            // so fall through to the Close control, then the first filter group heading,
            // and only then the panel itself.
            var head = panel.querySelector('.filters__heading-text');
            if (head) head.setAttribute('tabindex', '-1');
            if (!panel.getAttribute('aria-label')) panel.setAttribute('aria-label', 'Filters');
            SDS.focusFirst([
              head,
              panel.querySelector('.filters__close'),
              panel.querySelector('.filter-group__heading'),
              panel
            ]);
          }, 400);
        }, true);
      }
    });
  })();
  // ===== BUG 107 — END =====


  // ===== BUG 108 — START =====
  // Collection : Filters · Medium · 4.1.2 Name, Role, Value, 4.1.3 Status Messages
  // Sheet row 12: "Minimum and maximum price does not [have an] accessible name. When
  // slider updates, [it] does not announce any announcement."
  // Inputs are named only "From"/"To" and the slider handles carry a bare number.
  (function () {
    var last = '';
    function money(v) { return '$' + Number(v || 0).toLocaleString('en-US'); }
    SDS.bug(108, 'Price range: accessible names and value announcements', {
      init: function () {
        var announce = function () {
          var min = document.getElementById('CCPriceRangeMin');
          var max = document.getElementById('CCPriceRangeMax');
          if (!min || !max) return;
          var msg = 'Price range ' + money(min.value || min.placeholder) + ' to ' + money(max.value || max.placeholder);
          if (msg === last) return;
          last = msg;
          SDS.announce(msg);
        };
        var debounced;
        ['input', 'change'].forEach(function (ev) {
          document.addEventListener(ev, function (e) {
            if (!e.target.closest || !e.target.closest('.cc-price-range')) return;
            clearTimeout(debounced);
            debounced = setTimeout(announce, 600);
          }, true);
        });
        document.addEventListener('keyup', function (e) {
          if (!e.target.closest || !e.target.closest('.cc-price-range__control')) return;
          clearTimeout(debounced);
          debounced = setTimeout(announce, 600);
        }, true);
      },
      run: function () {
        var min = document.getElementById('CCPriceRangeMin');
        var max = document.getElementById('CCPriceRangeMax');
        if (min) min.setAttribute('aria-label', 'Minimum price in dollars');
        if (max) max.setAttribute('aria-label', 'Maximum price in dollars');
        [['CCPriceRangeSliderMin', 'Minimum price'], ['CCPriceRangeSliderMax', 'Maximum price']].forEach(function (pair) {
          var el = document.getElementById(pair[0]);
          if (!el) return;
          el.setAttribute('aria-label', pair[1]);
          var now = el.getAttribute('aria-valuenow');
          if (now !== null) el.setAttribute('aria-valuetext', money(now));
        });
      }
    });
  })();
  // ===== BUG 108 — END =====


  // ===== BUG 109 — START =====
  // Collection · Medium · 4.1.3 Status Messages
  // Sheet row 13: "When filter and sort by is applied then it should make the announcement
  // to the screen reader like '785 products found matching with your filter'."
  (function () {
    var last = null;
    function countEl() {
      return document.querySelector('.utility-bar__centre .utility-bar__item, .collection-count, [data-product-count]');
    }
    SDS.bug(109, 'Announce the result count after filtering or sorting', {
      run: function () {
        var el = countEl();
        if (!el) return;
        // The count node is swapped out wholesale by the AJAX filter, so compare the text
        // (not the marker) — otherwise every re-render would silently reset the baseline.
        var now = SDS.text(el);
        if (!el.getAttribute('data-sds-b109')) {
          el.setAttribute('data-sds-b109', '1');
          el.setAttribute('role', 'status');
          el.setAttribute('aria-live', 'polite');
        }
        if (now && last !== null && now !== last) {
          SDS.announce(now + ' found matching your filters.');
        }
        if (now) last = now;
      }
    });
  })();
  // ===== BUG 109 — END =====


  // ===== BUG 110 — START =====
  // Collection : product cards · High · 2.1.1 Keyboard
  // Sheet row 14: "variant options are expanded on hover but they are not expanded and
  // accessible for keyboard users."
  // Theme CSS: .product-block-options__item--truncated{display:none} and
  // .product-block-options:hover .product-block-options__item--truncated{display:inline-block}
  // so with 20 colours a mouse user sees all 20 and a keyboard user sees 3.
  // NOTE: Bug 31 marks off-screen swatches aria-hidden; it skips any swatch this block
  // has made keyboard reachable (data-sds-b110).

  (function () {
    var counter = 0;

        function srOnly(text) {
          var s = document.createElement("span");
          s.className = "sds-sr-only";
          s.textContent = text;
          return s;
        }

        // visible "+ 17 more" / "Show fewer", accessible name "+ 17 more color options" (SC 2.5.3 safe)
        function label(btn, expanded) {
          var n = btn.getAttribute("data-sds-more"), opt = btn.getAttribute("data-sds-opt");
          btn.textContent = "";
          btn.appendChild(document.createTextNode(expanded ? "Show fewer" : "+ " + n + " more"));
          btn.appendChild(srOnly(" " + opt + " options"));
        }

        function setState(opts, btn, expanded) {
          opts.classList.toggle("product-block-options--sds-expanded", expanded);
          btn.setAttribute("aria-expanded", expanded ? "true" : "false");
          label(btn, expanded);
        }

        SDS.bug(110, "Collection cards: truncated variant swatches behind a keyboard-operable disclosure", {
          css:
            /* revealed only by the button — mouse users keep the theme's own :hover reveal */
            ".product-block-options--sds-expanded .product-block-options__item--truncated{display:inline-block!important}" +
            /* native <button> wearing the theme's label class */
            "button.product-block-options__more-label{background:none;border:0;padding:0;font:inherit;color:inherit;cursor:pointer}" +
            "button.product-block-options__more-label:focus-visible{outline:2px solid currentColor;outline-offset:3px}",

          init: function () {
            document.addEventListener("click", function (e) {
              var btn = e.target.closest && e.target.closest("button.product-block-options__more-label[data-sds-b110]");
              if (!btn) return;
              e.preventDefault();
              var opts = btn.closest(".product-block-options");
              var open = btn.getAttribute("aria-expanded") !== "true";
              setState(opts, btn, open);
              if (open) {
                // revealed swatches sit *before* the button in DOM order, so put focus on the first one
                var first = opts.querySelector(".product-block-options__item--truncated[data-sds-b110]");
                if (first) first.focus();
                SDS.announce(btn.getAttribute("data-sds-more") + " more " + btn.getAttribute("data-sds-opt") + " options shown");
              }
            });

            // Esc anywhere inside an open group collapses it and returns focus to the toggle
            document.addEventListener("keydown", function (e) {
              if (e.key !== "Escape" && e.key !== "Esc") return;
              var opts = e.target.closest && e.target.closest(".product-block-options--sds-expanded");
              if (!opts) return;
              var btn = opts.querySelector("button.product-block-options__more-label[data-sds-b110]");
              if (!btn) return;
              setState(opts, btn, false);
              btn.focus();
            });
          },

          run: function () {
            // truncated swatches: named buttons, but display:none keeps them out of the tab order until opened
            [].forEach.call(document.querySelectorAll("product-block .product-block-options__item--truncated[data-media]:not([data-sds-b110])"), function (item) {
              var name = SDS.text(item.querySelector(".product-block-options__item__text"));
              item.setAttribute("data-sds-b110", "1");            // bug 31 leaves b110 items alone
              item.removeAttribute("aria-hidden");
              SDS.buttonize(item);
              item.setAttribute("tabindex", "0");
              if (name && item.getAttribute("aria-label") !== name) item.setAttribute("aria-label", name);
              [].forEach.call(item.querySelectorAll("img"), function (img) {
                if (img.getAttribute("alt") !== "") img.setAttribute("alt", "");
              });
            });

            // "+ N more" span → native disclosure button
            [].forEach.call(document.querySelectorAll("product-block span.product-block-options__more-label"), function (span) {
              var opts = span.closest(".product-block-options"), inner = span.closest(".product-block-options__inner");
              var n = (SDS.text(span).match(/\d+/) || [])[0];
              if (!opts || !inner || !n) return;

              var hint = span.nextElementSibling;                 // bug 31's "…on the product page" note – now false
              if (hint && hint.classList.contains("sds-sr-only")) hint.parentNode.removeChild(hint);
              if (!inner.id) inner.id = "sds-swatches-" + (++counter);

              var btn = document.createElement("button");
              btn.type = "button";
              btn.className = span.className;
              btn.setAttribute("data-sds-b110", "1");
              btn.setAttribute("data-sds-b31", "1");              // bug 31's :not([data-sds-b31]) skips it
              btn.setAttribute("aria-expanded", "false");
              btn.setAttribute("aria-controls", inner.id);
              btn.setAttribute("data-sds-more", n);
              btn.setAttribute("data-sds-opt", (opts.getAttribute("data-option-name") || "color").toLowerCase());
              span.parentNode.replaceChild(btn, span);
              label(btn, false);
            });
          }
        });
  })();


  // ===== BUG 110 — END =====


  // ===== BUG 111 — START =====
  // Global · High · 2.4.1 Bypass Blocks, 2.4.7 Focus Visible
  // Sheet row 15: "When I click on the skip to main content link, the page loads itself."
  // The link is also permanently .visually-hidden, so it never appears on focus.
  (function () {
    SDS.bug(111, 'Skip to content: focuses main instead of reloading, visible on focus', {
      css:
        'a.skip-link:focus{position:fixed!important;top:10px!important;left:10px!important;width:auto!important;' +
        'height:auto!important;margin:0!important;padding:10px 16px!important;clip:auto!important;overflow:visible!important;' +
        'background:#fff!important;color:#000!important;border:2px solid #000!important;border-radius:4px;z-index:100000!important;' +
        'white-space:nowrap}',
      init: function () {
        document.addEventListener('click', function (e) {
          var a = e.target.closest && e.target.closest('a.skip-link, a[data-cs-role="skip"], a[href="#content"]');
          if (!a) return;
          var target = document.getElementById((a.getAttribute('href') || '#content').slice(1)) || document.getElementById('content');
          if (!target) return;
          e.preventDefault();                       // stop the navigation that reloads the page
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
          target.scrollIntoView();
        }, true);
      }
    });
  })();
  // ===== BUG 111 — END =====


  // ===== BUG 112 — START =====
  // Product · High · 4.1.2 Name, Role, Value, 4.1.3 Status Messages
  // Sheet row 16: "Size and colors options do not have proper accessible name and also
  // selected item is not announced to screen reader."
  // Colour swatches are radio inputs whose <label> holds only an image, so they have no name.
  (function () {
    SDS.bug(112, 'Product options: names for swatches and selection announcements', {
      init: function () {
        document.addEventListener('change', function (e) {
          var input = e.target.closest && e.target.closest('.option-selector .opt-btn');
          if (!input || !input.checked) return;
          var set = input.closest('.option-selector');
          var option = (set && set.getAttribute('data-option')) || 'Option';
          SDS.announce(option + ': ' + (input.value || input.getAttribute('aria-label')) + ' selected.');
        }, true);
      },
      run: function () {
        [].forEach.call(document.querySelectorAll('.option-selector'), function (set) {
          var option = set.getAttribute('data-option') || SDS.text(set.querySelector('legend'));
          [].forEach.call(set.querySelectorAll('.opt-btn'), function (input) {
            var label = input.id && set.querySelector('label[for="' + input.id.replace(/"/g, '\\"') + '"]');
            // The swatch image inside the label is always decorative: the label already holds
            // the colour name, so the raw Shopify alt ("#color_celie-pink-and-green-fabric")
            // only doubles up the announcement.
            [].forEach.call(label ? label.querySelectorAll('img') : [], function (img) {
              if (img.getAttribute('alt') !== '') img.setAttribute('alt', '');
            });
            var visible = SDS.text(label);
            if (!visible) {                                  // image-only swatch → name it
              var name = input.value || input.getAttribute('data-value') || '';
              if (name && input.getAttribute('aria-label') !== option + ': ' + name) {
                input.setAttribute('aria-label', option + ': ' + name);
              }
            }
          });
        });
      }
    });
  })();
  // ===== BUG 112 — END =====


  // ===== BUG 113 — START =====
  // Product · High · 4.1.2 Name, Role, Value, 4.1.3 Status Messages
  // Sheet row 17: "Quantity buttons are announcing as link, also quantity status is not
  // announced." On the product page the controls are
  // <quantity-wrapper><a data-quantity="down"> … <input name="quantity"> … <a data-quantity="up">.
  // (Bug 39 covers the same controls inside the cart drawer.)
  (function () {
    SDS.bug(113, 'Product quantity controls: buttons + value announcements', {
      init: function () {
        var t;
        var watch = function (input) {
          if (!input || input.getAttribute('data-sds-b113')) return;
          input.setAttribute('data-sds-b113', '1');
          ['change', 'input'].forEach(function (ev) {
            input.addEventListener(ev, function () {
              clearTimeout(t);
              t = setTimeout(function () { SDS.announce('Quantity ' + input.value); }, 350);
            });
          });
        };
        document.addEventListener('click', function (e) {
          var b = e.target.closest && e.target.closest('quantity-wrapper [data-quantity], .quantity [data-quantity]');
          if (!b) return;
          var wrap = b.closest('quantity-wrapper, .quantity');
          var input = wrap && wrap.querySelector('input[name="quantity"], input[type="number"]');
          watch(input);
          setTimeout(function () { if (input) SDS.announce('Quantity ' + input.value); }, 300);
        }, true);
      },
      run: function () {
        [].forEach.call(document.querySelectorAll('quantity-wrapper [data-quantity], .quantity a[data-quantity]'), function (a) {
          SDS.buttonize(a);
          if (!a.getAttribute('aria-label')) {
            a.setAttribute('aria-label', a.getAttribute('data-quantity') === 'up' ? 'Increase quantity' : 'Decrease quantity');
          }
        });
      }
    });
  })();
  // ===== BUG 113 — END =====


  // ===== BUG 114 — START =====
  // Global · Medium · 4.1.3 Status Messages
  // Sheet row 18: "When item is added to the cart, user should be informed about it."
  // Bug 37 announces this on the product page only; Quick buy and collection add-to-cart
  // did not announce anything.
  (function () {
    var lastCount = null, pending = false;
    function count() {
      var el = document.querySelector('.cart-link__count');
      return el ? (parseInt(SDS.text(el), 10) || 0) : null;
    }
    SDS.bug(114, 'Announce every add to cart', {
      init: function () {
        document.addEventListener('click', function (e) {
          if (e.target.closest && e.target.closest('[name="add"], .add-to-cart, .quick-add__submit')) {
            lastCount = count();
            pending = true;
            setTimeout(function () { pending = false; }, 12000);
          }
        }, true);
        var check = function () {
          if (!pending) { lastCount = count(); return; }
          var now = count();
          if (now !== null && lastCount !== null && now > lastCount) {
            pending = false;
            SDS.announce('Item added to your cart. Cart now has ' + now + (now === 1 ? ' item.' : ' items.'));
          }
          lastCount = now;
        };
        ['on:cart:after-merge', 'on:cart:change', 'on:cart-drawer:after-open'].forEach(function (ev) {
          document.addEventListener(ev, function () { setTimeout(check, 300); });
        });
        setInterval(check, 1200);
      }
    });
  })();
  // ===== BUG 114 — END =====


  // ===== BUG 115 — START =====
  // Collection (all templates) · High · 1.1.1 Non-text Content
  // Sheet row 19: "Add meaningful alt text for this image."
  // (/collections/fabric-by-the-yard-swatches — product card images carry alt="".)
  // Bug 11 did this on the homepage only; this covers every template that uses product cards.
  (function () {
    SDS.bug(115, 'Product card images named on every template', {
      run: function () {
        if (SDS.isTemplate('index')) return;           // Bug 11 owns the homepage
        [].forEach.call(document.querySelectorAll('product-block'), function (block) {

        if (block.getAttribute('role') === 'option') {
          [].forEach.call(block.querySelectorAll('.product-block__image img'), function (img) {
            if (img.getAttribute('alt') !== '') img.setAttribute('alt', '');
          });
          return;
        }
          var title = SDS.text(block.querySelector('.product-block__title'));
          if (!title) return;
          [].forEach.call(block.querySelectorAll('.image-cont a.product-link[aria-hidden="true"]'), function (a) {
            a.removeAttribute('aria-hidden');
          });
          [].forEach.call(block.querySelectorAll('.product-block__image'), function (wrap) {
            var img = wrap.querySelector('img');
            if (!img) return;
            if (wrap.classList.contains('product-block__image--active')) {
              wrap.removeAttribute('aria-hidden');
              var alt = SDS.cleanAlt(img.getAttribute('alt')) || title;
              if (img.getAttribute('alt') !== alt) img.setAttribute('alt', alt);
            } else if (wrap.getAttribute('aria-hidden') !== 'true') {
              wrap.setAttribute('aria-hidden', 'true');
            }
          });
        });
      }
    });
  })();
  // ===== BUG 115 — END =====


  // ===== BUG 116 — START =====
  // Collection · High · 2.1.1 Keyboard, 2.4.3 Focus Order
  // Sheet row 20: "Quick view button is not keyboard accessible, it is only receiving
  // keyboard focus when on backward tab."
  // Root cause is the same hover-only visibility as Bugs 13/27/90: the control cannot take
  // focus while it is visibility:hidden, so forward Tab skips past it and only a Shift+Tab
  // from the next card lands on it. This block keeps it revealed and named on every
  // collection template, including /collections/fabric-by-the-yard-swatches.
  (function () {
    SDS.bug(116, 'Quick view/Quick buy reachable on forward Tab', {
      css:
        'product-block:focus-within .btn.quickbuy-toggle,product-block .btn.quickbuy-toggle:focus,' +
        'product-block:hover .btn.quickbuy-toggle' +
        '{visibility:visible!important;opacity:1!important;transition:none!important}',
      run: function () {
        [].forEach.call(document.querySelectorAll('product-block .quickbuy-toggle'), function (t) {
          if (t.getAttribute('tabindex') === '-1') t.removeAttribute('tabindex');
          var title = SDS.text((t.closest('product-block') || document).querySelector('.product-block__title'));
          var label = SDS.text(t) || 'Quick view';
          if (title && t.getAttribute('aria-label') !== label + ', ' + title) {
            t.setAttribute('aria-label', label + ', ' + title);
          }
        });
      }
    });
  })();
  // ===== BUG 116 — END =====


  // ===== BUG 117 — START =====
  // /pages/designer-trade-program · High · 3.3.1, 3.3.2, 3.3.3, 4.1.3 — VENDOR-SIDE
  // Sheet row 21: "Required field is not present also when submitting the form it shows
  // browser alert. there is no descriptive error msg present, also the success msg is not
  // announced to the screen reader."
  // The application form is a cross-origin NetSuite iframe
  // (4523154.extforms.netsuite.com), so its fields, validation and messages CANNOT be
  // changed from the Shopify theme — same-origin policy blocks any script access.
  // What must happen: fix the form in NetSuite (mark required fields, replace the browser
  // alert with inline errors tied to each field, and put the success message in a live
  // region). This block only makes the embed itself announce properly and gives keyboard
  // users a labelled region plus an email fallback route.
  (function () {
    SDS.bug(117, 'Designer Trade form embed: labelled region + fallback (form itself is NetSuite-side)', {
      run: function () {
        if (!SDS.isPage('designer-trade-program')) return;
        [].forEach.call(document.querySelectorAll('main iframe'), function (f) {
          var t = (f.getAttribute('title') || '').trim();
          if (!t || /^(embedded content|iframe|frame)$/i.test(t)) f.setAttribute('title', 'Designer Trade Program application form');
          if (f.getAttribute('data-sds-b117')) return;
          f.setAttribute('data-sds-b117', '1');
          var region = document.createElement('div');
          region.setAttribute('role', 'region');
          region.setAttribute('aria-label', 'Designer Trade Program application form');
          f.parentNode.insertBefore(region, f);
          region.appendChild(f);
          var note = document.createElement('p');
          note.className = 'sds-sr-only';
          note.textContent = 'This application form is provided by an external system. If any field is unclear or the form cannot be submitted, email hello@scoutdesignstudio.com to apply instead.';
          region.insertBefore(note, f);
        });
      }
    });
  })();

  (function () {
  SDS.bug(117, 'Quick buy: print-only tear sheet rendered on screen', {
    css: '#tearSheetQuickBuy{display:none!important}'
  });
})();
  // ===== BUG 117 — END =====


  // ===== BUG 118 — START =====
  // /pages/hospitality · Medium · 1.4.3 Contrast (Minimum)
  // Sheet row 22: "Texts are present above the image making [it] difficult to see visually:
  // <div class="text-overlay__subheading subheading subheading--over">Birdie's Eastside Dallas</div>"
  // Gallery captions are white with no shadow or scrim over photos of any brightness.
  // (function () {
  //   SDS.bug(118, 'Hospitality gallery captions legible over photos', {
  //     css:
  //       '.subheading--over,.gallery__item .text-overlay__subheading,.gallery__item .text-overlay__title,' +
  //       '.image-overlay .subheading--over' +
  //       '{text-shadow:0 1px 4px rgba(0,0,0,.8),0 0 16px rgba(0,0,0,.5)}'
  //   });
  // })();
  // ===== BUG 118 — END =====


  // ===== BUG 119 — START =====
  // /pages/contact · Medium · 1.1.1 Non-text Content, 2.4.4 Link Purpose
  // Sheet row 23: "decorative image has alt text, also with no accessible name for all".
  // Bug 69 gave the illustration links a name by putting it on the <img>. The image is
  // decorative next to its own heading and email link, so the name belongs on the link:
  // image alt="" and the purpose on the anchor itself.
  (function () {
    var PURPOSE = {
      'sales@': 'Email our sales team',
      'delivery@': 'Email our delivery team',
      'customerservice@': 'Email customer service',
      'hello@': 'Email Scout Design Studio to collaborate'
    };
    SDS.bug(119, 'Contact: illustration links named, images decorative', {
      run: function () {
        if (!SDS.isPage('contact')) return;
        [].forEach.call(document.querySelectorAll('main a[href^="mailto:"]'), function (a) {
          var img = a.querySelector('img');
          if (!img || SDS.text(a)) return;                   // only image-only links
          var href = (a.getAttribute('href') || '').toLowerCase();
          var name = '';
          for (var key in PURPOSE) { if (href.indexOf(key) > -1) { name = PURPOSE[key]; break; } }
          if (!name) name = 'Email ' + href.replace('mailto:', '');
          if (a.getAttribute('aria-label') !== name) a.setAttribute('aria-label', name);
          if (img.getAttribute('alt') !== '') img.setAttribute('alt', '');   // decorative
        });
      }
    });
  })();
  // ===== BUG 119 — END =====


  // ===== BUG 120 — START =====
  // Collection · Medium · 1.3.2, 4.1.2 (hidden content exposed)
  // Sheet row 24: "Filter sidebar content is accessible for screen reader even it is hidden
  // on mobile view." The panel keeps display:block/visibility:visible while it is parked
  // off-canvas, so its 18 controls stay in the tab order and the screen reader buffer.
  (function () {
    function apply() {
      var fc = document.querySelector('filter-container');
      var panel = fc && fc.querySelector('.filters');
      if (!panel) return;
      var visible = !!panel.offsetParent && panel.getBoundingClientRect().width > 0;
      if (visible) {
        panel.removeAttribute('aria-hidden');
        panel.removeAttribute('inert');
      } else if (!panel.hasAttribute('inert')) {
        panel.setAttribute('aria-hidden', 'true');
        panel.setAttribute('inert', '');
      }
    }
    SDS.bug(120, 'Closed filter panel hidden from screen readers and Tab', {
      init: function () {
        var fc = document.querySelector('filter-container');
        if (fc) new MutationObserver(apply).observe(fc, { attributes: true, attributeFilter: ['class'] });
        window.addEventListener('resize', function () { setTimeout(apply, 300); });
      },
      run: apply
    });
  })();
  // ===== BUG 120 — END =====



  // ===== BUG 121 — Search annoucement =====
// Global : Header search · Medium · 4.1.3 Status Messages, 1.3.1
// Theme writes #HeaderSearchResultsStatus only when results exist, and with the
// search page's capped total. No-results and cleared states are never announced.
(function () {
  SDS.bug(118, 'Header search: announce suggestion count, no results, and how to browse', {
    init: function () {
      var box = document.getElementById('HeaderSearchResults');
      var input = document.querySelector('.main-search__input');
      var themeStatus = document.getElementById('HeaderSearchResultsStatus');
      if (!box || !input || !themeStatus) return;

      // retire the theme's region (its write races ours) and own a fresh one
      themeStatus.removeAttribute('role');
      themeStatus.removeAttribute('aria-live');
      var status = document.createElement('div');
      status.id = 'sds-search-status';
      status.className = 'sds-sr-only';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      themeStatus.parentNode.insertBefore(status, themeStatus);
      
      var last = '', timer;
      function update() {
        var term = (input.value || '').trim();
        var empty = box.querySelector('.main-search__empty-message');
        var items = box.querySelectorAll('.main-search-result, .product-block');
        var msg = '';
        // theme resets its index but not the attribute → VO announces the new first
          // option as selected on the next search; clear it whenever results re-render
          input.removeAttribute('aria-activedescendant');
          [].forEach.call(box.querySelectorAll('[aria-selected="true"]'), function (o) {
            o.setAttribute('aria-selected', 'false');
          });
          // image pager buttons have no role in a listbox and pollute the option name
          [].forEach.call(box.querySelectorAll('[role="option"] .image-page-button'), function (b) {
            b.setAttribute('aria-hidden', 'true');
            b.setAttribute('tabindex', '-1');
          });

          // option name = all content; the image alt duplicates the title link
          [].forEach.call(box.querySelectorAll('[role="option"] .image-cont img'), function (i) {
            i.setAttribute('alt', '');
          });


        if (term && empty) msg = 'No results for \u201c' + term + '\u201d.';
        else if (term && items.length) msg = items.length + ' suggestion' + (items.length === 1 ? '' : 's') + ' for \u201c' + term + '\u201d. Use up and down arrow keys to browse, Enter to open.';
        if (msg === last) return;
        last = msg;
        status.textContent = '';
        setTimeout(function () { status.textContent = msg; }, 80);
      }
      new MutationObserver(function () {
        clearTimeout(timer); timer = setTimeout(update, 150);
       }).observe(box, { childList: true, subtree: true });
        }
      });
    })();

// ===== BUG 121 — END =====

// ===== BUG 130 — START =====

// New error, will go to staging to live

(function () {
  SDS.bug(131, "Product cards: lazy images never activate for keyboard users", {
    init: function () {
      document.addEventListener("focusin", function (e) {
        var block = e.target.closest && e.target.closest("product-block");
        if (!block) return;
        if (typeof block.lazyLoadAll === "function") {
          block.lazyLoadAll();
        } else {
          block.querySelectorAll(".product-block__image--inactivated")
               .forEach(function (el) { el.classList.remove("product-block__image--inactivated"); });
        }
      }, true);
    }
  });
})();



// ===== BUG 132 — START =====
(function () {
  SDS.bug(132, "Shop Pay installments: '/mo' expanded to 'per month' for screen readers", {
    init: function () {
      // The component re-renders on variant change; watch its shadow root directly.
      var watched = [];
      function watch(host) {
        if (!host.shadowRoot || watched.indexOf(host) > -1) return;
        watched.push(host);
        new MutationObserver(function () { SDS.runBug(132); })
          .observe(host.shadowRoot, { childList: true, subtree: true, characterData: true });
      }
      document.querySelectorAll("shopify-payment-terms").forEach(watch);
      this._watch = watch;
    },
    run: function () {
      var self = this;
      document.querySelectorAll("shopify-payment-terms").forEach(function (host) {
        var root = host.shadowRoot;
        if (!root) return;
        self._watch && self._watch(host);
        var content = root.querySelector("#shopify-installments-content");
        if (!content || content.querySelector("[data-sds-mo]")) return;

        var walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
        var node;
        while ((node = walker.nextNode())) {
          var m = node.textContent.match(/\/mo\b/);
          if (!m) continue;

          var after = node.splitText(m.index);       // "/mo with "
          after.textContent = after.textContent.slice(3); // " with "

          var visual = document.createElement("span");
          visual.setAttribute("aria-hidden", "true");
          visual.setAttribute("data-sds-mo", "1");
          visual.textContent = "/mo";

          var sr = document.createElement("span");
          sr.textContent = " per month";
          sr.style.cssText = "position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;";

          node.parentNode.insertBefore(visual, after);
          node.parentNode.insertBefore(sr, after);
          break;
        }
      });
    }
  });
})();
// ===== BUG 132 — END =====



// ===== BUG 134 — START =====
(function () {
  SDS.bug(134, "Footer newsletter: focus and announce the result after submit", {
    css: ".sds-newsletter-msg:focus{outline:2px solid currentColor;outline-offset:4px}",
    init: function () {
      var params = window.location.search;
      var posted = /[?&]customer_posted=true/.test(params);
      var block = document.querySelector(".section-footer__newsletter-block, .signup-form");
      if (!block) return;

      // Find the result message rendered by the theme (success text or form errors).
      var msg = null;
      if (posted) {
        msg = [].slice.call(block.querySelectorAll("p, div"))
          .filter(function (el) { return el.children.length === 0 && /thank you/i.test(el.textContent); })[0];
      } else {
        msg = block.querySelector(".errors, .form-errors, [class*='error']");
      }
      if (!msg) return;

      msg.classList.add("sds-newsletter-msg");
      msg.setAttribute("role", posted ? "status" : "alert");
      msg.setAttribute("tabindex", "-1");

      var text = msg.textContent.replace(/\s+/g, " ").trim();
      setTimeout(function () {
        msg.scrollIntoView({ block: "center" });
        msg.focus({ preventScroll: true });
        SDS.announce(text);
      }, 300);

      // Clean the URL so a refresh/back doesn't re-trigger this.
      if (posted && window.history && history.replaceState) {
        history.replaceState(null, "", window.location.pathname + window.location.hash);
      }
    }
  });
})();
// ===== BUG 134 — END =====




// ===== BUG 135 — START =====
(function () {
  var cache = {};   // handle -> { byId: {mediaId: alt}, title }

  function handleFrom(root) {
    var a = root.querySelector('a[href*="/products/"]');
    var m = a && a.getAttribute("href").match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function label(root, data) {
    var title = (root.querySelector(".title, .product-title, h2") || {}).textContent || "";
    title = title.replace(/\s+/g, " ").trim() || (data && data.title) || "Product";

    // Slides
    var items = [].slice.call(root.querySelectorAll("media-gallery .slider__item"));
    var total = items.length;
    items.forEach(function (li, i) {
      var link = li.querySelector("a.block");
      var img = li.querySelector("img");
      var id = (li.querySelector("[data-media-id]") || li).getAttribute("data-media-id");
      var alt = data && data.byId[id] ? data.byId[id] : title + ", image " + (i + 1) + " of " + total;
      if (img) img.setAttribute("alt", alt);
      if (link) link.setAttribute("aria-label", "Open image " + (i + 1) + " of " + total + " in full-screen gallery");
    });

    // Thumbnails
    var thumbs = [].slice.call(root.querySelectorAll(".thumbnails a.thumbnail"));
    thumbs.forEach(function (t, i) {
      var li = t.closest(".slider__item");
      var id = (li && li.getAttribute("data-media-id")) || (items[i] && (items[i].querySelector("[data-media-id]") || items[i]).getAttribute("data-media-id"));
      var alt = data && data.byId[id] ? data.byId[id] : title;
      t.setAttribute("aria-label", "View image " + (i + 1) + " of " + thumbs.length + ": " + alt);
      var img = t.querySelector("img");
      if (img) img.setAttribute("alt", "");                     // link carries the name
      if (li && li.classList.contains("is-active")) t.setAttribute("aria-current", "true");
      else t.removeAttribute("aria-current");
    });
    root.setAttribute("data-sds-b135", "1");
  }

  SDS.bug(135, "Quick buy: gallery slides and thumbnails named", {
    init: function () {
      // keep aria-current in sync when the user changes thumbnail
      document.addEventListener("click", function (e) {
        var t = e.target.closest && e.target.closest(".quickbuy-container .thumbnails a.thumbnail");
        if (!t) return;
        var root = t.closest(".quickbuy-container");
        setTimeout(function () { root && label(root, cache[handleFrom(root)]); }, 100);
      }, true);
    },
    run: function () {
      document.querySelectorAll(".quickbuy-container").forEach(function (root) {
        if (!root.querySelector("media-gallery")) return;
        var h = handleFrom(root);
        if (!h) { label(root, null); return; }
        if (cache[h]) { label(root, cache[h]); return; }
        cache[h] = { byId: {}, title: "", loading: true };
        label(root, null);                                        // immediate fallback names
        var xhr = new XMLHttpRequest();
        xhr.open("GET", "/products/" + h + ".js", true);
        xhr.onload = function () {
          try {
            var json = JSON.parse(xhr.responseText);
            var byId = {};
            (json.media || []).forEach(function (m) { if (m.alt && m.alt.trim()) byId[m.id] = m.alt.trim(); });
            cache[h] = { byId: byId, title: json.title || "" };
            if (document.body.contains(root)) label(root, cache[h]);   // upgrade to real alts
          } catch (e) { cache[h] = { byId: {}, title: "" }; }
        };
        xhr.onerror = function () { cache[h] = { byId: {}, title: "" }; };
        xhr.send();
      });
    }
  });
})();
// ===== BUG 135 — END =====



// ===== BUG 136 — START =====
(function () {
  function assertive(msg) {
    var el = document.getElementById("sds-a11y-live-assertive");
    if (!el) {
      el = document.createElement("div");
      el.id = "sds-a11y-live-assertive";
      el.className = "sds-sr-only";
      el.setAttribute("role", "alert");
      el.setAttribute("aria-live", "assertive");
      el.setAttribute("aria-atomic", "true");
      document.body.appendChild(el);
    }
    el.textContent = "";
    setTimeout(function () { el.textContent = msg; }, 50);
  }

  SDS.bug(136, "Add to cart: announce 'Adding' and 'Added' instead of NVDA's 'unavailable'", {
    init: function () {
      // 1. Capture-phase click: block double submits ourselves, then announce.
      document.addEventListener("click", function (e) {
        var btn = e.target.closest && e.target.closest('product-form [name="add"], form[action*="/cart/add"] [name="add"]');
        if (!btn) return;
        if (btn.getAttribute("data-sds-adding") === "1") {
          e.preventDefault(); e.stopImmediatePropagation();      // theme's aria-disabled guard, replicated
          return;
        }
        var form = btn.closest("form");
        if (form && !form.checkValidity()) return;                // let theme show its validation error

        btn.setAttribute("data-sds-adding", "1");
        btn.setAttribute("aria-busy", "true");
        assertive("Adding to cart");

        // 2. Watch the button: remove aria-disabled before AT sees it; detect completion.
        var obs = new MutationObserver(function (records) {
          records.forEach(function (r) {
            if (r.attributeName === "aria-disabled" && btn.getAttribute("aria-disabled") === "true") {
              btn.removeAttribute("aria-disabled");
            }
            if (r.attributeName === "class" && !btn.classList.contains("is-loading") && btn.getAttribute("data-sds-adding") === "1") {
              finish();
            }
          });
        });
        obs.observe(btn, { attributes: true, attributeFilter: ["aria-disabled", "class"] });

        var timer = setTimeout(finish, 10000);                     // safety net
        function finish() {
          clearTimeout(timer); obs.disconnect();
          btn.removeAttribute("data-sds-adding");
          btn.removeAttribute("aria-busy");
          btn.removeAttribute("aria-disabled");
          var err = form && form.querySelector('[role="alert"].js-form-error, .js-form-error');
          var errText = err && err.textContent.replace(/\s+/g, " ").trim();
          if (errText) assertive(errText);
          else {
            var name = (document.querySelector(".product-title, .title, h1") || {}).textContent || "";
            name = name.replace(/\s+/g, " ").trim();
            assertive((name ? name + " added to cart." : "Item added to cart.") + " Cart drawer opened.");
          }
        }
      }, true);
    }
  });
})();
// ===== BUG 136 — END =====



// ===== BUG 137 — START =====
(function () {
  var SEL = "carousel-slider.collection-slider";          // cart drawer upsells (also matches homepage sliders)
  var FOCUSABLE = 'a, button, input, select, textarea, [tabindex]';
  var HINT = " items. Use the Next and Previous buttons, or the left and right arrow keys, to see more.";

  function slidesOf(car) {
    return (car.slides && car.slides.length) ? car.slides : [].slice.call(car.querySelectorAll(".slider__item"));
  }

  function update(car) {
    var slides = slidesOf(car), total = slides.length, first = -1, last = -1;
    slides.forEach(function (li, i) {
      var vis = car.getSlideVisibility ? car.getSlideVisibility(li) : true;
      li.setAttribute("role", "group");
      li.setAttribute("aria-roledescription", "slide");
      li.setAttribute("aria-label", (i + 1) + " of " + total);
      li.setAttribute("aria-hidden", vis ? "false" : "true");
      li.querySelectorAll(FOCUSABLE).forEach(function (el) {
        if (vis) {
          if (el.hasAttribute("data-sds-ti")) {              // restore original tabindex
            var t = el.getAttribute("data-sds-ti");
            t === "" ? el.removeAttribute("tabindex") : el.setAttribute("tabindex", t);
            el.removeAttribute("data-sds-ti");
          }
        } else {
          if (!el.hasAttribute("data-sds-ti")) el.setAttribute("data-sds-ti", el.getAttribute("tabindex") || "");
          el.setAttribute("tabindex", "-1");
        }
      });
      if (vis) { if (first < 0) first = i; last = i; }
    });
    var hint = document.getElementById(car.getAttribute("aria-describedby") || "");
    if (hint) hint.textContent = total + HINT;
    return { first: first + 1, last: last + 1, total: total };
  }

  function announce(car) {
    var s = update(car);
    SDS.announce(s.first === s.last ? "Showing item " + s.first + " of " + s.total
                                    : "Showing items " + s.first + " to " + s.last + " of " + s.total);
  }

  function focusFirstVisible(car) {
    var li = slidesOf(car).filter(function (l) { return l.getAttribute("aria-hidden") === "false"; })[0];
    var el = li && li.querySelector(FOCUSABLE + ':not([tabindex="-1"])');
    el && el.focus({ preventScroll: true });
  }

  function setup(car) {
    // Wait until the theme has initialised the carousel AND the recommended products have arrived
    if (!car.hasAttribute("loaded") || slidesOf(car).length === 0) return;
    if (car.hasAttribute("data-sds-b137")) return;
    car.setAttribute("data-sds-b137", "1");

    var wrap = car.closest(".cart-item-upsells") || car.parentElement;
    var labelEl = wrap.querySelector(".product-info-label, .section-heading, h2, h3");

    // Region semantics + instructions
    car.setAttribute("role", "region");
    car.setAttribute("aria-roledescription", "carousel");
    car.setAttribute("aria-label", labelEl ? labelEl.textContent.replace(/\s+/g, " ").trim() : "Recommended products");
    var hint = document.createElement("p");
    hint.id = "sds-b137-hint-" + Math.random().toString(36).slice(2, 7);
    hint.className = "sds-sr-only";
    car.insertAdjacentElement("afterbegin", hint);
    car.setAttribute("aria-describedby", hint.id);

    update(car);

    // Re-evaluate after any scroll (nav buttons, drag, arrow keys)
    var scroller = car.slider || car.querySelector(".slider") || car;
    var t;
    scroller.addEventListener("scroll", function () {
      clearTimeout(t);
      t = setTimeout(function () {
        var active = document.activeElement;
        var wasInside = car.contains(active);
        announce(car);
        if (wasInside && active.getAttribute("tabindex") === "-1" && active.hasAttribute("data-sds-ti")) {
          focusFirstVisible(car);                              // focus was on a slide that just scrolled away
        }
      }, 150);
    }, { passive: true });

    // If focus lands on an off-screen slide anyway (e.g. screen reader), bring it into view
    car.addEventListener("focusin", function (e) {
      var li = e.target.closest(".slider__item");
      if (li && li.getAttribute("aria-hidden") === "true" && car.scrollToElement) car.scrollToElement(li);
    });

    // Arrow keys anywhere in the upsell block (slides or Prev/Next buttons)
    wrap.addEventListener("keydown", function (e) {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (e.target.closest("input, select, textarea, [role=listbox], [role=slider]")) return;
      var btn = e.key === "ArrowRight" ? car.nextBtn : car.prevBtn;
      btn = btn || wrap.querySelector(e.key === "ArrowRight" ? '.slider-nav__btn[name="next"]' : '.slider-nav__btn[name="prev"]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      btn.click();
    });

    // Resize changes how many slides are visible
    window.addEventListener("resize", function () {
      clearTimeout(t);
      t = setTimeout(function () { update(car); }, 200);
    });
  }

  SDS.bug(137, "Cart drawer recommendations: only visible slides tabbable, arrow keys move the carousel, position announced", {
    run: function () {
      document.querySelectorAll(SEL).forEach(function (car) {
        setup(car);                                            // no-op until loaded + slides present
        if (car.hasAttribute("data-sds-b137")) update(car);    // re-tag slides on every pass (late/re-rendered)
      });
    }
  });
})();
// ===== BUG 137 — END =====


// ===== BUG 138 — START =====
(function () {
  var MSG = (window.theme && theme.strings && theme.strings.cartTermsConfirmation) ||
            "You must agree to the terms and conditions before continuing.";

  function assertive(msg) {
    var el = document.getElementById("sds-a11y-live-assertive");
    if (!el) {
      el = document.createElement("div");
      el.id = "sds-a11y-live-assertive";
      el.className = "sds-sr-only";
      el.setAttribute("role", "alert");
      el.setAttribute("aria-live", "assertive");
      el.setAttribute("aria-atomic", "true");
      document.body.appendChild(el);
    }
    el.textContent = "";
    setTimeout(function () { el.textContent = msg; }, 50);
  }

  function errorNode(terms) {
    var id = "sds-terms-error";
    var el = document.getElementById(id);
    if (!el) {
      el = document.createElement("p");
      el.id = id;
      el.className = "sds-sr-only";
      terms.appendChild(el);
    }
    return el;
  }

  SDS.bug(138, "Cart: terms-not-agreed message announced, checkbox marked invalid and focused", {
    init: function () {
      document.addEventListener("click", function (e) {
        var ctrl = e.target.closest && e.target.closest('#cartform [name="checkout"], .additional-checkout-buttons input, a[href*="/checkout"]');
        if (!ctrl) return;
        var terms = document.querySelector("terms-agreement");
        var box = terms && terms.querySelector('input[type="checkbox"]');
        if (!box || box.checked) return;

        // Theme will preventDefault and show its visual popup; we add the accessible layer.
        var err = errorNode(terms);
        err.textContent = MSG;
        box.setAttribute("aria-invalid", "true");
        box.setAttribute("aria-describedby", err.id);
        assertive(MSG);
        setTimeout(function () { box.focus(); }, 50);
      }, true);

      // Clear the error once the user agrees
      document.addEventListener("change", function (e) {
        var box = e.target.closest && e.target.closest('terms-agreement input[type="checkbox"]');
        if (!box || !box.checked) return;
        box.removeAttribute("aria-invalid");
        box.removeAttribute("aria-describedby");
        var err = document.getElementById("sds-terms-error");
        if (err) err.textContent = "";
      }, true);
    },
    run: function () {
      // The Checkout control is an <a>; expose it as a button so the role matches what it does
      document.querySelectorAll('#CartDrawer-2 a[href*="/checkout"], cart-drawer a[href*="/checkout"]').forEach(function (a) {
        if (!a.hasAttribute("role")) a.setAttribute("role", "button");
      });
    }
  });
})();
// ===== BUG 138 — END =====


// ===== BUG 139 — START =====
(function () {
  SDS.bug(139, "Footer newsletter: unique form id (was duplicate #contact_form)", {
    run: function () {
      var f = document.querySelector('footer form#contact_form, .section-footer form#contact_form');
      if (f && document.querySelectorAll("#contact_form").length > 1) {
        f.id = "footer_newsletter_form";
        f.setAttribute("action", f.getAttribute("action").replace("#contact_form", "#footer_newsletter_form"));
      }
    }
  });
})();
// ===== BUG 139 — END =====


  // ==========================================================================
  //  START
  // ==========================================================================
  SDS.boot();

})(window, document);